import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Table, Button, Form, Input, Select, Space, message, Tag, Row, Col,
    Card, Divider, Typography, DatePicker, Modal, InputNumber, Steps,
    Alert, Tooltip, Popconfirm, Switch, Spin, Collapse, Descriptions,
    Progress,
    Tabs, Pagination, Dropdown
} from 'antd';
import {
    PlusOutlined, SyncOutlined, ClearOutlined,
    ArrowRightOutlined, SaveOutlined, SearchOutlined,
    EyeOutlined, FileExcelOutlined, FilePdfOutlined,
    DeleteOutlined, EditOutlined, CopyOutlined, HistoryOutlined, ArrowLeftOutlined, CloseOutlined,
    SendOutlined, DownOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import RegistryHistoryCard from './RegistryHistoryCard';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import Layout from './Layout';
import {
    formatPercent,
    parsePercent,
    percentInputNumberProps,
    percentInputNumberWithSuffixProps
} from '../utils/formatPercent';

dayjs.extend(utc);
dayjs.extend(timezone);

const MOSCOW_TIMEZONE = 'Europe/Moscow';

const { TextArea } = Input;

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const TASK_STATUS_OPTIONS = [
    { value: 'new', label: 'Новая' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'filled', label: 'Заполнена' },
    { value: 'rework', label: 'На доработке' },
    { value: 'approval', label: 'Согласование' },
    { value: 'analysis', label: 'Требует анализа' },
    { value: 'approved', label: 'Утверждено' }
];

const REGISTRY_STATUS_OPTIONS = [
    { value: 'draft', text: 'Черновик' },
    { value: 'approval', text: 'На согласовании' },
    { value: 'approved', text: 'Утвержден' },
    { value: 'rejected', text: 'Отклонен' },
    { value: 'paid', text: 'Оплачен' }
];

const REGISTRY_TYPES = [
    { value: 'customer_order', label: 'Заказ покупателя' },

    { value: 'supplier_order', label: 'Заказ поставщика' },
   

];

const periodToPayload = period => ({
    period_start: period?.[0] ? dayjs(period[0]).format('YYYY-MM-DD') : null,
    period_end: period?.[1] ? dayjs(period[1]).format('YYYY-MM-DD') : null
});

const registryPeriodValue = registry =>
    registry?.period_start && registry?.period_end
        ? [dayjs(registry.period_start), dayjs(registry.period_end)]
        : null;

const formatRegistryPeriod = registry =>
    registry?.period_start && registry?.period_end
        ? `${dayjs(registry.period_start).format('DD.MM.YYYY')} – ${dayjs(registry.period_end).format('DD.MM.YYYY')}`
        : null;

const formatPeriodMoney = value =>
    Number(value || 0).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

/** Синхронизация открытого документа между вкладками браузера */
const REGISTRY_SYNC_CHANNEL = 'crm-docs20-registry-sync';
const REGISTRY_SYNC_STORAGE_KEY = 'crm-docs20-registry-sync';

const createRegistryTabId = () =>
    `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const sumRegistryInvoiceAmounts = registry =>
    (Array.isArray(registry?.items) ? registry.items : []).reduce(
        (sum, item) => sum + (Number(item?.invoice?.amount) || 0),
        0
    );

const registrySnapshotKey = registry => {
    if (!registry) return '';
    return [
        registry.id,
        registry.updated_at || '',
        registry.status || '',
        Number(registry.total_amount) || 0,
        Array.isArray(registry.items) ? registry.items.length : 0,
        Math.round(sumRegistryInvoiceAmounts(registry) * 100)
    ].join('|');
};

const INITIAL_API_FILTERS = {
    external_ids: '',
    nomer_tt: '',
    onlyStatus: [],
    stage: [],
    assigned_user_fio: [],
    status_invoices: [],
    region: [],
    object_id: [],
    filling_user: [],
    news_type_name: [],
    geCreated: null,
    leCreated: null,
    geExit: null,
    leExit: null,
    geUpdated: null,
    leUpdated: null,
    geClosed: null,
leClosed: null,
    minProfitability: null,
    maxProfitability: null
};

/** Фильтры поиска заявок в читаемом виде — сохраняются в историю документа. */
const buildHistoryFilters = filters => {
    if (!filters) return null;

    const result = {};
    const list = value =>
        (Array.isArray(value) ? value : [value])
            .filter(item => item !== null && item !== undefined && item !== '')
            .join(', ');
    const date = value =>
        value && dayjs.isDayjs(value) ? value.format('DD.MM.YYYY HH:mm') : '';
    const range = (from, to) => {
        const a = date(from);
        const b = date(to);
        if (!a && !b) return '';
        return `${a || '…'} — ${b || '…'}`;
    };
    const yesNo = value =>
        value === 'yes' ? 'Да' : value === 'no' ? 'Нет' : '';
    const put = (label, value) => {
        if (value) result[label] = value;
    };

    put('Внешние ID', String(filters.external_ids || '').trim());
    put('Номера ТТ', String(filters.nomer_tt || '').trim());
    put('Заказчик (Регион)', list(filters.region));
    put('Объект (БС)', list(filters.object_id));
    put('Вид работ', list(filters.news_type_name));
    put('Исполнитель', list(filters.assigned_user_fio));
    put('Заполнил ВДР', list(filters.filling_user));
    put('Этапы', list(filters.onlyStatus));
    put(
        'Стадия',
        list(
            (filters.stage || []).map(value =>
                Number(value) === 1 ? 'В работе' : Number(value) === 2 ? 'Завершено' : value
            )
        )
    );
    put(
        'Статус ВДР',
        list(
            (filters.status_invoices || []).map(value =>
                TASK_STATUS_OPTIONS.find(option => option.value === value)?.label || value
            )
        )
    );
    put('Дата создания', range(filters.geCreated, filters.leCreated));
    put('Покидание сайта', range(filters.geExit, filters.leExit));
    put('Обновление', range(filters.geUpdated, filters.leUpdated));
    put('Дата закрытия', range(filters.geClosed, filters.leClosed));
    if (
        (filters.minProfitability !== null && filters.minProfitability !== undefined) ||
        (filters.maxProfitability !== null && filters.maxProfitability !== undefined)
    ) {
        put(
            'Рентабельность, %',
            `${filters.minProfitability ?? '…'} — ${filters.maxProfitability ?? '…'}`
        );
    }
    put('Есть заказ покупателя', yesNo(filters.has_customer_order));
    put('Есть заказ поставщика', yesNo(filters.has_supplier_order));
    put('Есть ежедневный отчёт', yesNo(filters.has_daily_report));

    return Object.keys(result).length ? result : null;
};

const { Panel } = Collapse;

const mathRound = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

/**
 * Разбор поиска «через запятую»:
 * номера ТТ и id задач.
 */
const parseMultiSearchTokens = raw =>
    String(raw || '')
        .split(/[,;]+/)
        .map(token => token.trim().toLowerCase())
        .filter(Boolean);

const normalizeSearchValue = value => {
    const normalized = String(value ?? '')
        .trim()
        .toLowerCase();

    if (
        !normalized ||
        normalized === '-' ||
        normalized === 'null' ||
        normalized === 'undefined' ||
        normalized === 'none'
    ) {
        return '';
    }

    return normalized;
};

const safeJsonParseClient = (value, fallback = {}) => {
    if (value == null || value === '') {
        return fallback;
    }

    if (typeof value === 'object') {
        return value;
    }

    if (typeof value !== 'string') {
        return fallback;
    }

    try {
        return JSON.parse(value);
    } catch (_) {
        return fallback;
    }
};

/**
 * custom_fields в заявках часто лежит внутри raw_data
 * и сам может быть JSON-строкой.
 */
const getTaskCustomFieldsClient = task => {
    if (!task) {
        return {};
    }

    const direct = safeJsonParseClient(
        task.custom_fields ?? task.customFields,
        null
    );

    if (
        direct &&
        typeof direct === 'object' &&
        !Array.isArray(direct)
    ) {
        return direct;
    }

    const raw = safeJsonParseClient(task.raw_data, {});

    return safeJsonParseClient(
        raw?.custom_fields ?? raw?.customFields,
        {}
    );
};

const extractNomerTtFromTitle = task => {
    const title = String(task?.title || '');
    const match = title.match(
        /Номер\s*ТТ:\s*([^,]+)/i
    );

    if (!match) {
        return '';
    }

    const value = String(match[1] || '').trim();

    if (
        !value ||
        value.toLowerCase() === 'none' ||
        value === '-'
    ) {
        return '';
    }

    return value;
};

/**
 * Совпадение любого токена с id / external_id / № ТТ.
 * Несколько значений через запятую — OR.
 * № ТТ — только точное совпадение (без «содержит»).
 */
const matchesIdOrTtSearch = (
    rawSearch,
    {
        ids = [],
        tt = ''
    } = {}
) => {
    const tokens = parseMultiSearchTokens(rawSearch);

    if (tokens.length === 0) {
        return true;
    }

    const idValues = ids
        .map(normalizeSearchValue)
        .filter(Boolean);

    const ttValue = normalizeSearchValue(tt);

    return tokens.some(token => {
        if (idValues.some(id => id === token)) {
            return true;
        }

        if (ttValue && ttValue === token) {
            return true;
        }

        return false;
    });
};

const extractNomerTt = task => {
    if (!task) {
        return '';
    }

    if (task.nomer_tt) {
        return task.nomer_tt;
    }

    try {
        const cf = getTaskCustomFieldsClient(task);
        const field = cf?.Nomer_TT;
        const fromCf =
            field && typeof field === 'object'
                ? field.value
                : field;

        if (
            fromCf !== undefined &&
            fromCf !== null &&
            String(fromCf).trim() !== '' &&
            String(fromCf).toLowerCase() !== 'none' &&
            String(fromCf) !== 'false'
        ) {
            return String(fromCf).trim();
        }

        return extractNomerTtFromTitle(task);
    } catch (_) {
        return extractNomerTtFromTitle(task);
    }
};

const safeParseIntArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data.map(item => parseInt(item, 10)).filter(n => !isNaN(n));
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            if (Array.isArray(parsed)) return parsed.map(item => parseInt(item, 10)).filter(n => !isNaN(n));
        } catch (e) {
            return data.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
        }
    }
    return [];
};
const _getCounterpartyOptionByName = (name, all) => {
    console.log(`[LOG] Поиск КА: "${name}" в массиве из ${all?.length} элементов`);
    if (!name || !all) return null;
    const found = all.find(c => c.name?.trim().toLowerCase() === name.trim().toLowerCase());
    console.log(`[LOG] Результат поиска КА:`, found ? found.name : "НЕ НАЙДЕН");
    return found ? { value: found.id, label: found.name } : null;
};

const _getArticleOptionByName = (cpOpt, name, counterparties, articles) => {
    const cp = counterparties.find(c => c.id === cpOpt?.value);
    if (!cp || !name) return null;

    // Парсим разрешенные ID статей для этого контрагента
    const allowedIds = safeParseIntArray(cp.allowed_articles);

    // Ищем статью по имени СРЕДИ разрешенных для этого КА
    const found = articles.find(a =>
        a.name?.trim().toLowerCase() === name.trim().toLowerCase() &&
        allowedIds.includes(Number(a.id))
    );
    return found ? { value: found.id, label: found.name } : null;
};

const _getNomenclatureOptions = (
    selectedCounterpartyOption,
    selectedArticleOption,
    allCounterparties,
    allNomenclatures,
    allPriceTypesList
) => {
    if (
        !selectedCounterpartyOption?.value ||
        !selectedArticleOption?.value
    ) {
        return [];
    }
    const cp = allCounterparties.find(
        c => c.id === selectedCounterpartyOption.value
    );
    if (!cp) return [];
    const priceLists = safeParseIntArray(cp.price_lists);
    const result = [];
    allNomenclatures
        .filter(
            n =>
                Number(n.article_id) ===
                Number(selectedArticleOption.value)
        )
        .forEach(n => {
            if (n.prices && typeof n.prices === 'object') {
                Object.entries(n.prices).forEach(
                    ([ptidStr, val]) => {
                        const ptid = parseInt(ptidStr, 10);
                        if (
                            priceLists.includes(ptid) &&
                            typeof val === 'number'
                        ) {
                            const ptName =
                                allPriceTypesList.find(
                                    pt => pt.id === ptid
                                )?.name || `Тип ${ptid}`;
                            result.push({
                                value: `${n.id}-${ptid}`,
                                label: `${n.name} (${ptName}: ${val.toLocaleString('ru-RU')})`,
                                name: n.name,
                                unit: n.unit,
                                priceValue: val,
                                priceTypeName: ptName
                            });
                        }
                    }
                );
            }
        });
    return result;
};

const _getNomenclatureOptionByDetails = (
    cpOpt,
    artOpt,
    name,
    unit,
    price,
    counterparties,
    nomenclatures,
    priceTypes,
    exactPriceMatch = true
) => {
    if (!cpOpt || !artOpt || !name) return null;
    const options = _getNomenclatureOptions(
        cpOpt,
        artOpt,
        counterparties,
        nomenclatures,
        priceTypes || []
    );
    const priceVal = Number(price) || 0;
    const exact = options.find(
        opt =>
            opt.name.toLowerCase() === name.toLowerCase() &&
            (opt.unit || '').toLowerCase() ===
                (unit || '').toLowerCase() &&
            Number(opt.priceValue) === priceVal
    );
    if (exact) return exact;
    if (!exactPriceMatch) {
        return (
            options.find(
                opt =>
                    opt.name.toLowerCase() ===
                    name.toLowerCase()
            ) || null
        );
    }
    return null;
};

const FixedArticleSelectCell = React.memo(
    ({
        formInstance,
        memoizedGetArticleOptions,
        currentCounterparty
    }) => {
        const options =
            memoizedGetArticleOptions(
                currentCounterparty
            );

        return (
            <Form.Item
                name="article"
                rules={[
                    {
                        required: true,
                        message:
                            'Выберите статью'
                    }
                ]}
                style={{
                    marginBottom: 0
                }}
            >
                <Select
                    options={options}
                    labelInValue
                    showSearch
                    placeholder={
                        options.length
                            ? 'Выберите статью'
                            : 'Нет доступных статей'
                    }
                    onChange={article => {
                        formInstance.setFieldsValue({
                            article,
                            selectedRateOption:
                                null,
                            rate_name: '',
                            unit: '',
                            price: 0
                        });
                    }}
                />
            </Form.Item>
        );
    }
);

const FixedNomenclatureSelectCell = React.memo(
    ({
        formInstance,
        memoizedGetNomenclatureOptions,
        currentCounterparty,
        currentArticle
    }) => {
        const rawOptions = useMemo(
            () =>
                memoizedGetNomenclatureOptions(
                    currentCounterparty,
                    currentArticle
                ),
            [
                memoizedGetNomenclatureOptions,
                currentCounterparty,
                currentArticle
            ]
        );

        const options = useMemo(
            () =>
                rawOptions.map(opt => ({
                    ...opt,
                    plainName: opt.name,
                    searchValue:
                        `${opt.name} ${opt.priceTypeName || ''} ${opt.priceValue}`.toLowerCase(),
                    label: (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '4px 0'
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 'bold',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.2',
                                    color: '#262626'
                                }}
                            >
                                {opt.name}
                            </div>
                            <div
                                style={{
                                    fontSize: 11,
                                    color: '#8c8c8c',
                                    marginTop: 2
                                }}
                            >
                                Прайс:{' '}
                                {opt.priceTypeName || '—'}{' '}
                                | Цена:{' '}
                                <span
                                    style={{
                                        color: '#1890ff'
                                    }}
                                >
                                    {Number(
                                        opt.priceValue
                                    ).toLocaleString(
                                        'ru-RU'
                                    )}{' '}
                                    р.
                                </span>
                            </div>
                        </div>
                    )
                })),
            [rawOptions]
        );

        const disabled =
            !currentCounterparty ||
            !currentArticle ||
            options.length === 0;

        const handleChange = selectedValue => {
            const selectedOption = options.find(
                opt =>
                    String(opt.value) ===
                    String(selectedValue?.value)
            );
            const price =
                Number(selectedOption?.priceValue) || 0;

            const quantity =
                Number(
                    formInstance.getFieldValue('quantity')
                ) || 1;

            const currentPercent =
                formInstance.getFieldValue('percent');

            const percent =
                currentPercent === undefined ||
                currentPercent === null
                    ? 100
                    : Number(currentPercent);

            const currentPenalty =
                formInstance.getFieldValue('penalty');

            const penalty =
                currentPenalty === undefined ||
                currentPenalty === null
                    ? 0
                    : Number(currentPenalty);

            formInstance.setFieldsValue({
                selectedRateOption: {
                    ...selectedValue,
                    name: selectedOption?.name || '',
                    unit: selectedOption?.unit || '',
                    priceValue: price,
                    priceTypeName:
                        selectedOption?.priceTypeName ||
                        null
                },
                rate_name: selectedOption?.name || '',
                unit: selectedOption?.unit || '',
                price,
                quantity,
                percent,
                penalty
            });
        };

        return (
            <>
                <Form.Item
                    name="rate_name"
                    hidden
                    preserve
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="unit"
                    hidden
                    preserve
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="selectedRateOption"
                    rules={[
                        {
                            required: true,
                            message:
                                'Выберите наименование'
                        }
                    ]}
                    style={{
                        marginBottom: 0
                    }}
                >
                    <Select
                        options={options}
                        labelInValue
                        showSearch
                        disabled={disabled}
                        placeholder={
                            !currentArticle
                                ? 'Сначала выберите статью'
                                : options.length === 0
                                    ? 'Нет доступных расценок'
                                    : 'Выберите наименование'
                        }
                        optionLabelProp="plainName"
                        popupMatchSelectWidth={false}
                        dropdownStyle={{ width: 500 }}
                        filterOption={(input, option) =>
                            String(
                                option?.searchValue || ''
                            ).includes(input.toLowerCase())
                        }
                        onChange={handleChange}
                    />
                </Form.Item>
            </>
        );
    }
);
const isInvoiceLocked = record => {
    if (!record) {
        return false;
    }
    if (Array.isArray(record.blocking_registries)) {
        return record.blocking_registries.length > 0;
    }
    const linked = Array.isArray(record.linked_registries)
        ? record.linked_registries.filter(
              registry =>
                  registry?.type !== 'daily_report'
          )
        : [];
    if (linked.length) {
        return true;
    }
    return Boolean(
        record.is_locked ||
            record.linked_registry_numbers
    );
};

const invoiceLockLabel = record => {
    if (!record) return '';
    if (record.linked_registry_numbers) {
        return String(record.linked_registry_numbers);
    }
    const list =
        record.blocking_registries ||
        (Array.isArray(record.linked_registries)
            ? record.linked_registries.filter(
                  r => r?.type !== 'daily_report'
              )
            : []);
    return list
        .map(r => r.number || `ID ${r.id}`)
        .filter(Boolean)
        .join(', ');
};

const DocumentCreatePage = () => {
    const [modalForm] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);

    const [registries, setRegistries] = useState([]);
    const [registriesTotal, setRegistriesTotal] = useState(0);

    const [registriesPagination, setRegistriesPagination] = useState({
        current: 1,
        pageSize: 50
    });

    const [counterparties, setCounterparties] = useState([]);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [tasksSource, setTasksSource] = useState([]);
    const [invoicesSource, setInvoicesSource] = useState([]);
    const [
        wizardInvoiceSearch,
        setWizardInvoiceSearch
    ] = useState('');

    const [
        wizardInvoiceAvailability,
        setWizardInvoiceAvailability
    ] = useState('all');

    const [
        wizardInvoicePage,
        setWizardInvoicePage
    ] = useState(1);

    const [
        wizardInvoicePageSize,
        setWizardInvoicePageSize
    ] = useState(10);

    const [
        removingVdrGroupKey,
        setRemovingVdrGroupKey
    ] = useState(null);

    const [activeTab] = useState('daily_report');
    const [loading, setLoading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [commentHistory, setCommentHistory] = useState([]);
    const [registryHistory, setRegistryHistory] = useState([]);
    const [newCommentText, setNewCommentText] = useState('');
    const [hideFullyReported, setHideFullyReported] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const commentsRef = useRef(null);
    const historyRef = useRef(null);

    // Фильтры главной страницы
    const [mainSearch, setMainSearch] = useState({ text: '', ka: '', dateRange: null, status: null });

    // Состояния просмотра и редактирования
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedRegistry, setSelectedRegistry] = useState(null);

    const [
    registryViewLoading,
    setRegistryViewLoading
] = useState(false);

    const [
    exportFormatModalOpen,
    setExportFormatModalOpen
] = useState(false);

const [
    selectedExportFormat,
    setSelectedExportFormat
] = useState(
    'daily_report'
);

const [
    exportLoading,
    setExportLoading
] = useState(false);

    const [registryViewSearch, setRegistryViewSearch] = useState('');

    const [registryViewClosureFilter, setRegistryViewClosureFilter] =
        useState('all');

    const [
        registryViewPage,
        setRegistryViewPage
    ] = useState(1);

    const [
        registryViewPageSize,
        setRegistryViewPageSize
    ] = useState(10);

    const [isEditMode, setIsEditMode] = useState(false);

    // ДОБАВЬТЕ ЭТИ ТРИ СТРОКИ:
    const [nomenclatures, setNomenclatures] = useState([]);
    const [financialArticles, setFinancialArticles] = useState([]);
    const [priceTypesList, setPriceTypesList] = useState([]);

    const [supplierBasisModal, setSupplierBasisModal] = useState(false);
    const [basisSuppliers, setBasisSuppliers] = useState([]);
    const [basisLoading, setBasisLoading] = useState(false);

    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [taskData, setTaskData] = useState(null);
    const [taskInvoices, setTaskInvoices] = useState({ income: [], expense: [] });
    const [taskMetadataLoading, setTaskMetadataLoading] = useState(false);
    const [activeTaskDbId, setActiveTaskDbId] = useState(null);
    const [activeTaskExtId, setActiveTaskExtId] = useState(null);

    const [taskComments, setTaskComments] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [expandedSections, setExpandedSections] = useState(['basic', 'income', 'expense']);
    const [editInvoiceForm] = Form.useForm(); // Для совместимости с логикой TasksPage
    const [editingInvoice, setEditingInvoice] = useState(null);

    const [selectedTaskKeys, setSelectedTaskKeys] = useState([]);
    const [selectedInvoiceKeys, setSelectedInvoiceKeys] = useState([]);
    const [enrichedTasks, setEnrichedTasks] = useState({});
    const [
        taskDistributionStats,
        setTaskDistributionStats
    ] = useState({});
    const [tasksPagination, setTasksPagination] = useState({ current: 1, pageSize: 10 });
    const [invoicesPagination, setInvoicesPagination] = useState({ current: 1, pageSize: 20 });

    const [isHeaderEdit, setIsHeaderEdit] = useState(false);
    const [periodCheck, setPeriodCheck] = useState(null);
    const [periodCheckLoading, setPeriodCheckLoading] = useState(false);
    const [periodMissingKeys, setPeriodMissingKeys] = useState([]);
    const [periodAdding, setPeriodAdding] = useState(false);
    const [headerForm] = Form.useForm();

    const [allActs, setAllActs] = useState([]);
    const [allActsLoading, setAllActsLoading] = useState(false);
    const [selectedOrderKeys, setSelectedOrderKeys] = useState([]);
    const [selectedActKeys, setSelectedActKeys] = useState([]);
    const [paymentDocsExportLoading, setPaymentDocsExportLoading] =
        useState(false);
    const [supplierActsExportLoading, setSupplierActsExportLoading] =
        useState(false);
    const [supplierOrdersExportLoading, setSupplierOrdersExportLoading] =
        useState(false);

    const [taskModalRegistryContext, setTaskModalRegistryContext] =
    useState(null);

    const pendingOrderInvoiceTempIdsRef =
    useRef(new Set());

    /** Справочники уже на странице — не тянуть снова при каждом открытии ВДР */
    const dictionariesLoadedRef = useRef(false);
    /** Защита от гонки: фоновый /ext|/history|/comments не затрёт другую открытую задачу */
    const taskModalLoadGenRef = useRef(0);

    // Функция прокрутки (нужна для комментариев и истории)
    const scrollToBottom = (ref) => {
        if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
    };

    // Состояние для хранения подгруженных актов (ключ - ID заказа, значение - массив актов)
    const [expandedActs, setExpandedActs] = useState({});
    const [expandingLoading, setExpandingLoading] = useState({});

    const [selectedBasisKeys, setSelectedBasisKeys] = useState([]);

    const [unlinkedActs, setUnlinkedActs] = useState([]);
    const [unlinkedLoading, setUnlinkedLoading] = useState(false);
    const [isCreateOnBasis, setIsCreateOnBasis] = useState(false);
    const [options, setOptions] = useState({
        users: [], regions: [], objects: [], types: [], fillers: [], stages: []
    });

    const [registryViewHistory, setRegistryViewHistory] = useState([]);

    const [isAddingItemsToExistingRegistry, setIsAddingItemsToExistingRegistry] = useState(false);

    const [apiFilters, setApiFilters] = useState(INITIAL_API_FILTERS);
    const lastSearchFiltersRef = useRef(null);

    const counterpartyOptions = useMemo(() => counterparties.map(c => ({ value: c.id, label: c.name })), [counterparties]);

    const memoizedGetArticleOptions = useCallback((cpOpt) => {
        const cp = counterparties.find(c => c.id === cpOpt?.value);
        if (!cp) return [];
        const allowedIds = Array.isArray(cp.allowed_articles) ? cp.allowed_articles : JSON.parse(cp.allowed_articles || '[]');
        return financialArticles.filter(a => allowedIds.includes(a.id)).map(a => ({ value: a.id, label: a.name }));
    }, [counterparties, financialArticles]);

    const memoizedGetNomenclatureOptions = useCallback(
        (cpOpt, artOpt) =>
            _getNomenclatureOptions(
                cpOpt,
                artOpt,
                counterparties,
                nomenclatures,
                priceTypesList
            ),
        [counterparties, nomenclatures, priceTypesList]
    );

    const [vdrEditModalVisible, setVdrEditModalVisible] = useState(false);
    const [editingVdrItem, setEditingVdrRecord] = useState(null);
    const [vdrEditForm] = Form.useForm();

    const [
        attachInvoiceProposal,
        setAttachInvoiceProposal
    ] = useState(null);

    const [
        attachInvoiceLoading,
        setAttachInvoiceLoading
    ] = useState(false);

    const docType = Form.useWatch('type', modalForm);
    const selectedCpId = Form.useWatch('counterparty_id', modalForm);
    const selectedParentId = Form.useWatch('parent_id', modalForm);
    const [registryTableFilters, setRegistryTableFilters] = useState({
        id: '',
        number: '',
        title: '',
        counterparty_name: '',
        status: '',
        date: '',
        user_fio: '',
        type: [
            'customer_order',
            'supplier_order'
        ]
    });
    const selectedCpName = useMemo(() => {
        return counterparties.find(c => c.id === selectedCpId)?.name;
    }, [selectedCpId, counterparties]);

    useEffect(() => {
        fetchRegistries();
        fetchDictionaries();
        fetchAllActs();
    }, []);

    /*
 * Основной запрос /api/registries/:id уже возвращает
 * полные данные задач внутри:
 *
 * selectedRegistry.items[].invoice.task
 *
 * Поэтому никаких дополнительных запросов
 * /api/tasks/ext/:externalId здесь не выполняем.
 */
useEffect(() => {
    const items =
        Array.isArray(
            selectedRegistry?.items
        )
            ? selectedRegistry.items
            : [];

    if (items.length === 0) {
        setEnrichedTasks({});
        return;
    }

    const taskCache = {};

    items.forEach(item => {
        const task =
            item?.invoice?.task;

        if (!task) {
            return;
        }

        /*
         * Сохраняем задачу сразу под двумя ключами,
         * поскольку существующий фронт ищет её
         * и по внутреннему id, и по external_id.
         */
        if (task.id) {
            taskCache[
                String(task.id)
            ] = task;
        }

        if (task.external_id) {
            taskCache[
                String(
                    task.external_id
                )
            ] = task;
        }
    });

    setEnrichedTasks(
        taskCache
    );
}, [
    selectedRegistry?.id,
    selectedRegistry?.items
]);
    
    const fetchDictionaries = async () => {
        const token = localStorage.getItem('token');
        const h = { 'Authorization': `Bearer ${token}` };
        try {
            // Оставляем только те роуты, которые реально существуют
            const [u, r, o, t, f, s, cp, nom] = await Promise.all([
                axios.get('/api/tasks/assigned-users', { headers: h }),
                axios.get('/api/tasks/regions', { headers: h }),
                axios.get('/api/tasks/service-objects', { headers: h }),
                axios.get('/api/tasks/types', { headers: h }),
                axios.get('/api/tasks/filling-users', { headers: h }),
                axios.get('/api/tasks/statuses', { headers: h }),
                axios.get('/api/counterparties?limit=1000', { headers: h }),
                axios.get('/api/nomenclature?limit=10000', { headers: h })
            ]);

            setOptions({
                users: u.data.map(v => ({ value: v, label: v })),
                regions: r.data.map(v => ({ value: v, label: v })),
                objects: o.data.map(v => ({ value: v, label: v })),
                types: t.data.map(v => ({ value: v, label: v })),
                fillers: f.data.map(v => ({ value: v.login, label: v.fio || v.login })),
                stages: s.data.map(v => ({ value: v, label: v }))
            });

            // ПРАВИЛЬНОЕ РАСПРЕДЕЛЕНИЕ ДАННЫХ (как в TasksPage)
            setCounterparties(cp.data.data || []);

            // Статьи и Типы цен берем из объекта options, который прислал роут контрагентов
            if (cp.data.options?.articles) {
                setFinancialArticles(cp.data.options.articles);
            }
            if (cp.data.options?.priceTypes) {
                setPriceTypesList(cp.data.options.priceTypes);
            }

            setNomenclatures(nom.data.data || []);
            // Если в номенклатуре тоже есть типы цен, а в КА не было - берем оттуда
            if (nom.data.priceTypes && !priceTypesList.length) {
                setPriceTypesList(nom.data.priceTypes);
            }

            dictionariesLoadedRef.current = true;
            console.log("Справочники загружены успешно:", {
                cp: cp.data.data?.length,
                articles: cp.data.options?.articles?.length,
                nom: nom.data.data?.length
            });
        } catch (e) {
            console.error('Ошибка при загрузке справочников:', e);
            message.error('Не удалось загрузить справочники');
        }
    };

    const fetchRegistries = async (params = {}) => {
        setLoading(true);

        try {
            const token =
                localStorage.getItem('token');

            const page =
                Number(
                    params.page ??
                    registriesPagination.current ??
                    1
                );

            const limit =
                Number(
                    params.limit ??
                    params.pageSize ??
                    registriesPagination.pageSize ??
                    50
                );

            const requestFilters = {
                ...registryTableFilters,
                ...params
            };

            const requestParams = {
                type:
                    requestFilters.type?.length
                        ? requestFilters.type
                        : [
                            'customer_order',
                            'supplier_order'
                        ],

                id:
                    requestFilters.id ||
                    undefined,

                number:
                    requestFilters.number ||
                    undefined,

                title:
                    requestFilters.title ||
                    undefined,

                counterparty_name:
                    requestFilters.counterparty_name ||
                    undefined,

                status:
                    requestFilters.status ||
                    undefined,

                date:
                    requestFilters.date ||
                    undefined,

                user_fio:
                    requestFilters.user_fio ||
                    undefined,
                
                search:
                    requestFilters.search ||
                    undefined,

                date_from:
                    requestFilters.date_from ||
                    undefined,

                date_to:
                    requestFilters.date_to ||
                    undefined,

                closed_status:
                    requestFilters.closed_status ||
                    undefined,

                page,
                limit
            };

            const res = await axios.get(
                '/api/registries',
                {
                    params: requestParams,

                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            const responseData =
                Array.isArray(res.data)
                    ? res.data
                    : res.data?.data || [];

            const responseTotal =
                Array.isArray(res.data)
                    ? res.data.length
                    : Number(
                        res.data?.total || 0
                    );

            setRegistries(responseData);
            setRegistriesTotal(responseTotal);

            setRegistriesPagination({
                current:
                    Number(
                        res.data?.page ||
                        page
                    ),

                pageSize:
                    Number(
                        res.data?.limit ||
                        limit
                    )
            });
        } catch (error) {
            console.error(
                'Ошибка загрузки реестров:',
                error
            );

            message.error(
                error.response?.data?.error ||
                'Ошибка загрузки реестров'
            );

            setRegistries([]);
            setRegistriesTotal(0);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = (id) => {
        const token = localStorage.getItem('token');
        window.open(`/api/registries/${id}/daily-export?token=${token}`, '_blank');
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/registries/${selectedRegistry.id}/comments`,
                { text: newCommentText },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setNewCommentText('');
            const res = await axios.get(`/api/registries/${selectedRegistry.id}/comments`, { headers: { 'Authorization': `Bearer ${token}` } });
            setCommentHistory(res.data);
        } catch (e) { message.error("Ошибка отправки"); }
    };



    const groupedInvoicesSource = useMemo(() => {
        if (!invoicesSource || invoicesSource.length === 0) return [];

        // Определяем, какой тип строк нам нужен исходя из выбранного типа документа
        const currentDocType = modalForm.getFieldValue('type') || activeTab;
        const targetInvoiceType = currentDocType?.includes('customer') ? 'income' : 'expense';

        const filtered = invoicesSource.filter(inv => {
            // 1. Фильтр по типу (Доход/Расход)
            const matchType = inv.type === targetInvoiceType;

            // 2. Фильтр по выбранному контрагенту (чтобы не намешать в один акт разных КА)
            // Если КА выбран в селекте, показываем только его строки
            const matchCP = !selectedCpName || inv.counterparty?.trim().toLowerCase() === selectedCpName.trim().toLowerCase();

            return matchType && matchCP;
        });

        const groups = {};
        filtered.forEach(inv => {
            const extId = inv.task?.external_id || 'ID?';
            if (!groups[extId]) {
                groups[extId] = { task: inv.task, items: [], total: 0 };
            }
            groups[extId].items.push(inv);
            groups[extId].total += parseFloat(inv.amount || 0);
        });
        return Object.values(groups);
    }, [invoicesSource, selectedCpName, docType]);

    const filteredGroupedInvoicesSource =
    useMemo(() => {
        const groups =
            Array.isArray(
                groupedInvoicesSource
            )
                ? groupedInvoicesSource
                : [];

        const search =
            String(
                wizardInvoiceSearch || ''
            )
                .trim()
                .toLowerCase();

        const selectedIds =
            new Set(
                (
                    selectedInvoiceKeys ||
                    []
                ).map(Number)
            );

        return groups
            .map(group => {
                const task =
                    group.task || {};

                const filteredItems =
                    (
                        Array.isArray(
                            group.items
                        )
                            ? group.items
                            : []
                    ).filter(invoice => {
                        const invoiceId =
                            Number(
                                invoice.id
                            );

                        /*
                         * Строка считается занятой,
                         * если бэкенд вернул is_used
                         * либо она уже находится
                         * в другом заказе/акте.
                         */
                        const isUsed =
                            Boolean(
                                invoice.is_used
                            ) ||
                            Boolean(
                                invoice.order_id
                            );

                        const isSelected =
                            selectedIds.has(
                                invoiceId
                            );

                        if (
                            wizardInvoiceAvailability ===
                                'available' &&
                            isUsed
                        ) {
                            return false;
                        }

                        if (
                            wizardInvoiceAvailability ===
                                'used' &&
                            !isUsed
                        ) {
                            return false;
                        }

                        if (
                            wizardInvoiceAvailability ===
                                'selected' &&
                            !isSelected
                        ) {
                            return false;
                        }

                        if (!search) {
                            return true;
                        }

                        /*
                         * Поиск по нескольким id / № ТТ
                         * через запятую.
                         */
                        return matchesIdOrTtSearch(
                            search,
                            {
                                ids: [
                                    invoice.id,
                                    invoice.task_id,
                                    task.id,
                                    task.external_id
                                ],
                                tt: extractNomerTt(task)
                            }
                        );
                    });

                return {
                    ...group,

                    items:
                        filteredItems,

                    total:
                        filteredItems.reduce(
                            (
                                sum,
                                invoice
                            ) =>
                                sum +
                                (
                                    Number(
                                        invoice
                                            .amount
                                    ) || 0
                                ),
                            0
                        )
                };
            })
            .filter(group => {
                if (
                    group.items.length >
                    0
                ) {
                    return true;
                }

                /*
                 * Также разрешаем находить
                 * группу по самой задаче.
                 */
                if (!search) {
                    return false;
                }

                const task =
                    group.task || {};

                return matchesIdOrTtSearch(
                    search,
                    {
                        ids: [
                            task.id,
                            task.external_id
                        ],
                        tt: extractNomerTt(task)
                    }
                );
            });
    }, [
        groupedInvoicesSource,
        wizardInvoiceSearch,
        wizardInvoiceAvailability,
        selectedInvoiceKeys
    ]);

    const wizardFilteredStats =
    useMemo(() => {
        const items =
            filteredGroupedInvoicesSource
                .flatMap(
                    group =>
                        group.items || []
                );

        const selectedIds =
            new Set(
                (
                    selectedInvoiceKeys ||
                    []
                ).map(Number)
            );

        const availableItems =
            items.filter(
                item =>
                    !item.is_used &&
                    !item.order_id
            );

        const selectedVisibleItems =
            items.filter(item =>
                selectedIds.has(
                    Number(item.id)
                )
            );

        return {
            rows:
                items.length,

            groups:
                filteredGroupedInvoicesSource
                    .length,

            amount:
                items.reduce(
                    (sum, item) =>
                        sum +
                        (
                            Number(
                                item.amount
                            ) || 0
                        ),
                    0
                ),

            availableRows:
                availableItems.length,

            selectedRows:
                selectedVisibleItems.length,

            selectedAmount:
                selectedVisibleItems.reduce(
                    (sum, item) =>
                        sum +
                        (
                            Number(
                                item.amount
                            ) || 0
                        ),
                    0
                )
        };
    }, [
        filteredGroupedInvoicesSource,
        selectedInvoiceKeys
    ]);

    const paginatedGroupedInvoicesSource =
        useMemo(() => {
            const groups = Array.isArray(
                filteredGroupedInvoicesSource
            )
                ? filteredGroupedInvoicesSource
                : [];

            const start =
                (wizardInvoicePage - 1) *
                wizardInvoicePageSize;

            return groups.slice(
                start,
                start + wizardInvoicePageSize
            );
    }, [
        filteredGroupedInvoicesSource,
        wizardInvoicePage,
        wizardInvoicePageSize
    ]);

    useEffect(() => {
        const totalPages = Math.max(
            1,
            Math.ceil(
                (
                    filteredGroupedInvoicesSource
                        ?.length || 0
                ) /
                    wizardInvoicePageSize
            )
        );

        if (wizardInvoicePage > totalPages) {
            setWizardInvoicePage(totalPages);
        }
    }, [
        filteredGroupedInvoicesSource,
        wizardInvoicePage,
        wizardInvoicePageSize
    ]);

    const filteredRegistries = useMemo(() => {
        const source = Array.isArray(registries)
            ? registries
            : [];

        return source.filter(r => {
            const searchVal = mainSearch.text
                ?.trim()
                .toLowerCase();

            const matchText =
                !searchVal ||
                String(r.number || '')
                    .toLowerCase()
                    .includes(searchVal) ||
                String(r.title || '')
                    .toLowerCase()
                    .includes(searchVal) ||
                String(r.id || '')
                    .includes(searchVal);

            const matchKA =
                !mainSearch.ka ||
                String(r.counterparty_name || '')
                    .toLowerCase()
                    .includes(
                        mainSearch.ka.toLowerCase()
                    );

            const matchStatus =
                !mainSearch.status ||
                r.status === mainSearch.status;

            let matchDate = true;

            if (
                mainSearch.dateRange?.[0] &&
                mainSearch.dateRange?.[1]
            ) {
                const registryDate = dayjs(r.date);

                const startDate =
                    mainSearch.dateRange[0].startOf('day');

                const endDate =
                    mainSearch.dateRange[1].endOf('day');

                matchDate =
                    !registryDate.isBefore(startDate) &&
                    !registryDate.isAfter(endDate);
            }

            let matchClosed = true;

            if (mainSearch.closedStatus) {
                const total = Number(r.total_rows) || 0;
                const closed = Number(r.closed_rows) || 0;

                if (mainSearch.closedStatus === 'none') {
                    matchClosed = closed === 0;
                }

                if (mainSearch.closedStatus === 'full') {
                    matchClosed =
                        total > 0 &&
                        closed >= total;
                }

                if (mainSearch.closedStatus === 'partial') {
                    matchClosed =
                        closed > 0 &&
                        closed < total;
                }
            }

            return (
                matchText &&
                matchKA &&
                matchStatus &&
                matchDate &&
                matchClosed
            );
        });
    }, [registries, mainSearch]);

    const removeItemFromRegistry = async (itemId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/registries/items/${itemId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            message.success('Строка удалена');
            await refreshRegistryView(selectedRegistry.id, {
                refreshList: true,
                refreshActs: true,
                refreshHistory: true
            });
        } catch (e) { message.error('Ошибка при удалении'); }
    };

    const addItemsToRegistry = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/registries/${selectedRegistry.id}/add-items`, { invoiceIds: selectedInvoiceKeys, history_filters: lastSearchFiltersRef.current }, { headers: { 'Authorization': `Bearer ${token}` } });
           message.success('Строки добавлены');

            setIsModalVisible(false);
            setSelectedInvoiceKeys([]);

            await refreshRegistryView(
                selectedRegistry.id,
                {
                    refreshList: true,
                    refreshActs: true,
                    refreshHistory: true
                }
            );
        } catch (e) { message.error('Ошибка при добавлении'); }
    };

    const selectedRegistryPeriodKey = selectedRegistry
        ? [
            selectedRegistry.id,
            selectedRegistry.period_start || '',
            selectedRegistry.period_end || '',
            Array.isArray(selectedRegistry.items) ? selectedRegistry.items.length : 0
        ].join('|')
        : '';

    const fetchPeriodCheck = useCallback(async registryId => {
        if (!registryId) return;

        setPeriodCheckLoading(true);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/registries/${registryId}/period-check`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPeriodCheck(res.data);
        } catch (e) {
            console.error('Ошибка проверки периода документа:', e);
            setPeriodCheck(null);
        } finally {
            setPeriodCheckLoading(false);
        }
    }, []);

    useEffect(() => {
        setPeriodMissingKeys([]);

        if (
            !selectedRegistry?.id ||
            !selectedRegistry.period_start ||
            !selectedRegistry.period_end
        ) {
            setPeriodCheck(null);
            return;
        }

        fetchPeriodCheck(selectedRegistry.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRegistryPeriodKey, fetchPeriodCheck]);

    const periodOutsideTaskIds = useMemo(
        () => new Set((periodCheck?.outside || []).map(row => Number(row.task_id))),
        [periodCheck]
    );

    const addPeriodMissingToRegistry = async invoiceIds => {
        if (!selectedRegistry?.id || !invoiceIds.length) return;

        setPeriodAdding(true);

        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `/api/registries/${selectedRegistry.id}/add-items`,
                {
                    invoiceIds,
                    history_filters: {
                        'Основание': `Не включённые строки ВДР за период ${formatRegistryPeriod(selectedRegistry)}`
                    }
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            message.success(`Добавлено строк: ${invoiceIds.length}`);
            setPeriodMissingKeys([]);

            await refreshRegistryView(selectedRegistry.id, {
                refreshList: true,
                refreshActs: true,
                refreshHistory: true
            });
        } catch (e) {
            message.error(e.response?.data?.error || 'Ошибка при добавлении строк');
        } finally {
            setPeriodAdding(false);
        }
    };

    useEffect(() => {
        const currentType = docType || activeTab;
        if (currentType?.includes('act') || currentType === 'daily_report') {
            const token = localStorage.getItem('token');
            axios.get('/api/registries/available-orders', { params: { type: currentType }, headers: { 'Authorization': `Bearer ${token}` } }).then(res => setAvailableOrders(res.data));
        }
    }, [docType, activeTab, currentStep]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const autoOpenId = Number(urlParams.get('id'));

        if (autoOpenId > 0) {
            viewRegistryDetails({ id: autoOpenId }, false);
            // Очищаем URL, чтобы при обновлении не открывалось снова
            navigate(location.pathname, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    const fetchAllActs = async () => {
        setAllActsLoading(true);

        try {
            const token = localStorage.getItem('token');

            const res = await axios.get('/api/registries', {
                params: {
                    type: [
                        'customer_act',
                        'supplier_act'
                    ],

                    page: 1,

                    /*
                    * Временно можно запросить до 200 актов,
                    * поскольку на бэкенде установлен максимум 200.
                    */
                    limit: 200
                },

                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const acts = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [];

            setAllActs(acts);
        } catch (e) {
            console.error('Ошибка загрузки актов:', e);
            setAllActs([]);
        } finally {
            setAllActsLoading(false);
        }
    };
    const handleFilterChange = (key, value) => setApiFilters(p => ({ ...p, [key]: value }));

    const handleSearchTasks = async () => {
        setLoading(true);
        lastSearchFiltersRef.current = buildHistoryFilters(apiFilters);

        const token =
            localStorage.getItem('token');

        try {
            const queryParams = {};

            for (const key in apiFilters) {
                const value = apiFilters[key];

                if (
                    value === null ||
                    value === undefined ||
                    value === '' ||
                    (
                        Array.isArray(value) &&
                        value.length === 0
                    )
                ) {
                    continue;
                }

                if (dayjs.isDayjs(value)) {
                    queryParams[key] =
                        value
                            .add(3, 'hour')
                            .format(
                                'YYYY-MM-DD HH:mm:ss'
                            );
                } else {
                    queryParams[key] =
                        Array.isArray(value)
                            ? value.join(',')
                            : (
                                typeof value === 'string'
                                    ? value.trim()
                                    : value
                            );
                }
            }

            /*
             * POST: длинные списки № ТТ / внешних ID
             * (до 4000 символов) не влезают в URL GET.
             */
            const response =
                await axios.post(
                    '/api/tasks',
                    {
                        ...queryParams,
                        limit: 4000,
                        skip_count: 1
                    },
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        },
                        timeout: 120000
                    }
                );

            setTasksSource(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.data || []
            );
        } catch (error) {
            console.error(
                'Ошибка поиска задач:',
                error
            );

            message.error(
                error.response?.data?.error ||
                'Не удалось выполнить поиск задач'
            );
        } finally {
            setLoading(false);
        }
    };

    const goToInvoicesStep = async () => {
        try {
            if (selectedTaskKeys.length === 0) return message.warning('Выберите хотя бы одну задачу');
            setModalLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/registries/get-invoices-by-tasks', {
                params: {
                    taskIds: selectedTaskKeys.join(','),
                    docType: isEditMode ? selectedRegistry.type : (docType || activeTab || 'daily_report')
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // ПУНКТ 2: Автоматическое заполнение контрагента из первого шага
            if (apiFilters.region) {
                const foundCp = counterparties.find(c => c.name === apiFilters.region);
                if (foundCp) {
                    modalForm.setFieldsValue({ counterparty_id: foundCp.id });
                }
            }

            if (
                !modalForm.getFieldValue('period') &&
                apiFilters.geClosed &&
                apiFilters.leClosed
            ) {
                modalForm.setFieldsValue({
                    period: [
                        dayjs(apiFilters.geClosed).startOf('day'),
                        dayjs(apiFilters.leClosed).startOf('day')
                    ]
                });
            }

            setInvoicesSource(res.data);
            setWizardInvoiceSearch('');
            setWizardInvoiceAvailability('all');
            setWizardInvoicePage(1);
            setCurrentStep(1);
        } catch (e) { message.error('Ошибка получения данных'); } finally { setModalLoading(false); }
    };


    const handleCreate = async () => {
    try {
        const values =
            await modalForm.validateFields();

        setIsSaving(true);

        const token =
            localStorage.getItem('token');

        const headers = {
            Authorization:
                `Bearer ${token}`
        };

        const counterparty =
            counterparties.find(
                item =>
                    Number(item.id) ===
                    Number(
                        values.counterparty_id
                    )
            );

        /*
         * Сохраняем данные до закрытия мастера,
         * потому что после resetFields они исчезнут.
         */
        const createdDocumentType =
            values.type;

        const parentOrderId =
            values.parent_id
                ? Number(values.parent_id)
                : null;

        const isCreatedAct =
            createdDocumentType ===
                'customer_act' ||
            createdDocumentType ===
                'supplier_act';

        const { period, ...registryValues } = values;

        const response =
            await axios.post(
                '/api/registries',
                {
                    ...registryValues,

                    ...periodToPayload(period),

                    counterparty_name:
                        counterparty?.name,

                    selectedInvoiceIds:
                        selectedInvoiceKeys,

                    history_filters:
                        isCreatedAct && parentOrderId
                            ? { 'Основание': `Строки заказа ID ${parentOrderId}` }
                            : lastSearchFiltersRef.current
                },
                {
                    headers
                }
            );

        const createdRegistry =
            response.data;

        message.success(
            isCreatedAct
                ? 'Акт создан'
                : 'Документ создан'
        );

        /*
         * Закрываем мастер и очищаем
         * только данные мастера.
         */
        setIsModalVisible(false);
        setSelectedTaskKeys([]);
        setSelectedInvoiceKeys([]);
        setInvoicesSource([]);
        setCurrentStep(0);
        setIsCreateOnBasis(false);
        setIsAddingItemsToExistingRegistry(
            false
        );

        setWizardInvoiceSearch('');
        setWizardInvoiceAvailability(
            'all'
        );

        modalForm.resetFields();

        /*
         * Пересобираем основной список заказов.
         * Это обновит проценты и количество актов.
         */
        await fetchRegistries({
            page:
                registriesPagination.current,

            limit:
                registriesPagination.pageSize
        });

        /*
         * Полностью обновляем общий список актов.
         */
        await fetchAllActs();

        /*
         * Если создан акт на основании заказа,
         * заново загружаем акты именно этого заказа.
         */
        if (
            isCreatedAct &&
            parentOrderId
        ) {
            /*
             * Сначала удаляем старый кеш,
             * чтобы таблица не показывала
             * устаревший массив.
             */
            setExpandedActs(previous => {
                const next = {
                    ...previous
                };

                delete next[
                    parentOrderId
                ];

                return next;
            });

            setExpandingLoading(
                previous => ({
                    ...previous,

                    [parentOrderId]:
                        true
                })
            );

            try {
                const actsResponse =
                    await axios.get(
                        '/api/registries',
                        {
                            params: {
                                parent_id:
                                    parentOrderId,

                                type: [
                                    'customer_act',
                                    'supplier_act'
                                ],

                                page: 1,
                                limit: 200
                            },

                            headers
                        }
                    );

                const freshActs =
                    Array.isArray(
                        actsResponse.data
                    )
                        ? actsResponse.data
                        : Array.isArray(
                            actsResponse.data
                                ?.data
                        )
                            ? actsResponse.data
                                .data
                            : [];

                setExpandedActs(
                    previous => ({
                        ...previous,

                        [parentOrderId]:
                            freshActs
                    })
                );
            } catch (error) {
                console.error(
                    'Ошибка обновления таблицы актов:',
                    error
                );

                setExpandedActs(
                    previous => ({
                        ...previous,

                        [parentOrderId]:
                            []
                    })
                );

                message.error(
                    'Акт создан, но не удалось обновить таблицу актов'
                );
            } finally {
                setExpandingLoading(
                    previous => ({
                        ...previous,

                        [parentOrderId]:
                            false
                    })
                );
            }

            /*
             * Перезагружаем сам заказ:
             * состав, статистику закрытия,
             * номера актов и суммы.
             */
            await refreshRegistryView(
                parentOrderId,
                {
                    refreshList: false,
                    refreshActs: false,
                    refreshHistory: true
                }
            );

            /*
             * Возвращаем окно просмотра заказа,
             * которое закрывалось в createActOnBasis.
             */
            setIsViewModalVisible(true);
        }

        /*
         * Если создан обычный документ,
         * отдельного обновления заказа нет.
         */
        return createdRegistry;
    } catch (error) {
        console.error(
            'Ошибка создания документа:',
            error
        );

        message.error(
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Проверьте заполнение полей'
        );
    } finally {
        setIsSaving(false);
    }
};

    const handleBackRegistryView = async () => {

        if (
            registryViewHistory.length === 0
        ) {
            return;
        }

        const previous =
            registryViewHistory[
                registryViewHistory.length - 1
            ];

        setRegistryViewHistory(prev =>
            prev.slice(0, -1)
        );

        await viewRegistryDetails(
            {
                id: previous.id
            },
            false
        );
    };

    const closeRegistryView = () => {
        setIsViewModalVisible(false);
        setSelectedRegistry(null);

        setRegistryViewHistory([]);

        setRegistryViewSearch('');
        setRegistryViewClosureFilter('all');
        setRegistryViewPage(1);

        setIsEditMode(false);
        setIsHeaderEdit(false);

        setRegistryHistory([]);
        setCommentHistory([]);
    };

    const openedRegistryIdRef =
        useRef(null);
    const registryTabIdRef = useRef(createRegistryTabId());
    const isViewModalVisibleRef = useRef(false);
    const isEditModeRef = useRef(false);
    const isHeaderEditRef = useRef(false);
    const selectedRegistryRef = useRef(null);
    const refreshRegistryViewRef = useRef(null);
    const registrySyncBusyRef = useRef(false);
    const registryRemotePendingRef = useRef(false);

    useEffect(() => {
        isViewModalVisibleRef.current = isViewModalVisible;
    }, [isViewModalVisible]);

    useEffect(() => {
        isEditModeRef.current = isEditMode;
    }, [isEditMode]);

    useEffect(() => {
        isHeaderEditRef.current = isHeaderEdit;
    }, [isHeaderEdit]);

    useEffect(() => {
        selectedRegistryRef.current = selectedRegistry;
        if (selectedRegistry?.id) {
            openedRegistryIdRef.current = Number(selectedRegistry.id);
        }
    }, [selectedRegistry]);

    const broadcastRegistryUpdated = useCallback((registryId, extra = {}) => {
        const id = Number(registryId);
        if (!id) return;

        const payload = {
            type: 'registry-updated',
            registryId: id,
            tabId: registryTabIdRef.current,
            ts: Date.now(),
            ...extra
        };

        try {
            if (typeof BroadcastChannel !== 'undefined') {
                const channel = new BroadcastChannel(REGISTRY_SYNC_CHANNEL);
                channel.postMessage(payload);
                channel.close();
            }
        } catch (_) {
            /* ignore */
        }

        try {
            localStorage.setItem(
                REGISTRY_SYNC_STORAGE_KEY,
                JSON.stringify(payload)
            );
            localStorage.removeItem(REGISTRY_SYNC_STORAGE_KEY);
        } catch (_) {
            /* ignore */
        }
    }, []);

    const applyRemoteRegistrySync = useCallback(async (registryId, opts = {}) => {
        const id = Number(registryId);
        if (!id) return;

        if (
            !isViewModalVisibleRef.current ||
            Number(openedRegistryIdRef.current) !== id
        ) {
            return;
        }

        if (isEditModeRef.current || isHeaderEditRef.current) {
            registryRemotePendingRef.current = true;
            message.warning(
                'Документ изменён в другой вкладке. Закройте режим правки — данные обновятся автоматически.'
            );
            return;
        }

        if (registrySyncBusyRef.current) return;
        registrySyncBusyRef.current = true;

        try {
            const refreshed = await refreshRegistryViewRef.current?.(id, {
                refreshList: false,
                refreshActs: false,
                refreshHistory: true,
                fromRemoteSync: true,
                silent: true
            });

            if (refreshed && opts.notify !== false) {
                message.info('Документ обновлён из другой вкладки');
            }
            registryRemotePendingRef.current = false;
        } finally {
            registrySyncBusyRef.current = false;
        }
    }, []);

    useEffect(() => {
        let channel = null;

        const onMessage = event => {
            const data = event?.data || event;
            if (!data || data.type !== 'registry-updated') return;
            if (data.tabId === registryTabIdRef.current) return;
            applyRemoteRegistrySync(data.registryId);
        };

        try {
            if (typeof BroadcastChannel !== 'undefined') {
                channel = new BroadcastChannel(REGISTRY_SYNC_CHANNEL);
                channel.addEventListener('message', onMessage);
            }
        } catch (_) {
            channel = null;
        }

        const onStorage = e => {
            if (e.key !== REGISTRY_SYNC_STORAGE_KEY || !e.newValue) return;
            try {
                onMessage({ data: JSON.parse(e.newValue) });
            } catch (_) {
                /* ignore */
            }
        };

        window.addEventListener('storage', onStorage);

        return () => {
            window.removeEventListener('storage', onStorage);
            try {
                channel?.close();
            } catch (_) {
                /* ignore */
            }
        };
    }, [applyRemoteRegistrySync]);

    useEffect(() => {
        const softCheck = async () => {
            if (document.hidden) return;
            const id = Number(openedRegistryIdRef.current);
            if (!id || !isViewModalVisibleRef.current) return;
            if (isEditModeRef.current || isHeaderEditRef.current) {
                return;
            }
            if (registrySyncBusyRef.current) return;

            const token = localStorage.getItem('token');
            if (!token) return;

            try {
                const res = await axios.get(`/api/registries/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const prev = selectedRegistryRef.current;
                if (
                    registrySnapshotKey(prev) ===
                    registrySnapshotKey(res.data)
                ) {
                    if (registryRemotePendingRef.current) {
                        registryRemotePendingRef.current = false;
                    }
                    return;
                }

                await applyRemoteRegistrySync(id, { notify: true });
            } catch (_) {
                /* ignore soft check errors */
            }
        };

        const onFocus = () => {
            softCheck();
        };
        const onVisibility = () => {
            if (!document.hidden) softCheck();
        };

        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [applyRemoteRegistrySync]);

    /* После выхода из режима правки подтянуть чужие изменения */
    useEffect(() => {
        if (
            !isEditMode &&
            !isHeaderEdit &&
            registryRemotePendingRef.current &&
            isViewModalVisible &&
            selectedRegistry?.id
        ) {
            applyRemoteRegistrySync(selectedRegistry.id);
        }
    }, [
        isEditMode,
        isHeaderEdit,
        isViewModalVisible,
        selectedRegistry?.id,
        applyRemoteRegistrySync
    ]);

        const loadRegistryHistoryInBackground =
    async (
        registryId,
        headers
    ) => {
        try {
            const [
                historyResult,
                commentsResult
            ] =
                await Promise.allSettled([
                    axios.get(
                        `/api/registries/${registryId}/history`,
                        {
                            headers
                        }
                    ),

                    axios.get(
                        `/api/registries/${registryId}/comments`,
                        {
                            headers
                        }
                    )
                ]);

            /*
             * Пока запросы выполнялись,
             * пользователь мог открыть другой документ.
             */
            if (
                openedRegistryIdRef.current !==
                Number(registryId)
            ) {
                return;
            }

            if (
                historyResult.status ===
                'fulfilled'
            ) {
                setRegistryHistory(
                    Array.isArray(
                        historyResult.value.data
                    )
                        ? historyResult.value.data
                        : []
                );
            } else {
                console.error(
                    'Ошибка загрузки истории:',
                    historyResult.reason
                );

                setRegistryHistory([]);
            }

            if (
                commentsResult.status ===
                'fulfilled'
            ) {
                setCommentHistory(
                    Array.isArray(
                        commentsResult.value.data
                    )
                        ? commentsResult.value.data
                        : []
                );
            } else {
                console.error(
                    'Ошибка загрузки комментариев:',
                    commentsResult.reason
                );

                setCommentHistory([]);
            }
        } catch (error) {
            console.error(
                'Ошибка фоновой загрузки истории:',
                error
            );
        }
    };

   const viewRegistryDetails = async (
    record,
    saveHistory = true
) => {
    if (
        saveHistory &&
        selectedRegistry
    ) {
        setRegistryViewHistory(
            previous => [
                ...previous,
                selectedRegistry
            ]
        );
    }

    const registryId =
        Number(record.id);

    openedRegistryIdRef.current =
        registryId;

    setIsEditMode(false);
    setIsHeaderEdit(false);

    setRegistryViewSearch('');
    setRegistryViewClosureFilter('all');
    setRegistryViewPage(1);

    /*
     * Очищаем предыдущий документ.
     */
    setSelectedRegistry(null);
    setTaskDistributionStats({});

    setRegistryHistory([]);
    setCommentHistory([]);

    /*
     * Показываем модальное окно с индикатором.
     */
    setRegistryViewLoading(true);
    setIsViewModalVisible(true);

    const token =
        localStorage.getItem('token');

    const headers = {
        Authorization:
            `Bearer ${token}`
    };

    try {
        /*
         * Загружаем только основной документ.
         */
        const response =
            await axios.get(
                `/api/registries/${registryId}`,
                {
                    headers
                }
            );

        if (
            openedRegistryIdRef.current !==
            registryId
        ) {
            return;
        }

        setSelectedRegistry(
            response.data
        );

        setTaskDistributionStats(
            response.data
                ?.task_distribution_stats ||
            {}
        );

        headerForm.setFieldsValue({
            number:
                response.data.number,

            title:
                response.data.title,

            date:
                response.data.date
                    ? dayjs(
                        response.data.date
                    )
                    : null,

            period:
                registryPeriodValue(
                    response.data
                )
        });

        /*
         * ВАЖНО:
         * выключаем основной индикатор ДО запуска
         * истории и комментариев.
         */
        setRegistryViewLoading(false);

        /*
         * Сначала даём React и браузеру
         * отрисовать содержимое модального окна.
         *
         * Первый requestAnimationFrame:
         * React применил состояние.
         *
         * Второй requestAnimationFrame:
         * браузер уже выполнил хотя бы одну отрисовку.
         */
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (
                    openedRegistryIdRef.current !==
                    registryId
                ) {
                    return;
                }

                loadRegistryHistoryInBackground(
                    registryId,
                    headers
                );
            });
        });
    } catch (error) {
        if (
            openedRegistryIdRef.current ===
            registryId
        ) {
            setRegistryViewLoading(false);
            setIsViewModalVisible(false);
            setSelectedRegistry(null);

            message.error(
                error.response?.data?.error ||
                'Ошибка загрузки данных документа'
            );
        }

        console.error(
            'Ошибка в viewRegistryDetails:',
            error
        );
    }
};

    const createActOnBasis = async (report) => {
        setIsCreateOnBasis(true);
        setIsViewModalVisible(false);

        const actType = report.type === 'customer_order' ? 'customer_act' : 'supplier_act';
        const cp = counterparties.find(c => c.name === report.counterparty_name);

        modalForm.setFieldsValue({
            type: actType,
            counterparty_id: cp?.id,
            parent_id: report.id, // ID заказа
            title: `Акт на основании ${report.number || report.id}`,
            date: dayjs()
        });

        const taskIds = [...new Set(report.items.map(i => i.invoice.task_id))];
        setSelectedTaskKeys(taskIds);
        setSelectedInvoiceKeys([]);
        setIsModalVisible(true);
        setModalLoading(true);
        setWizardInvoiceSearch('');
        setWizardInvoiceAvailability(
            'all'
        );

        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/registries/get-invoices-by-tasks', {
                params: {
                    taskIds: taskIds.join(','),
                    docType: actType,
                    parentId: report.id // ПЕРЕДАЕМ ID ЗАКАЗА ДЛЯ БЛОКИРОВКИ СТРОК
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setInvoicesSource(res.data);
            setCurrentStep(1);
        } catch (e) {
            message.error('Ошибка подготовки данных');
            setIsModalVisible(false);
        } finally {
            setModalLoading(false);
        }
    };

    const filteredCpOptions = useMemo(() => {
        if (!isCreateOnBasis) {
            return counterparties.map(c => ({ label: c.name, value: c.id }));
        }
        // Собираем уникальные имена контрагентов из загруженных строк ВДР
        const namesInVdr = new Set(invoicesSource.map(inv => inv.counterparty));
        return counterparties
            .filter(c => namesInVdr.has(c.name))
            .map(c => ({ label: c.name, value: c.id }));
    }, [isCreateOnBasis, invoicesSource, counterparties]);


    // ФУНКЦИЯ "ВЫБРАТЬ ВСЕ" ДЛЯ ШАГА 2
    const isWizardInvoiceSelectable = invoice => {
        if (invoice?.is_used) {
            return false;
        }

        if (selectedParentId) {
            return (
                !invoice.order_id ||
                Number(invoice.order_id) ===
                    Number(selectedParentId)
            );
        }

        return !invoice.order_id;
    };

    const collectSelectableInvoiceIds = groups =>
        (Array.isArray(groups) ? groups : [])
            .flatMap(group => group.items || [])
            .filter(isWizardInvoiceSelectable)
            .map(invoice => Number(invoice.id))
            .filter(invoiceId => invoiceId > 0);

    const handleSelectAllInvoices = () => {
        setSelectedInvoiceKeys(
            collectSelectableInvoiceIds(
                filteredGroupedInvoicesSource
            )
        );
    };

    const handleSelectVisibleInvoices = () => {
        const pageIds = collectSelectableInvoiceIds(
            paginatedGroupedInvoicesSource
        );

        setSelectedInvoiceKeys(previous => {
            const merged = new Set(
                (previous || []).map(Number)
            );

            pageIds.forEach(id => merged.add(id));

            return [...merged];
        });
    };

    const handleClearVisibleInvoices = () => {
        const visibleIds = new Set(
            (paginatedGroupedInvoicesSource || [])
                .flatMap(group => group.items || [])
                .map(item => Number(item.id))
        );

        setSelectedInvoiceKeys(previous =>
            previous.filter(
                id => !visibleIds.has(Number(id))
            )
        );
    };

    const handleClearAllInvoices = () => {
        setSelectedInvoiceKeys([]);
    };

    const getTaskVal = (task, key) => {
        if (!task) return '-';
        // Ищем полную версию в кеше
        const fullTask = enrichedTasks[String(task.id)] || enrichedTasks[String(task.external_id)] || task;

        try {
            // 1. Поля верхнего уровня (Системные)
            if (key === 'id') return fullTask.external_id || fullTask.id || '-';
            if (key === 'assigned_user_fio') return fullTask.assigned_user_fio || task.assigned_user_fio || '-';
            if (key === 'description') return fullTask.text || fullTask.description || task.description || '-';
            if (key === 'news_date') {
                const date = fullTask.news_date || task.news_date;
                return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-';
            }
            if (key === 'news_type_name') return fullTask.news_type_name || task.news_type_name || '-';

            // 2. Парсинг custom_fields (в т.ч. из raw_data)
            const cf = getTaskCustomFieldsClient(fullTask);
            const fieldObj = cf?.[key];
            let val =
                fieldObj && typeof fieldObj === 'object'
                    ? fieldObj.value
                    : fieldObj;

            if (
                (val === undefined ||
                    val === null ||
                    val === '' ||
                    val === 'false' ||
                    String(val).toLowerCase() === 'none') &&
                key === 'Nomer_TT'
            ) {
                val = extractNomerTtFromTitle(fullTask);
            }

            if (val === undefined || val === null || val === '' || val === 'false') return '-';

            // 3. Обработка дат в кастомных полях: unix-время в UTC → московское, независимо от пояса браузера
            if (key.toLowerCase().includes('vremja') || key.toLowerCase().includes('srok')) {
                const raw = val.toString().trim();
                if (/^\d{10}$/.test(raw)) {
                    return dayjs.unix(Number(raw)).tz(MOSCOW_TIMEZONE).format('DD.MM.YYYY HH:mm');
                }
                if (/^\d{13}$/.test(raw)) {
                    return dayjs(Number(raw)).tz(MOSCOW_TIMEZONE).format('DD.MM.YYYY HH:mm');
                }
            }

            return val;
        } catch (e) {
            return '-';
        }
    };

    const registryViewFooterStats = useMemo(() => {
    const items =
        Array.isArray(selectedRegistry?.items)
            ? selectedRegistry.items
            : [];

    const taskIds = new Set();

    let amount = 0;

    items.forEach(item => {
        const invoice =
            item?.invoice;

        if (!invoice) {
            return;
        }

        const taskId =
            invoice.task_id ||
            invoice.task?.id ||
            invoice.task?.external_id;

        if (taskId !== undefined && taskId !== null) {
            taskIds.add(
                String(taskId)
            );
        }

        amount +=
            Number(invoice.amount) || 0;
    });

    return {
        tasksCount:
            taskIds.size,

        rowsCount:
            items.filter(
                item => Boolean(item?.invoice)
            ).length,

        amount:
            mathRound(amount)
    };
}, [selectedRegistry]);

    const taskSummaryData = useMemo(() => {
        if (!selectedRegistry?.items || selectedRegistry.items.length === 0) return [];

        const map = {};

        selectedRegistry.items.forEach(item => {
            const originalTask = item.invoice?.task;
            if (!originalTask) return; // Пропускаем, если нет данных о задаче

            // Ищем в кеше. Важно: приводим ключи к строке для надежности
            const task = enrichedTasks[String(originalTask.id)] ||
                enrichedTasks[String(originalTask.external_id)] ||
                originalTask;

            // Если внешнего ID нет, используем внутренний ID как ключ группировки
            const taskKey = task.external_id || task.id;
            if (!taskKey) return;

            if (!map[taskKey]) {
                map[taskKey] = {
                    task: task,
                    sum_total: 0,
                    sum_mileage: 0,
                    sum_works: 0,
                    sum_materials: 0,
                    // Км для суммы — из ВДР 1.1.2 (не Probeg из ActiveMap)
                    mileage_val: 0
                };
            }

            const amount = parseFloat(item.invoice?.amount || 0);
            const qty = parseFloat(item.invoice?.quantity || 0) || 0;
            const article = (item.invoice?.article || '').toLowerCase();
            const rateName = (item.invoice?.rate_name || '').toLowerCase();

            const isMileage = article.includes('пробег') ||
                article.includes('километр') ||
                rateName.includes('километр') ||
                article.includes('вызов');

            const isMaterial = article.includes('материал') ||
                article.includes('оборудование') ||
                rateName.includes('материал');

            if (isMileage) {
                map[taskKey].sum_mileage += amount;
                map[taskKey].mileage_val += qty;
            } else if (isMaterial) {
                map[taskKey].sum_materials += amount;
            } else {
                map[taskKey].sum_works += amount;
            }

            map[taskKey].sum_total += amount;
        });

        return Object.values(map);
    }, [selectedRegistry, enrichedTasks]); // Оставляем зависимости
    
    const openExportFormatModal =
    () => {
        if (!selectedRegistry) {
            message.warning(
                'Документ не выбран'
            );

            return;
        }

        /*
         * Для заказа покупателя
         * по умолчанию предлагаем
         * новый формат.
         */
        let defaultFormat =
    'daily_report';

if (
    selectedRegistry.type ===
    'customer_order'
) {
    defaultFormat =
        'customer_order';
}

if (
    selectedRegistry.type ===
    'supplier_order'
) {
    defaultFormat =
        'supplier_order';
}

setSelectedExportFormat(
    defaultFormat
);

        setExportFormatModalOpen(
            true
        );
    };

    const downloadRegistryExport =
    async (
        exportFormat
    ) => {
        if (!selectedRegistry?.id) {
            return;
        }

        setExportLoading(
            true
        );

        try {
            const token =
                localStorage.getItem(
                    'token'
                );

            let url =
                `/api/registries/${
                    selectedRegistry.id
                }/daily-export`;

            let fallbackName =
                `Ежедневный отчет_${
                    selectedRegistry.number ||
                    selectedRegistry.id
                }.xlsx`;

            if (
                exportFormat ===
                    'customer_order' ||
                exportFormat ===
                    'customer_order_with_note' ||
                exportFormat ===
                    'customer_order_name_km_inline' ||
                exportFormat ===
                    'customer_order_with_note_km_inline'
            ) {
                url =
                    `/api/registries/${
                        selectedRegistry.id
                    }/customer-order-export?template=${
                        encodeURIComponent(
                            exportFormat
                        )
                    }`;

                fallbackName =
                    `Заказ покупателя_${
                        selectedRegistry.number ||
                        selectedRegistry.id
                    }.xlsx`;
            }

            if (
    exportFormat ===
    'supplier_order'
) {
    url =
        `/api/registries/${
            selectedRegistry.id
        }/supplier-order-export`;

    fallbackName =
        `Заказ поставщика_${
            selectedRegistry.number ||
            selectedRegistry.id
        }.xlsx`;
}

            const response =
                await axios.get(
                    url,
                    {
                        responseType:
                            'blob',

                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );

            const contentDisposition =
                response.headers[
                    'content-disposition'
                ] || '';

            let fileName =
                fallbackName;

            const utf8Match =
                contentDisposition.match(
                    /filename\*=UTF-8''([^;]+)/i
                );

            const plainMatch =
                contentDisposition.match(
                    /filename="?([^"]+)"?/i
                );

            if (
                utf8Match?.[1]
            ) {
                fileName =
                    decodeURIComponent(
                        utf8Match[1]
                    );
            } else if (
                plainMatch?.[1]
            ) {
                fileName =
                    plainMatch[1];
            }

            const blobUrl =
                window.URL
                    .createObjectURL(
                        response.data
                    );

            const anchor =
                document.createElement(
                    'a'
                );

            anchor.href =
                blobUrl;

            anchor.download =
                fileName;

            document.body
                .appendChild(
                    anchor
                );

            anchor.click();
            anchor.remove();

            window.URL
                .revokeObjectURL(
                    blobUrl
                );

            setExportFormatModalOpen(
                false
            );

            message.success(
                'Excel-файл сформирован'
            );
        } catch (error) {
            console.error(
                'Ошибка экспорта Excel:',
                error
            );

            /*
             * При responseType blob тело ошибки
             * тоже приходит Blob.
             */
            let errorText =
                'Ошибка формирования Excel';

            try {
                if (
                    error.response?.data
                        instanceof Blob
                ) {
                    const text =
                        await error.response
                            .data.text();

                    const parsed =
                        JSON.parse(
                            text
                        );

                    errorText =
                        parsed.error ||
                        errorText;
                } else {
                    errorText =
                        error.response
                            ?.data?.error ||
                        errorText;
                }
            } catch {
                // Оставляем стандартный текст.
            }

            message.error(
                errorText
            );
        } finally {
            setExportLoading(
                false
            );
        }
    };

    const downloadPaymentDocsExport = async kind => {
        const selectedIds = [
            ...new Set(
                [
                    ...selectedOrderKeys,
                    ...selectedActKeys
                ].map(value => Number(value))
                    .filter(
                        value =>
                            Number.isInteger(value) &&
                            value > 0
                    )
            )
        ];

        if (!selectedIds.length) {
            message.warning(
                'Выберите заказы или акты поставщика'
            );
            return;
        }

        const kindLabel =
            kind === 'work_statement'
                ? 'Ведомость выполненных работ'
                : 'Заявка на оплату';

        setPaymentDocsExportLoading(true);

        try {
            const token =
                localStorage.getItem('token');

            const response = await axios.post(
                '/api/registries/payment-docs-export',
                {
                    ids: selectedIds,
                    kind
                },
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const contentDisposition =
                response.headers[
                    'content-disposition'
                ] || '';

            let fileName =
                `${kindLabel}.xlsx`;

            const utf8Match =
                contentDisposition.match(
                    /filename\*=UTF-8''([^;]+)/i
                );

            const plainMatch =
                contentDisposition.match(
                    /filename="?([^"]+)"?/i
                );

            if (utf8Match?.[1]) {
                fileName = decodeURIComponent(
                    utf8Match[1]
                );
            } else if (plainMatch?.[1]) {
                fileName = plainMatch[1];
            }

            const blobUrl =
                window.URL.createObjectURL(
                    new Blob([response.data])
                );

            const anchor =
                document.createElement('a');

            anchor.href = blobUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(blobUrl);

            message.success(
                `${kindLabel}: выгружено ${selectedIds.length} док.`
            );
        } catch (error) {
            let errorText =
                `Ошибка выгрузки: ${kindLabel}`;

            try {
                if (
                    error.response?.data instanceof
                    Blob
                ) {
                    const text =
                        await error.response.data.text();
                    const parsed = JSON.parse(text);
                    errorText =
                        parsed.error || errorText;
                } else {
                    errorText =
                        error.response?.data?.error ||
                        errorText;
                }
            } catch {
                // keep default
            }

            message.error(errorText);
        } finally {
            setPaymentDocsExportLoading(false);
        }
    };

    const downloadSupplierActsExport = async () => {
        const selectedIds = [
            ...new Set(
                selectedActKeys
                    .map(value => Number(value))
                    .filter(
                        value =>
                            Number.isInteger(value) &&
                            value > 0
                    )
            )
        ];

        if (!selectedIds.length) {
            message.warning(
                'Выберите акты поставщика'
            );
            return;
        }

        const supplierActIds = selectedIds.filter(id => {
            const act = allActs.find(
                row => Number(row.id) === id
            );

            return act?.type === 'supplier_act';
        });

        if (!supplierActIds.length) {
            message.warning(
                'Среди выбранных нет актов поставщика'
            );
            return;
        }

        if (supplierActIds.length < selectedIds.length) {
            message.warning(
                'Акты покупателя пропущены — выгружаются только акты поставщика'
            );
        }

        setSupplierActsExportLoading(true);

        try {
            const token =
                localStorage.getItem('token');

            const response = await axios.post(
                '/api/registries/supplier-acts-export',
                {
                    ids: supplierActIds
                },
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const contentDisposition =
                response.headers[
                    'content-disposition'
                ] || '';

            let fileName = 'Акты_поставщика.zip';

            const utf8Match =
                contentDisposition.match(
                    /filename\*=UTF-8''([^;]+)/i
                );

            const plainMatch =
                contentDisposition.match(
                    /filename="?([^"]+)"?/i
                );

            if (utf8Match?.[1]) {
                fileName = decodeURIComponent(
                    utf8Match[1]
                );
            } else if (plainMatch?.[1]) {
                fileName = plainMatch[1];
            }

            const blobUrl =
                window.URL.createObjectURL(
                    new Blob([response.data])
                );

            const anchor =
                document.createElement('a');

            anchor.href = blobUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(blobUrl);

            message.success(
                `Акты поставщика: ${supplierActIds.length} шт. (Excel + PDF)`
            );
        } catch (error) {
            let errorText =
                'Ошибка выгрузки актов поставщика';

            try {
                if (
                    error.response?.data instanceof
                    Blob
                ) {
                    const text =
                        await error.response.data.text();
                    const parsed = JSON.parse(text);
                    errorText =
                        parsed.error || errorText;
                } else {
                    errorText =
                        error.response?.data?.error ||
                        errorText;
                }
            } catch {
                // keep default
            }

            message.error(errorText);
        } finally {
            setSupplierActsExportLoading(false);
        }
    };

    const downloadSupplierOrdersExport = async () => {
        const selectedIds = [
            ...new Set(
                selectedOrderKeys
                    .map(value => Number(value))
                    .filter(
                        value =>
                            Number.isInteger(value) &&
                            value > 0
                    )
            )
        ];

        if (!selectedIds.length) {
            message.warning(
                'Выберите заказы поставщика'
            );
            return;
        }

        const supplierOrderIds = selectedIds.filter(id => {
            const order = (registries || []).find(
                row => Number(row.id) === id
            );

            return !order || order.type === 'supplier_order';
        });

        if (!supplierOrderIds.length) {
            message.warning(
                'Среди выбранных нет заказов поставщика'
            );
            return;
        }

        setSupplierOrdersExportLoading(true);

        try {
            const token =
                localStorage.getItem('token');

            const response = await axios.post(
                '/api/registries/supplier-orders-export',
                {
                    ids: supplierOrderIds
                },
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const contentDisposition =
                response.headers[
                    'content-disposition'
                ] || '';

            let fileName = 'Заказы_поставщика.zip';

            const utf8Match =
                contentDisposition.match(
                    /filename\*=UTF-8''([^;]+)/i
                );

            const plainMatch =
                contentDisposition.match(
                    /filename="?([^"]+)"?/i
                );

            if (utf8Match?.[1]) {
                fileName = decodeURIComponent(
                    utf8Match[1]
                );
            } else if (plainMatch?.[1]) {
                fileName = plainMatch[1];
            }

            const blobUrl =
                window.URL.createObjectURL(
                    new Blob([response.data])
                );

            const anchor =
                document.createElement('a');

            anchor.href = blobUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(blobUrl);

            message.success(
                `Заказы поставщика: ${supplierOrderIds.length} шт. (Excel + PDF)`
            );
        } catch (error) {
            let errorText =
                'Ошибка выгрузки заказов поставщика';

            try {
                if (
                    error.response?.data instanceof
                    Blob
                ) {
                    const text =
                        await error.response.data.text();
                    const parsed = JSON.parse(text);
                    errorText =
                        parsed.error || errorText;
                } else {
                    errorText =
                        error.response?.data?.error ||
                        errorText;
                }
            } catch {
                // keep default
            }

            message.error(errorText);
        } finally {
            setSupplierOrdersExportLoading(false);
        }
    };

    const confirmRegistryExport =
    async () => {
        const isCustomerOrderTemplate = [
            'customer_order',
            'customer_order_with_note',
            'customer_order_name_km_inline',
            'customer_order_with_note_km_inline'
        ].includes(selectedExportFormat);

        if (
            isCustomerOrderTemplate &&
            selectedRegistry?.type !==
                'customer_order'
        ) {
            message.warning(
                'Формат «Заказ покупателя» доступен только для заказа покупателя'
            );

            return;
        }

        if (
            selectedExportFormat ===
                'supplier_order' &&
            selectedRegistry?.type !==
                'supplier_order'
        ) {
            message.warning(
                'Формат «Заказ поставщика» доступен только для заказа поставщика'
            );

            return;
        }

        await downloadRegistryExport(
            selectedExportFormat
        );
    };

    

    const handleExportExcel = async (record) => {
        const hide = message.loading('Формирование полной выгрузки...', 0);

        try {
            const workbook = new ExcelJS.Workbook();

            const headerStyle = {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '70AD47' } },
                font: { color: { argb: 'FFFFFF' }, bold: true },
                alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
                border: {
                    top: {style:'thin'}, left: {style:'thin'},
                    bottom: {style:'thin'}, right: {style:'thin'}
                }
            };

            const dataStyle = {
                alignment: { vertical: 'middle', wrapText: true },
                border: {
                    top: {style:'thin'}, left: {style:'thin'},
                    bottom: {style:'thin'}, right: {style:'thin'}
                }
            };

            // --- ВКЛАДКА 1: СВОДНЫЙ ОТЧЕТ (как Таблица 1 в UI) ---
            const ws1 = workbook.addWorksheet('Сводный отчет');
            ws1.columns = [
                { header: 'ID заявки', key: 'id', width: 12 },
                { header: 'БС', key: 'bs', width: 12 },
                { header: 'Дата создания', key: 'date', width: 18 },
                { header: 'Номер ТТ', key: 'tt', width: 15 },
                { header: 'Текст задания', key: 'desc', width: 40 },
                { header: 'Время появления', key: 'in', width: 18 },
                { header: 'Время покидания', key: 'out', width: 18 },
                { header: 'Исполнитель', key: 'user', width: 25 },
                { header: 'Вид работы*', key: 'type', width: 20 },
                { header: 'Описание работ', key: 'work', width: 40 },
                { header: 'Км (1.1.2)', key: 'mil_v', width: 12 },
                { header: 'Сумма км', key: 'mil_s', width: 15 },
                { header: 'Сумма работы', key: 'work_s', width: 15 },
                { header: 'Сумма оборуд.', key: 'mat_s', width: 15 },
                { header: 'Итоговая Стоимость', key: 'final', width: 18 }
            ];

            taskSummaryData.forEach(item => {
                const row = ws1.addRow({
                    id: item.task?.external_id || item.task?.id,
                    bs: getTaskVal(item.task, 'Nomer_BS'),
                    date: item.task?.news_date ? dayjs(item.task.news_date).format('DD.MM.YYYY HH:mm') : '-',
                    tt: getTaskVal(item.task, 'Nomer_TT'),
                    desc: getTaskVal(item.task, 'description'),
                    in: getTaskVal(item.task, 'Vremja_pojavlenija_na_sayte'),
                    out: getTaskVal(item.task, 'Vremja_pokidanija_sayta'),
                    user: getTaskVal(item.task, 'assigned_user_fio'),
                    type: item.task?.news_type_name || '-',
                    work: getTaskVal(item.task, 'Opisanie_vepolnenneh_rabot'),
                    mil_v: item.mileage_val,
                    mil_s: item.sum_mileage,
                    work_s: item.sum_works,
                    mat_s: item.sum_materials,
                    final: item.sum_total
                });
                row.eachCell(c => c.style = dataStyle);
            });
            ws1.getRow(1).eachCell(c => c.style = headerStyle);

            // --- ВКЛАДКА 2: ДЕТАЛИЗАЦИЯ (Полная копия Таблицы 2 из UI) ---
            const ws2 = workbook.addWorksheet('Детализация');
            ws2.columns = [
                { header: 'ID заявки', key: 'id', width: 12 },
                { header: 'БС', key: 'bs', width: 12 },
                { header: 'Дата создания', key: 'date', width: 18 },
                { header: 'Номер ТТ', key: 'tt', width: 15 },
                { header: 'Текст задания', key: 'title', width: 30 },
                { header: 'Время появления', key: 'in', width: 18 },
                { header: 'Время покидания', key: 'out', width: 18 },
                { header: 'Исполнитель', key: 'user', width: 25 },
                { header: 'Представитель Заказчика', key: 'client', width: 25 },
                { header: 'Вид работы*', key: 'type', width: 20 },
                { header: 'Описание работ', key: 'work', width: 40 },
                { header: 'Оборудование', key: 'note', width: 25 },
                { header: 'Позиция ТЦП', key: 'rate', width: 40 },
                { header: 'Кол-во', key: 'qty', width: 10 },
                { header: 'Цена за ед', key: 'price', width: 12 },
                { header: 'Сумма', key: 'amt', width: 15 }
            ];

            selectedRegistry.items.forEach(item => {
                const inv = item.invoice;
                const task = inv?.task;
                const row = ws2.addRow({
                    id: getTaskVal(task, 'id'), // Используем getTaskVal
                    bs: getTaskVal(task, 'Nomer_BS'),
                    date: getTaskVal(task, 'news_date'), // Используем getTaskVal
                    tt: getTaskVal(task, 'Nomer_TT'),
                    title: task?.title || '-',
                    in: getTaskVal(task, 'Vremja_pojavlenija_na_sayte'),
                    out: getTaskVal(task, 'Vremja_pokidanija_sayta'),
                    user: getTaskVal(task, 'assigned_user_fio'), // Используем getTaskVal
                    client: getTaskVal(task, 'Zakazchik'),
                    type: getTaskVal(task, 'news_type_name'), // Используем getTaskVal
                    work: getTaskVal(task, 'Opisanie_vepolnenneh_rabot'),
                    note: inv?.note || '-',
                    rate: inv?.rate_name,
                    qty: inv?.quantity,
                    price: parseFloat(inv?.price || 0),
                    amt: parseFloat(inv?.amount || 0)
                });
                row.eachCell(c => c.style = dataStyle);
            });
            ws2.getRow(1).eachCell(c => c.style = headerStyle);

            // Сохранение файла
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `DailyReport_${record.number || record.id}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);

            message.success('Файл успешно сформирован');
        } catch (e) {
            console.error('Excel Export Error:', e);
            message.error('Ошибка при генерации Excel');
        } finally {
            hide();
        }
    };

    const confirmAttachInvoicesToRegistry =
    async () => {
        if (
            !attachInvoiceProposal?.registryId ||
            !attachInvoiceProposal
                ?.invoiceIds?.length
        ) {
            return;
        }

        setAttachInvoiceLoading(true);

        try {
            await attachCreatedInvoicesToRegistry({
                registryId:
                    attachInvoiceProposal
                        .registryId,

                invoiceIds:
                    attachInvoiceProposal
                        .invoiceIds
            });

            message.success(
                `Добавлено строк в заказ: ${
                    attachInvoiceProposal
                        .invoiceIds.length
                }`
            );

            setAttachInvoiceProposal(null);
            setTaskModalRegistryContext(null);

            /*
             * Возвращаем окно просмотра заказа.
             */
            setIsViewModalVisible(true);
        } catch (error) {
            console.error(
                'Ошибка добавления строк в заказ:',
                error
            );

            message.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                'Не удалось добавить строки в заказ'
            );
        } finally {
            setAttachInvoiceLoading(false);
        }
    };

    const cancelAttachInvoicesToRegistry =
    async () => {
        const registryId =
            attachInvoiceProposal?.registryId;

        setAttachInvoiceProposal(null);
        setTaskModalRegistryContext(null);

        if (registryId) {
            await refreshRegistryView(
                registryId,
                {
                    refreshList: true,
                    refreshActs: true,
                    refreshHistory: false
                }
            );
        }

        setIsViewModalVisible(true);
    };


    const handleDeleteRegistry = async (id, parentId = null) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/registries/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            message.success('Удалено');

            // Если мы удаляем Акт, который находится внутри Заказа (есть parentId)
            if (parentId) {
                setExpandedActs(prev => {
                    const currentActs = Array.isArray(prev[parentId])
                        ? prev[parentId]
                        : [];

                    return {
                        ...prev,
                        [parentId]: currentActs.filter(
                            item => item.id !== id
                        )
                    };
                });
            }

            // Обновляем остальные таблицы для синхронизации сумм и статусов
            fetchRegistries();
            fetchAllActs();
        } catch (e) {
            message.error('Ошибка при удалении');
        }
    };

    const updateRegistryStatus = async (newStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/registries/${selectedRegistry.id}/status`,
                { status: newStatus },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            message.success('Статус обновлен');

            // Обновляем локальное состояние, чтобы изменения были видны сразу
            setSelectedRegistry(prev => ({ ...prev, status: newStatus }));
            fetchRegistries(); // Обновляем основной список
            await refreshRegistryView(
                selectedRegistry.id,
                {
                    refreshList: true,
                    refreshActs: false,
                    refreshHistory: true
                }
            );
        } catch (e) {
            message.error('Ошибка при смене статуса');
        }
    };

    const filteredTasksSource = useMemo(() => {
        if (!hideFullyReported) return tasksSource;
        return tasksSource.filter(task => {
            const total = parseInt(task.total_income_count || 0);
            const assigned = parseInt(task.assigned_income_count || 0);

            // Скрываем, если (позиций > 0) И (все позиции уже в отчетах)
            if (total > 0 && total === assigned) return false;
            return true;
        });
    }, [tasksSource, hideFullyReported]);

    const openVdrEditModal = (item) => {
        setEditingVdrRecord(item);
        const inv = item.invoice;

        vdrEditForm.setFieldsValue({
            quantity: inv.quantity || 0,
            percent: parsePercent(inv.percent, 100),
            penalty: inv.penalty || 0
        });
        setVdrEditModalVisible(true);
    };

    const saveVdrEdit = async () => {
        try {
            const { quantity, percent, penalty } =
                await vdrEditForm.validateFields();
            const token = localStorage.getItem('token');
            const inv = editingVdrItem.invoice;

            const percentValue =
                percent === undefined || percent === null
                    ? 100
                    : Number(percent) || 0;

            const baseAmount =
                inv.price * quantity * (percentValue / 100);
            const newAmount = mathRound(
                baseAmount * (1 - (penalty || 0) / 100)
            );

            const updateData = {
                quantity,
                percent: percentValue,
                penalty,
                amount: newAmount
            };

            await axios.put(
                `/api/invoices/${editingVdrItem.invoice_id}`,
                updateData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            message.success('Данные строки обновлены');
            setVdrEditModalVisible(false);

            const res = await axios.get(
                `/api/registries/${selectedRegistry.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            setSelectedRegistry(res.data);
            fetchRegistries();
            await refreshRegistryView(selectedRegistry.id, {
                refreshList: true,
                refreshActs: true,
                refreshHistory: true
            });
        } catch (e) {
            if (e?.errorFields) {
                message.error(
                    'Проверьте заполнение обязательных полей'
                );
                return;
            }
            console.error('Ошибка сохранения ВДР:', e);
            message.error(
                e?.response?.data?.message ||
                    e?.response?.data?.error ||
                    e?.message ||
                    'Ошибка сохранения ВДР'
            );
        }
    };

    // 2. ОТКРЫТИЕ "БОЛЬШОГО ОКНА" (как в TasksPage)
    const openFullTaskModal = async (
    dbId,
    extId,
    sourceRegistry = null
) => {
    pendingOrderInvoiceTempIdsRef
    .current
    .clear();
    /*
     * Фиксируем заказ, из которого было открыто
     * большое окно ВДР.
     *
     * Не сохраняем весь selectedRegistry с items,
     * чтобы не держать тяжёлый объект.
     */
    const registryContext =
        sourceRegistry?.id
            ? {
                id:
                    Number(
                        sourceRegistry.id
                    ),

                type:
                    sourceRegistry.type,

                number:
                    sourceRegistry.number ||
                    null,

                title:
                    sourceRegistry.title ||
                    null
            }
            : selectedRegistry?.id
                ? {
                    id:
                        Number(
                            selectedRegistry.id
                        ),

                    type:
                        selectedRegistry.type,

                    number:
                        selectedRegistry.number ||
                        null,

                    title:
                        selectedRegistry.title ||
                        null
                }
                : null;

    setTaskModalRegistryContext(
        registryContext
    );

    console.log(
        '[OPEN FULL VDR CONTEXT]',
        registryContext
    );

    setTaskMetadataLoading(true);
    setTaskModalOpen(true);

    setActiveTaskDbId(
        Number(dbId)
    );

    setActiveTaskExtId(
        extId
    );

    setHasUnsavedChanges(false);
    setEditingInvoice(null);
    setCommentHistory([]);
    setRegistryHistory([]);

    const loadGen = ++taskModalLoadGenRef.current;

    // Шапка сразу из кэша реестра — не ждём /ext
    const cached =
        enrichedTasks[String(dbId)] ||
        enrichedTasks[String(extId)] ||
        null;
    setTaskData(cached || null);

    const token =
        localStorage.getItem('token');

    const headers = {
        Authorization:
            `Bearer ${token}`
    };

    try {
        // Справочники (КА + номенклатура) уже на странице — не тянем повторно
        const dictPromise = dictionariesLoadedRef.current
            ? Promise.resolve()
            : fetchDictionaries();

        // Для редактирования ВДР критичны только инвойсы (+ справочники при первом открытии)
        const [inv] = await Promise.all([
            axios.get(
                `/api/tasks/${dbId}/invoices`,
                { headers }
            ),
            dictPromise
        ]);

        if (taskModalLoadGenRef.current !== loadGen) {
            return;
        }

        setTaskInvoices({
            income:
                inv.data?.income || [],

            expense:
                inv.data?.expense || []
        });

        setTaskComments(
            inv.data?.comments || ''
        );

        setTaskMetadataLoading(false);

        // Шапка, история и комментарии — в фоне, UI уже открыт
        Promise.all([
            axios.get(
                `/api/tasks/ext/${extId}`,
                { headers }
            ),
            axios.get(
                `/api/tasks/${dbId}/history`,
                { headers }
            ),
            axios.get(
                `/api/tasks/${dbId}/comments`,
                { headers }
            )
        ]).then(([ext, hist, comms]) => {
            if (taskModalLoadGenRef.current !== loadGen) {
                return;
            }
            if (ext?.data) {
                setTaskData(ext.data);
            }
            setRegistryHistory(hist.data || []);
            setCommentHistory(comms.data || []);
        }).catch((bgErr) => {
            console.warn(
                'Фоновая загрузка метаданных задачи:',
                bgErr
            );
        });
    } catch (error) {
        if (taskModalLoadGenRef.current !== loadGen) {
            return;
        }

        console.error(
            'Ошибка открытия полного ВДР:',
            error
        );

        message.error(
            error.response?.data?.message ||
            error.response?.data?.error ||
            'Ошибка загрузки данных задачи'
        );

        setTaskModalOpen(false);
        setTaskModalRegistryContext(null);
        setTaskMetadataLoading(false);
    }
};

    // 3. СОХРАНЕНИЕ В "БОЛЬШОМ ОКНЕ" (логика из TasksPage)
  const saveTaskVdrChanges = async () => {
    if (editingInvoice) {
        message.warning(
            'Сначала сохраните редактируемую строку'
        );
        return;
    }

    if (!activeTaskDbId) {
        message.error(
            'Не определена редактируемая задача'
        );
        return;
    }

    const registrySnapshot =
        taskModalRegistryContext?.id
            ? {
                id: Number(
                    taskModalRegistryContext.id
                ),

                type:
                    taskModalRegistryContext.type,

                number:
                    taskModalRegistryContext.number ||
                    null,

                title:
                    taskModalRegistryContext.title ||
                    null
            }
            : null;

    /*
     * Делаем копию временных ID до запроса.
     */
    const pendingTempIds =
        new Set(
            pendingOrderInvoiceTempIdsRef
                .current
        );

    console.log(
        '[SAVE VDR CONTEXT]',
        {
            registrySnapshot,
            pendingTempIds:
                [...pendingTempIds]
        }
    );

    const token =
        localStorage.getItem('token');

    const headers = {
        Authorization:
            `Bearer ${token}`
    };

    try {
        const response =
            await axios.put(
                `/api/tasks/${activeTaskDbId}/invoices`,
                {
                    income:
                        taskInvoices.income ||
                        [],

                    expense:
                        taskInvoices.expense ||
                        [],

                    comments:
                        taskComments
                },
                {
                    headers
                }
            );

        const savedData =
            response.data || {};

        console.log(
            '[SAVE VDR RESPONSE]',
            savedData
        );

        const refreshedResponse =
            await axios.get(
                `/api/tasks/${activeTaskDbId}/invoices`,
                {
                    headers
                }
            );

        const refreshedIncome =
            Array.isArray(
                refreshedResponse.data
                    ?.income
            )
                ? refreshedResponse.data
                    .income
                : [];

        const refreshedExpense =
            Array.isArray(
                refreshedResponse.data
                    ?.expense
            )
                ? refreshedResponse.data
                    .expense
                : [];

        setTaskInvoices({
            income:
                refreshedIncome,

            expense:
                refreshedExpense
        });

        setTaskComments(
            refreshedResponse.data
                ?.comments || ''
        );

        setHasUnsavedChanges(false);

        message.success(
            'ВДР задачи обновлён'
        );

        /*
         * Если окно открыто не из заказа,
         * ничего не предлагаем.
         */
        if (
            !registrySnapshot?.id ||
            ![
                'customer_order',
                'supplier_order'
            ].includes(
                registrySnapshot.type
            )
        ) {
            pendingOrderInvoiceTempIdsRef
                .current
                .clear();

            return;
        }

        const expectedInvoiceType =
            registrySnapshot.type ===
            'customer_order'
                ? 'income'
                : 'expense';

        const createdMappings =
            Array.isArray(
                savedData.created_invoices
            )
                ? savedData.created_invoices
                : [];

        /*
         * Выбираем только строки, временные ID
         * которых мы сохранили при добавлении.
         */
        let suitableCreatedInvoices =
            createdMappings.filter(
                item =>
                    item &&
                    item.type ===
                        expectedInvoiceType &&
                    pendingTempIds.has(
                        String(
                            item.client_temp_id
                        )
                    ) &&
                    Number(
                        item.invoice_id
                    ) > 0
            );

        /*
         * Резервный вариант:
         * если сервер не вернул client_temp_id,
         * берём созданные строки нужного типа.
         */
        if (
            suitableCreatedInvoices
                .length === 0 &&
            pendingTempIds.size > 0
        ) {
            suitableCreatedInvoices =
                createdMappings.filter(
                    item =>
                        item &&
                        item.type ===
                            expectedInvoiceType &&
                        Number(
                            item.invoice_id
                        ) > 0
                );
        }

        console.log(
            '[CREATED INVOICES FOR ORDER]',
            {
                expectedInvoiceType,
                pendingTempIds:
                    [...pendingTempIds],
                createdMappings,
                suitableCreatedInvoices
            }
        );

        /*
         * После обработки очищаем ожидающие ID.
         */
        pendingOrderInvoiceTempIdsRef
            .current
            .clear();

        if (
            suitableCreatedInvoices
                .length === 0
        ) {
            message.warning(
                expectedInvoiceType ===
                'income'
                    ? 'Новая доходная строка сохранена, но сервер не вернул её ID для добавления в заказ'
                    : 'Новая расходная строка сохранена, но сервер не вернул её ID для добавления в заказ'
            );

            await refreshRegistryView(
                registrySnapshot.id,
                {
                    refreshList: true,
                    refreshActs: true,
                    refreshHistory: false
                }
            );

            return;
        }

        const invoiceIds = [
            ...new Set(
                suitableCreatedInvoices
                    .map(item =>
                        Number(
                            item.invoice_id
                        )
                    )
                    .filter(id => id > 0)
            )
        ];

        const totalAmount =
            suitableCreatedInvoices.reduce(
                (sum, item) =>
                    sum +
                    (
                        Number(
                            item.amount
                        ) || 0
                    ),
                0
            );

        const registryTypeTitle =
            registrySnapshot.type ===
            'customer_order'
                ? 'заказ покупателя'
                : 'заказ поставщика';

        /*
         * Закрываем большое окно перед confirm,
         * чтобы предложение гарантированно
         * было видно, а не оказалось под ним.
         *
         * Контекст пока не очищаем.
         */
        /*
 * Сначала сохраняем данные предложения
 * в React state.
 *
 * После этого закрываем большое окно ВДР.
 * Отдельный управляемый Modal останется
 * в дереве компонента и точно отобразится.
 */
setAttachInvoiceProposal({
    registryId:
        registrySnapshot.id,

    registryType:
        registrySnapshot.type,

    registryNumber:
        registrySnapshot.number,

    registryTitle:
        registrySnapshot.title,

    registryTypeTitle,

    invoiceIds,

    invoices:
        suitableCreatedInvoices,

    totalAmount
});

setTaskModalOpen(false);
    } catch (error) {
        console.error(
            'Ошибка сохранения ВДР:',
            {
                message:
                    error.message,

                status:
                    error.response?.status,

                data:
                    error.response?.data
            }
        );

        if (
            error.response?.status === 409 &&
            error.response?.data?.code ===
                'INVOICES_LINKED_TO_REGISTRIES'
        ) {
            const blocked =
                error.response.data
                    .blocked_deletions || [];

            Modal.error({
                title: 'Нельзя удалить строки ВДР',
                width: 600,
                zIndex: 2100,
                content: (
                    <div>
                        <p style={{ marginBottom: 12 }}>
                            {error.response?.data
                                ?.message ||
                                error.response?.data
                                    ?.error ||
                                'Строки уже включены в заказы или акты'}
                        </p>
                        {blocked.map(item => (
                            <div
                                key={item.invoice_id}
                                style={{
                                    marginBottom: 10
                                }}
                            >
                                <b>
                                    ID {item.invoice_id}:{' '}
                                    {item.rate_name}
                                </b>
                                <div>
                                    Документы:{' '}
                                    {(item.registries || [])
                                        .map(
                                            registry =>
                                                registry.number ||
                                                `ID ${registry.id}`
                                        )
                                        .join(', ') ||
                                        '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            });

            try {
                const token =
                    localStorage.getItem('token');
                const refreshed = await axios.get(
                    `/api/tasks/${activeTaskDbId}/invoices`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                setTaskInvoices({
                    income:
                        refreshed.data?.income || [],
                    expense:
                        refreshed.data?.expense || []
                });
                setHasUnsavedChanges(false);
            } catch (_) {
                /* ignore restore errors */
            }

            return;
        }

        message.error(
            error.response?.data?.message ||
            error.response?.data?.error ||
            'Ошибка сохранения ВДР'
        );
    }
};

   const groupedRegistryItems =
    useMemo(() => {
        if (
            !selectedRegistry?.items ||
            selectedRegistry.items
                .length === 0
        ) {
            return [];
        }

        const groups = {};

        selectedRegistry.items.forEach(
            item => {
                const invoice =
                    item.invoice;

                const originalTask =
                    invoice?.task;

                const taskId =
                    Number(
                        invoice?.task_id
                    ) ||
                    Number(
                        originalTask?.id
                    ) ||
                    null;

                const externalId =
                    originalTask
                        ?.external_id;

                const fullTask =
                    enrichedTasks[
                        String(taskId)
                    ] ||
                    enrichedTasks[
                        String(
                            externalId
                        )
                    ] ||
                    originalTask ||
                    {};

                const groupKey =
                    taskId ||
                    externalId ||
                    'unknown';

                if (!groups[groupKey]) {
                    const stats =
                        taskDistributionStats[
                            taskId
                        ] || {};

                    groups[
                        groupKey
                    ] = {
                        task:
                            fullTask,

                        dbId:
                            taskId,

                        extId:
                            externalId ||
                            fullTask
                                .external_id ||
                            '?',

                        items:
                            [],

                        total:
                            0,

                        incomeRows:
                            Number(
                                stats
                                    .incomeRows
                            ) || 0,

                        expenseRows:
                            Number(
                                stats
                                    .expenseRows
                            ) || 0,

                        distributedIncomeRows:
                            Number(
                                stats
                                    .distributedIncomeRows
                            ) || 0,

                        distributedExpenseRows:
                            Number(
                                stats
                                    .distributedExpenseRows
                            ) || 0,

                        currentDocumentIncomeRows:
                            0,

                        currentDocumentExpenseRows:
                            0
                    };
                }

                groups[
                    groupKey
                ].items.push(
                    item
                );

                groups[
                    groupKey
                ].total +=
                    Number(
                        invoice?.amount
                    ) || 0;

                if (
                    invoice?.type ===
                    'income'
                ) {
                    groups[
                        groupKey
                    ]
                        .currentDocumentIncomeRows +=
                        1;
                }

                if (
                    invoice?.type ===
                    'expense'
                ) {
                    groups[
                        groupKey
                    ]
                        .currentDocumentExpenseRows +=
                        1;
                }
            }
        );

        return Object.values(
            groups
        );
    }, [
        selectedRegistry,
        enrichedTasks,
        taskDistributionStats
    ]);



    // --- ЛОГИКА УПРАВЛЕНИЯ СТРОКАМИ ВДР ЗАДАЧИ ---

    // --- ЛОГИКА ИНЛАЙН РЕДАКТИРОВАНИЯ ВДР ЗАДАЧИ ---

 const handleAddNewTaskInvoice = type => {
    if (editingInvoice) {
        message.warning(
            'Завершите редактирование текущей строки'
        );
        return;
    }

    const tempId =
        `new-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;

    const emptyRow = {
        id: tempId,
        client_temp_id: tempId,

        type,

        quantity: 1,
        percent: 100,
        penalty: 0,
        include: true,

        price: 0,
        amount: 0,

        rate_name: '',
        unit: '',
        counterparty: null,
        article: null
    };

    /*
     * Запоминаем временный ID только тогда,
     * когда строку потенциально можно добавить
     * в открытый заказ.
     */
    const expectedType =
        taskModalRegistryContext?.type ===
        'customer_order'
            ? 'income'
            : taskModalRegistryContext?.type ===
                'supplier_order'
                ? 'expense'
                : null;

    if (
        taskModalRegistryContext?.id &&
        type === expectedType
    ) {
        pendingOrderInvoiceTempIdsRef
            .current
            .add(tempId);

        console.log(
            '[PENDING ORDER INVOICE ADDED]',
            {
                tempId,
                type,
                registry:
                    taskModalRegistryContext
            }
        );
    }

    setTaskInvoices(prev => ({
        ...prev,

        [type]: [
            emptyRow,
            ...(prev[type] || [])
        ]
    }));

    setHasUnsavedChanges(true);

    startEditingInvoice(
        emptyRow,
        type
    );
};

const TaskDistributionInfo = ({
    group
}) => {
    const incomeTotal =
        Number(
            group.incomeRows
        ) || 0;

    const incomeDistributed =
        Number(
            group.distributedIncomeRows
        ) || 0;

    const expenseTotal =
        Number(
            group.expenseRows
        ) || 0;

    const expenseDistributed =
        Number(
            group.distributedExpenseRows
        ) || 0;

    const incomePercent =
        incomeTotal > 0
            ? Math.min(
                100,
                Math.round(
                    (
                        incomeDistributed /
                        incomeTotal
                    ) *
                    100
                )
            )
            : 0;

    const expensePercent =
        expenseTotal > 0
            ? Math.min(
                100,
                Math.round(
                    (
                        expenseDistributed /
                        expenseTotal
                    ) *
                    100
                )
            )
            : 0;

    return (
        <Space
            size={6}
            wrap
        >
            <Tooltip
                title={
                    <div>
                        <div>
                            Доходных строк ВДР:{' '}
                            <b>
                                {incomeTotal}
                            </b>
                        </div>

                        <div>
                            Распределено:{' '}
                            <b>
                                {
                                    incomeDistributed
                                }
                            </b>
                        </div>

                        <div>
                            Осталось:{' '}
                            <b>
                                {Math.max(
                                    0,
                                    incomeTotal -
                                    incomeDistributed
                                )}
                            </b>
                        </div>

                        <div>
                            В этом документе:{' '}
                            <b>
                                {
                                    group
                                        .currentDocumentIncomeRows
                                }
                            </b>
                        </div>
                    </div>
                }
            >
                <Tag
                    color={
                        incomeTotal > 0 &&
                        incomeDistributed >=
                            incomeTotal
                            ? 'green'
                            : incomeDistributed >
                                0
                                ? 'blue'
                                : 'default'
                    }
                    style={{
                        margin: 0
                    }}
                >
                    Доходы:{' '}
                    {incomeDistributed}/
                    {incomeTotal}{' '}
                    ({incomePercent}%)
                </Tag>
            </Tooltip>

            <Tooltip
                title={
                    <div>
                        <div>
                            Расходных строк ВДР:{' '}
                            <b>
                                {expenseTotal}
                            </b>
                        </div>

                        <div>
                            Распределено:{' '}
                            <b>
                                {
                                    expenseDistributed
                                }
                            </b>
                        </div>

                        <div>
                            Осталось:{' '}
                            <b>
                                {Math.max(
                                    0,
                                    expenseTotal -
                                    expenseDistributed
                                )}
                            </b>
                        </div>

                        <div>
                            В этом документе:{' '}
                            <b>
                                {
                                    group
                                        .currentDocumentExpenseRows
                                }
                            </b>
                        </div>
                    </div>
                }
            >
                <Tag
                    color={
                        expenseTotal > 0 &&
                        expenseDistributed >=
                            expenseTotal
                            ? 'green'
                            : expenseDistributed >
                                0
                                ? 'orange'
                                : 'default'
                    }
                    style={{
                        margin: 0
                    }}
                >
                    Расходы:{' '}
                    {expenseDistributed}/
                    {expenseTotal}{' '}
                    ({expensePercent}%)
                </Tag>
            </Tooltip>
        </Space>
    );
};

const getExpectedInvoiceTypeForRegistry =
    registryType => {
        if (
            registryType ===
            'customer_order'
        ) {
            return 'income';
        }

        if (
            registryType ===
            'supplier_order'
        ) {
            return 'expense';
        }

        return null;
    };

    const attachCreatedInvoicesToRegistry =
    async ({
        registryId,
        invoiceIds
    }) => {
        if (
            !registryId ||
            !Array.isArray(invoiceIds) ||
            invoiceIds.length === 0
        ) {
            return;
        }

        const token =
            localStorage.getItem('token');

        await axios.post(
            `/api/registries/${registryId}/add-items`,
            {
                invoiceIds,
                history_filters: {
                    'Источник': 'Новые строки ВДР, добавленные из карточки документа'
                }
            },
            {
                headers: {
                    Authorization:
                        `Bearer ${token}`
                }
            }
        );

        await refreshRegistryView(
            registryId,
            {
                refreshList: true,
                refreshActs: true,
                refreshHistory: true
            }
        );
    };

    const handleCopyTaskInvoice = (type, record) => {
        const newId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newRow = { ...record, id: newId };
        setTaskInvoices(prev => ({ ...prev, [type]: [newRow, ...prev[type]] }));
        setHasUnsavedChanges(true);
        message.success('Строка скопирована');
    };

    const startEditingInvoice = (inv, type) => {
        if (editingInvoice) return message.warning('Завершите редактирование текущей строки');
        setEditingInvoice({ id: inv.id, type });

        // Важно: берем текстовые значения из строки ВДР
        const rawCpName = inv.counterparty;
        const rawArtName = inv.article;

        // Находим объекты в справочниках
        const cp = _getCounterpartyOptionByName(rawCpName, counterparties);
        const art = _getArticleOptionByName(cp, rawArtName, counterparties, financialArticles);
        const rate = (cp && art) ? _getNomenclatureOptionByDetails(
            cp, art, inv.rate_name, inv.unit, inv.price,
            counterparties, nomenclatures, priceTypesList
        ) : null;

        editInvoiceForm.setFieldsValue({
            ...inv,
            counterparty: cp,
            article: art,
            selectedRateOption: rate,
            quantity: inv.quantity || 1,
            percent: parsePercent(inv.percent, 100),
            penalty: inv.penalty || 0,
            note: inv.note || ''
        });
    };

    const saveInlineEditedInvoice = async () => {
        try {
            const vals = await editInvoiceForm.validateFields();

            // Вытаскиваем текстовые названия из объектов Select
            const cpName = vals.counterparty?.label || vals.counterparty;
            const artName = vals.article?.label || vals.article;
            const finalRateName = vals.selectedRateOption?.name || vals.rate_name || editingInvoice.rate_name;

           const price =
    Number(vals.price) || 0;

const quantity =
    Number(vals.quantity) || 0;

const percent =
    vals.percent === undefined ||
    vals.percent === null
        ? 100
        : Number(vals.percent) || 0;

const penalty =
    Number(vals.penalty) || 0;

const baseAmount =
    price *
    quantity *
    (percent / 100);

const amountAfterPenalty =
    mathRound(
        baseAmount *
        (
            1 -
            penalty / 100
        )
    );
            setTaskInvoices(prev => ({
                ...prev,
                [editingInvoice.type]: prev[editingInvoice.type].map(i => i.id === editingInvoice.id ? {
                    ...i,
                    counterparty: cpName,
                    article: artName,
                    rate_name: finalRateName,
                    unit: vals.unit,
                    price: vals.price,
                    quantity: vals.quantity,
                    percent: vals.percent,
                    note: vals.note,
                    penalty: vals.penalty,
                    include: vals.include !== undefined ? vals.include : i.include,
                    amount: amountAfterPenalty
                } : i)
            }));
            setHasUnsavedChanges(true);
            setEditingInvoice(null);
            editInvoiceForm.resetFields();
        } catch (e) {
            if (e?.errorFields) {
                message.error(
                    'Проверьте заполнение обязательных полей'
                );
                return;
            }
            console.error(
                'Ошибка сохранения строки ВДР:',
                e
            );
            message.error(
                e?.response?.data?.message ||
                    e?.response?.data?.error ||
                    e?.message ||
                    'Ошибка сохранения строки ВДР'
            );
        }
    };

    const deleteTaskInvoice = (
    type,
    id
) => {
    const invoice =
        taskInvoices[type]?.find(
            item =>
                String(item.id) === String(id)
        );

    if (isInvoiceLocked(invoice)) {
        const docs = invoiceLockLabel(invoice);
        Modal.warning({
            title: 'Нельзя удалить строку ВДР',
            zIndex: 2100,
            content: docs
                ? `Строка уже включена в документ: ${docs}`
                : 'Строка уже включена в заказ или акт'
        });
        return;
    }

    if (
        String(id).startsWith('new')
    ) {
        pendingOrderInvoiceTempIdsRef
            .current
            .delete(String(id));
    }

    setTaskInvoices(prev => ({
        ...prev,

        [type]:
            prev[type].filter(
                item =>
                    item.id !== id
            )
    }));

    setHasUnsavedChanges(true);

    if (
        editingInvoice?.id === id
    ) {
        setEditingInvoice(null);
        editInvoiceForm.resetFields();
    }
};

    const cancelEditing = (
    type,
    id
) => {
    if (
        String(id).startsWith('new')
    ) {
        pendingOrderInvoiceTempIdsRef
            .current
            .delete(String(id));

        deleteTaskInvoice(
            type,
            id
        );
    }

    setEditingInvoice(null);
    editInvoiceForm.resetFields();
};

    const closeFullTaskModal = () => {
    setTaskModalOpen(false);

    setTaskModalRegistryContext(
        null
    );

    setActiveTaskDbId(null);
    setActiveTaskExtId(null);

    setTaskData(null);

    setTaskInvoices({
        income: [],
        expense: []
    });

    setTaskComments('');
    setEditingInvoice(null);
    setHasUnsavedChanges(false);

    editInvoiceForm.resetFields();
};

const isSelectedRegistryAct =
    selectedRegistry?.type ===
        'customer_act' ||
    selectedRegistry?.type ===
        'supplier_act';

    const getTaskInvoiceColumns = useCallback((form, type) => {
        const commonStyle = { width: '100%' };
        return [
            {
                title: 'Действия',
                width: 110,
                fixed: 'left',
                render: (_, r) => {
                    const isEdit = editingInvoice?.id === r.id;
                    return isEdit ? (
                        <Space size="small">
                            <Button size="small" icon={<SaveOutlined />} type="primary" onClick={saveInlineEditedInvoice} />
                            <Button size="small" icon={<CloseOutlined />} onClick={() => cancelEditing(type, r.id)} />
                        </Space>
                    ) : (
                        <Space size="small">
                            <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyTaskInvoice(type, r)} disabled={!!editingInvoice} />
                            <Button size="small" icon={<EditOutlined />} onClick={() => startEditingInvoice(r, type)} disabled={!!editingInvoice} />
                            {isInvoiceLocked(r) ? (
                                <Tooltip
                                    title={`В документе: ${invoiceLockLabel(r) || 'заказ/акт'}`}
                                >
                                    <Button
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        danger
                                        disabled
                                    />
                                </Tooltip>
                            ) : (
                                <Popconfirm
                                    title="Удалить?"
                                    onConfirm={() =>
                                        deleteTaskInvoice(
                                            type,
                                            r.id
                                        )
                                    }
                                >
                                    <Button
                                        size="small"
                                        icon={
                                            <DeleteOutlined />
                                        }
                                        danger
                                        disabled={
                                            !!editingInvoice
                                        }
                                    />
                                </Popconfirm>
                            )}
                        </Space>
                    );
                }
            },
            {
                title: 'Контрагент',
                width: 180,
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    <Form.Item name="counterparty" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                        <Select
    showSearch
    allowClear
    labelInValue

    placeholder="Выберите контрагента"

    options={counterpartyOptions}

    /*
     * Ищем по русскому названию,
     * а не по числовому value.
     */
    optionFilterProp="label"

    filterOption={(input, option) =>
        String(option?.label || '')
            .toLowerCase()
            .includes(
                String(input || '')
                    .trim()
                    .toLowerCase()
            )
    }

    onChange={counterparty => {
        form.setFieldsValue({
            counterparty:
                counterparty || null,

            /*
             * При смене контрагента
             * очищаем зависимые поля.
             */
            article: null,
            selectedRateOption: null,
            rate_name: '',
            unit: '',
            price: 0
        });
    }}
/>
                    </Form.Item>
                ) : (r.counterparty)
            },
            {
                title: 'Статья',
                width: 160,
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    <Form.Item noStyle shouldUpdate={(p, c) => p.counterparty !== c.counterparty}>
                        {({ getFieldValue }) => <FixedArticleSelectCell formInstance={form} memoizedGetArticleOptions={memoizedGetArticleOptions} currentCounterparty={getFieldValue('counterparty')} />}
                    </Form.Item>
                ) : r.article
            },
            {
                title: 'Наименование',
                width: 250,
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    <Form.Item noStyle shouldUpdate={(p, c) => p.article !== c.article}>
                        {({ getFieldValue }) => <FixedNomenclatureSelectCell formInstance={form} memoizedGetNomenclatureOptions={memoizedGetNomenclatureOptions} currentCounterparty={getFieldValue('counterparty')} currentArticle={getFieldValue('article')} />}
                    </Form.Item>
                ) : r.rate_name
            },
            {
    title: 'Цена',
    width: 120,
    align: 'right',

    render: (_, row) => {
        const isEdit =
            editingInvoice?.id === row.id;

        if (!isEdit) {
            return `${Number(
                row.price || 0
            ).toLocaleString('ru-RU')} р.`;
        }

        return (
            <Form.Item
                name="price"
                style={{
                    marginBottom: 0
                }}
            >
                <InputNumber
                    min={0}
                    step={0.01}
                    decimalSeparator=","
                    style={{
                        width: '100%',
                        backgroundColor:
                            '#f5f5f5'
                    }}
                    readOnly
                    formatter={value =>
                        Number(
                            value || 0
                        ).toLocaleString(
                            'ru-RU',
                            {
                                minimumFractionDigits:
                                    2,
                                maximumFractionDigits:
                                    2
                            }
                        )
                    }
                />
            </Form.Item>
        );
    }
},
            {
                title: 'Кол-во', width: 70, align: 'center',
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    <Form.Item name="quantity" style={{ marginBottom: 0 }}><InputNumber style={commonStyle} /></Form.Item>
                ) : r.quantity
            },
            {
                title: '%', width: 90, align: 'center',
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    <Form.Item name="percent" style={{ marginBottom: 0 }}>
                        <InputNumber {...percentInputNumberProps} style={commonStyle} />
                    </Form.Item>
                ) : `${formatPercent(r.percent)}%`
            },
            {
                title: 'Штраф %', width: 80, align: 'center',
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    <Form.Item name="penalty" style={{ marginBottom: 0 }}><InputNumber min={0} max={100} style={commonStyle} formatter={v => `${v}%`} parser={v => v.replace('%', '')} /></Form.Item>
                ) : r.penalty > 0 ? <Tag color="error">-{r.penalty}%</Tag> : '-'
            },
            {
    title: 'Сумма',
    width: 140,
    align: 'right',

    render: (_, r) => {
        const isEdit =
            editingInvoice?.id === r.id;

        if (!isEdit) {
            return (
                <b>
                    {Number(
                        r.amount || 0
                    ).toLocaleString(
                        'ru-RU',
                        {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        }
                    )}{' '}
                    р.
                </b>
            );
        }

        return (
            <Form.Item
                noStyle
                shouldUpdate={(
                    previousValues,
                    currentValues
                ) =>
                    previousValues.price !==
                        currentValues.price ||
                    previousValues.quantity !==
                        currentValues.quantity ||
                    previousValues.percent !==
                        currentValues.percent ||
                    previousValues.penalty !==
                        currentValues.penalty
                }
            >
                {({ getFieldValue }) => {
                    const price =
                        Number(
                            getFieldValue('price')
                        ) || 0;

                    const quantity =
                        Number(
                            getFieldValue(
                                'quantity'
                            )
                        ) || 0;

                    const percentValue =
                        getFieldValue('percent');

                    const percent =
                        percentValue ===
                            undefined ||
                        percentValue === null
                            ? 100
                            : Number(
                                percentValue
                            ) || 0;

                    const penalty =
                        Number(
                            getFieldValue(
                                'penalty'
                            )
                        ) || 0;

                    const baseAmount =
                        price *
                        quantity *
                        (percent / 100);

                    const finalAmount =
                        mathRound(
                            baseAmount *
                            (
                                1 -
                                penalty / 100
                            )
                        );

                    return (
                        <InputNumber
                            value={finalAmount}
                            readOnly
                            controls={false}
                            style={{
                                ...commonStyle,
                                backgroundColor:
                                    '#f5f5f5',
                                fontWeight: 700
                            }}
                            formatter={value =>
                                `${Number(
                                    value || 0
                                ).toLocaleString(
                                    'ru-RU',
                                    {
                                        minimumFractionDigits:
                                            2,
                                        maximumFractionDigits:
                                            2
                                    }
                                )} р.`
                            }
                        />
                    );
                }}
            </Form.Item>
        );
    }
},
            {
                title: 'Примечание',
                width: 180,
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    <Form.Item name="note" style={{ marginBottom: 0 }}>
                        <TextArea autoSize={{ minRows: 1, maxRows: 4 }} style={commonStyle} placeholder="Примечание" />
                    </Form.Item>
                ) : (
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.note || ''}</span>
                )
            },
            {
                title: 'Учит.', width: 60, align: 'center',
                render: (_, r) => <Switch checked={r.include} disabled={!!editingInvoice} onChange={(val) => {
                    setTaskInvoices(prev => ({ ...prev, [type]: prev[type].map(i => i.id === r.id ? { ...i, include: val } : i) }));
                    setHasUnsavedChanges(true);
                }} />
            }
        ];
    }, [editingInvoice, counterpartyOptions, counterparties, nomenclatures, financialArticles]);

    const CalculatedAmountDisplay = React.memo(({ formInstance, commonInputStyle }) => {
        const price = Form.useWatch('price', formInstance) || 0;
        const quantity = Form.useWatch('quantity', formInstance) || 0;
        const percent = Form.useWatch('percent', formInstance) || 0;
        const penaltyPercent = Form.useWatch('penalty', formInstance) || 0;

        const baseAmount = price * quantity * (percent / 100);
        const finalAmount = mathRound(baseAmount * (1 - penaltyPercent / 100));

        return (
            <InputNumber
                value={finalAmount}
                readOnly
                style={{ ...commonInputStyle, backgroundColor: '#f0f0f0', fontWeight: 'bold' }}
                formatter={v => `${parseFloat(v).toLocaleString()} р.`}
            />
        );
    });

    const fetchRegistriesWithFilters = async (filters = {}) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/registries', {
                params: {
                    type: 'daily_report', // или другой тип
                    ...filters
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setRegistries(res.data);
        } catch (e) {
            message.error("Ошибка загрузки");
        } finally {
            setLoading(false);
        }
    };

    const applyMainRegistrySearch =
        async (
            nextSearch = mainSearch
        ) => {
            const dateFrom =
                nextSearch.dateRange?.[0]
                    ? nextSearch
                        .dateRange[0]
                        .format('YYYY-MM-DD')
                    : '';

            const dateTo =
                nextSearch.dateRange?.[1]
                    ? nextSearch
                        .dateRange[1]
                        .format('YYYY-MM-DD')
                    : '';

            await fetchRegistries({
                /*
                * Сохраняем фильтры колонок,
                * чтобы верхний поиск с ними
                * комбинировался.
                */
                ...registryTableFilters,

                search:
                    String(
                        nextSearch.text || ''
                    ).trim() ||
                    undefined,

                status:
                    nextSearch.status ||
                    registryTableFilters.status ||
                    undefined,

                date_from:
                    dateFrom ||
                    undefined,

                date_to:
                    dateTo ||
                    undefined,

                closed_status:
                    nextSearch.closedStatus ||
                    undefined,

                page: 1,

                limit:
                    registriesPagination.pageSize
            });
        };

    const getColumnSearchProps = (
        dataIndex,
        placeholder
    ) => ({
        key: dataIndex,

        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters
        }) => (
            <div
                style={{ padding: 8 }}
                onKeyDown={event =>
                    event.stopPropagation()
                }
            >
                <Input
                    autoFocus
                    allowClear

                    placeholder={
                        `Поиск: ${placeholder}`
                    }

                    value={
                        selectedKeys?.[0] || ''
                    }

                    onChange={event => {
                        const value =
                            event.target.value;

                        setSelectedKeys(
                            value
                                ? [value]
                                : []
                        );
                    }}

                    onPressEnter={() => {
                        confirm({
                            closeDropdown: true
                        });
                    }}

                    style={{
                        width: 240,
                        marginBottom: 8,
                        display: 'block'
                    }}
                />

                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() => {
                            confirm({
                                closeDropdown: true
                            });
                        }}
                    >
                        Найти
                    </Button>

                    <Button
                        size="small"
                        onClick={() => {
                            clearFilters?.();

                            confirm({
                                closeDropdown: true
                            });
                        }}
                    >
                        Сброс
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: filtered => (
            <SearchOutlined
                style={{
                    color: filtered
                        ? '#1677ff'
                        : undefined
                }}
            />
        )
    });

    const handleTableChange = (
        pagination,
        filters,
        sorter
    ) => {
        const nextFilters = {
            type:
                filters.type?.length
                    ? filters.type
                    : [
                        'customer_order',
                        'supplier_order'
                    ],

            id:
                filters.id?.[0] || '',

            number:
                filters.number?.[0] || '',

            title:
                filters.title?.[0] || '',

            counterparty_name:
                filters.counterparty_name?.[0] ||
                '',

            status:
                filters.status?.[0] || '',

            date:
                filters.date?.[0] || '',

            user_fio:
                filters.user_fio?.[0] || ''
        };

        setRegistryTableFilters(
            nextFilters
        );

        const dateFrom =
            mainSearch.dateRange?.[0]
                ? mainSearch
                    .dateRange[0]
                    .format('YYYY-MM-DD')
                : '';

        const dateTo =
            mainSearch.dateRange?.[1]
                ? mainSearch
                    .dateRange[1]
                    .format('YYYY-MM-DD')
                : '';

        fetchRegistries({
            ...nextFilters,

            search:
                mainSearch.text?.trim() ||
                undefined,

            /*
            * Верхний статус имеет
            * приоритет над колонкой.
            */
            status:
                mainSearch.status ||
                nextFilters.status ||
                undefined,

            date_from:
                dateFrom ||
                undefined,

            date_to:
                dateTo ||
                undefined,

            closed_status:
                mainSearch.closedStatus ||
                undefined,

            page:
                pagination.current || 1,

            limit:
                pagination.pageSize ||
                registriesPagination.pageSize
        });
    };

    const filterHints = useMemo(() => {
        const hints = {
            numbers: new Set(),
            titles: new Set(),
            counterparties: new Set(),
            creators: new Set()
        };
        registries.forEach(r => {
            if (r.number) hints.numbers.add(r.number);
            if (r.title) hints.titles.add(r.title);
            if (r.counterparty_name) hints.counterparties.add(r.counterparty_name);
            if (r.user?.fio) hints.creators.add(r.user.fio);
        });
        return {
            numbers: Array.from(hints.numbers).map(v => ({ value: v, label: v })),
            titles: Array.from(hints.titles).map(v => ({ value: v, label: v })),
            counterparties: Array.from(hints.counterparties).map(v => ({ value: v, label: v })),
            creators: Array.from(hints.creators).map(v => ({ value: v, label: v }))
        };
    }, [registries]);

    // Фильтр с подсказками (Select)
    const getColumnHintProps = (
        dataIndex,
        options
    ) => ({
        key: dataIndex,

        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters
        }) => (
            <div
                style={{
                    padding: 8
                }}
                onKeyDown={event =>
                    event.stopPropagation()
                }
            >
                <Select
                    showSearch
                    allowClear
                    placeholder="Поиск..."
                    value={
                        selectedKeys?.[0] ||
                        undefined
                    }
                    style={{
                        width: 220,
                        display: 'block',
                        marginBottom: 8
                    }}
                    options={options}
                    filterOption={(
                        input,
                        option
                    ) =>
                        String(
                            option?.label || ''
                        )
                            .toLowerCase()
                            .includes(
                                input.toLowerCase()
                            )
                    }
                    onChange={value => {
                        setSelectedKeys(
                            value ? [value] : []
                        );
                    }}
                    onSearch={value => {
                        setSelectedKeys(
                            value ? [value] : []
                        );
                    }}
                    onClear={() => {
                        setSelectedKeys([]);
                    }}
                    onKeyDown={event => {
                        if (
                            event.key === 'Enter'
                        ) {
                            confirm();
                        }
                    }}
                />

                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() =>
                            confirm({
                                closeDropdown: true
                            })
                        }
                    >
                        Найти
                    </Button>

                    <Button
                        size="small"
                        onClick={() => {
                            clearFilters?.();

                            confirm({
                                closeDropdown: true
                            });
                        }}
                    >
                        Сброс
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: filtered => (
            <SearchOutlined
                style={{
                    color: filtered
                        ? '#1677ff'
                        : undefined
                }}
            />
        )
    });

    // Фильтр с датой
    const getColumnDateProps = () => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <DatePicker
                    value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
                    onChange={date => setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : [])}
                    style={{ marginBottom: 8, display: 'block' }}
                    format="DD.MM.YYYY"
                />
                <Space>
                    <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>ОК</Button>
                    <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Сброс</Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    });

    const fetchActsForOrder = async (orderId) => {
        /*
        * Проверяем наличие ключа, а не значение.
        * Пустой массив тоже означает, что запрос уже выполнялся.
        */
        if (
            Object.prototype.hasOwnProperty.call(
                expandedActs,
                orderId
            )
        ) {
            return;
        }

        setExpandingLoading(prev => ({
            ...prev,
            [orderId]: true
        }));

        try {
            const token = localStorage.getItem('token');

            const res = await axios.get('/api/registries', {
                params: {
                    parent_id: orderId,
                    page: 1,
                    limit: 200
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const acts = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.data)
                    ? res.data.data
                    : [];

            setExpandedActs(prev => ({
                ...prev,
                [orderId]: acts
            }));
        } catch (e) {
            console.error(
                `Ошибка загрузки актов заказа ${orderId}:`,
                e
            );

            /*
            * Сохраняем пустой массив, чтобы Table
            * гарантированно получил корректный dataSource.
            */
            setExpandedActs(prev => ({
                ...prev,
                [orderId]: []
            }));

            message.error(
                e.response?.data?.error ||
                'Не удалось загрузить связанные акты'
            );
        } finally {
            setExpandingLoading(prev => ({
                ...prev,
                [orderId]: false
            }));
        }
    };

    const fetchUnlinkedActs = async () => {
        setUnlinkedLoading(true);

        try {
            const token = localStorage.getItem('token');

            const res = await axios.get('/api/registries', {
                params: {
                    type: [
                        'customer_act',
                        'supplier_act'
                    ],
                    page: 1,
                    limit: 200
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const acts = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.data)
                    ? res.data.data
                    : [];

            setUnlinkedActs(
                acts.filter(act => !act.parent_id)
            );
        } catch (e) {
            console.error(
                'Ошибка загрузки непривязанных актов:',
                e
            );

            setUnlinkedActs([]);

            message.error(
                e.response?.data?.error ||
                'Не удалось загрузить непривязанные акты'
            );
        } finally {
            setUnlinkedLoading(false);
        }
    };

    const handleSaveMetadata = async () => {
        try {
            const { period, ...vals } = await headerForm.validateFields();
            const token = localStorage.getItem('token');
            // Роут /api/registries/:id/metadata должен быть создан на бэкенде
            await axios.put(`/api/registries/${selectedRegistry.id}/metadata`, { ...vals, ...periodToPayload(period) }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            message.success('Реквизиты документа обновлены');
            setIsHeaderEdit(false);
            await refreshRegistryView(
                selectedRegistry.id,
                {
                    refreshList: true,
                    refreshActs: false,
                    refreshHistory: true
                }
            );
            // Обновляем данные в текущем окне и общем списке
            const res = await axios.get(`/api/registries/${selectedRegistry.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            setSelectedRegistry(res.data);
            fetchRegistries();
        } catch (e) {
            message.error('Ошибка при сохранении реквизитов');
        }
    };

    const handleDocTypeChange = async (newType) => {
        // Если мы на втором шаге и выбрали задачи, нужно обновить список строк ВДР
        if (selectedTaskKeys.length > 0) {
            setModalLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get('/api/registries/get-invoices-by-tasks', {
                    params: {
                        taskIds: selectedTaskKeys.join(','),
                        docType: newType, // Отправляем новый выбранный тип
                        // Если это режим "на основании", передаем родителя для блокировки
                        parentId: modalForm.getFieldValue('parent_id')
                    },
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setInvoicesSource(res.data);
                // При смене типа лучше сбросить выбранные галочки,
                // так как старые ID инвойсов могут не существовать в новом типе
                setSelectedInvoiceKeys([]);
            } catch (e) {
                message.error('Ошибка при обновлении списка работ');
            } finally {
                setModalLoading(false);
            }
        }
    };

    const allActsFilterHints = useMemo(() => {
        const hints = {
            numbers: new Set(),
            counterparties: new Set()
        };
        allActs.forEach(a => {
            if (a.number) hints.numbers.add(a.number);
            if (a.counterparty_name) hints.counterparties.add(a.counterparty_name);
        });
        return {
            numbers: Array.from(hints.numbers).map(v => ({ value: v, label: v })),
            counterparties: Array.from(hints.counterparties).map(v => ({ value: v, label: v }))
        };
    }, [allActs]);

    // 2. Функция для открытия анализа
    const handleOpenSupplierBasis = async () => {
        setBasisLoading(true);
        setSelectedBasisKeys([]);

        try {
            const token =
                localStorage.getItem('token');

            const res = await axios.get(
                `/api/registries/basis-analysis/${selectedRegistry.id}`,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            const suppliers =
                Array.isArray(res.data)
                    ? res.data
                    : [];

            setBasisSuppliers(suppliers);

            /*
            * Выбираем всех поставщиков,
            * у которых остались свободные строки.
            */
            const readyToCreate =
                suppliers
                    .filter(
                        supplier =>
                            Number(
                                supplier.availableRows
                            ) > 0
                    )
                    .map(
                        supplier =>
                            supplier.key ||
                            supplier.name
                    );

            setSelectedBasisKeys(
                readyToCreate
            );

            setSupplierBasisModal(true);
        } catch (e) {
            console.error(
                'Ошибка анализа основания:',
                e
            );

            message.error(
                e.response?.data?.error ||
                'Ошибка анализа данных'
            );
        } finally {
            setBasisLoading(false);
        }
    };

    // 3. Функция создания
    const confirmCreateSuppliers =
    async createActs => {
        if (
            selectedBasisKeys.length === 0
        ) {
            message.warning(
                'Выберите хотя бы одного поставщика'
            );

            return;
        }

        try {
            const token =
                localStorage.getItem('token');

            const toCreate =
                basisSuppliers
                    .filter(supplier =>
                        selectedBasisKeys.includes(
                            supplier.key ||
                            supplier.name
                        )
                    )
                    .map(supplier => ({
                        name:
                            supplier.name,

                        /*
                         * Отправляем только свободные строки.
                         */
                        availableItems:
                            supplier.availableItems
                    }));

            const res = await axios.post(
                '/api/registries/bulk-suppliers',
                {
                    customerOrderId:
                        selectedRegistry.id,

                    suppliers:
                        toCreate,

                    createActs
                },
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            const createdCount =
                res.data?.results?.length ||
                0;

            const skippedCount =
                res.data?.skipped?.length ||
                0;

            message.success(
                `Создано заказов: ${createdCount}` +
                (
                    skippedCount > 0
                        ? `. Пропущено: ${skippedCount}`
                        : ''
                )
            );

            setSupplierBasisModal(false);
            setSelectedBasisKeys([]);

            fetchRegistries();

            /*
             * Обновляем открытый заказ покупателя,
             * чтобы статистика сразу пересчиталась.
             */
            const refreshed =
                await axios.get(
                    `/api/registries/${selectedRegistry.id}`,
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );

            setSelectedRegistry(
                refreshed.data
            );
        } catch (e) {
            console.error(
                'Ошибка создания заказов:',
                e
            );

            message.error(
                e.response?.data?.error ||
                'Ошибка при создании документов'
            );
        }
    };

    const toNumber = (value) => {
        const number = Number(value);
        return Number.isFinite(number) ? number : 0;
    };

    const calculatePercent = (value, total) => {
        const numericValue = toNumber(value);
        const numericTotal = toNumber(total);

        if (numericTotal <= 0) return 0;

        return Math.min(
            100,
            Math.max(0, Math.round((numericValue / numericTotal) * 100))
        );
    };

    const getProgressColor = (percent) => {
        if (percent < 50) return '#f5222d';
        if (percent < 90) return '#1890ff';
        return '#52c41a';
    };

    const formatMoney = (value) =>
        `${toNumber(value).toLocaleString('ru-RU', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        })} ₽`;

    const ClosingCircle = ({
        percent,
        tooltip,
        label,
        size = 45
    }) => (
        <Tooltip
            title={tooltip}
            placement="top"
            overlayStyle={{ maxWidth: 420 }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 68,
                    cursor: 'help'
                }}
            >
                <Progress
                    type="circle"
                    percent={percent}
                    size={size}
                    strokeWidth={10}
                    strokeColor={getProgressColor(percent)}
                    format={(value) => (
                        <span
                            style={{
                                fontSize: 10,
                                fontWeight: 700
                            }}
                        >
                            {value}%
                        </span>
                    )}
                />

                <Text
                    type="secondary"
                    style={{
                        fontSize: 9,
                        lineHeight: '11px',
                        marginTop: 4,
                        textAlign: 'center',
                        whiteSpace: 'normal'
                    }}
                >
                    {label}
                </Text>
            </div>
        </Tooltip>
    );

    const handleRegistriesTableChange = (
        pagination,
        filters,
        sorter
    ) => {
        const current = pagination?.current || 1;

        const pageSize =
            pagination?.pageSize ||
            registriesPagination.pageSize ||
            50;

        const params = {
            page: current,
            limit: pageSize,

            /*
            * В Ant Design значения фильтров
            * всегда приходят массивами.
            */
            type:
                filters?.type?.length > 0
                    ? filters.type
                    : [
                        'customer_order',
                        'supplier_order'
                    ],

            id:
                filters?.id?.[0] ||
                undefined,

            number:
                filters?.number?.[0] ||
                undefined,

            title:
                filters?.title?.[0] ||
                undefined,

            counterparty_name:
                filters?.counterparty_name?.[0] ||
                undefined,

            user_fio:
                filters?.user_fio?.[0] ||
                undefined,

            status:
                filters?.status?.[0] ||
                undefined,

            date:
                filters?.date?.[0] ||
                undefined
        };

        console.log(
            '[REGISTRY TABLE FILTERS]',
            filters
        );

        console.log(
            '[REGISTRY API PARAMS]',
            params
        );

        setRegistriesPagination({
            current,
            pageSize
        });

        fetchRegistries(params);
    };

    const filteredRegistryItems = useMemo(() => {
        const items = Array.isArray(selectedRegistry?.items)
            ? selectedRegistry.items
            : [];

        const search =
            registryViewSearch
                .trim()
                .toLowerCase();

        return items.filter(item => {
            const invoice = item.invoice || {};
            const task = invoice.task || {};

            const linkedCustomerAct =
                invoice.linked_customer_act || '';

            const linkedSupplierAct =
                invoice.linked_supplier_act || '';

            const isClosed =
                Boolean(linkedCustomerAct) ||
                Boolean(linkedSupplierAct);

            if (
                registryViewClosureFilter === 'closed' &&
                !isClosed
            ) {
                return false;
            }

            if (
                registryViewClosureFilter === 'open' &&
                isClosed
            ) {
                return false;
            }

            if (!search) {
                return true;
            }

            const nomerTt = getTaskVal(
                task,
                'Nomer_TT'
            );

            return matchesIdOrTtSearch(
                registryViewSearch,
                {
                    ids: [
                        item.id,
                        item.invoice_id,
                        task.id,
                        task.external_id,
                        invoice.task_id
                    ],
                    tt: nomerTt
                }
            );
        });
    }, [
        selectedRegistry,
        registryViewSearch,
        registryViewClosureFilter,
        enrichedTasks
    ]);

    const openAddItemsToRegistry = async () => {
        setIsAddingItemsToExistingRegistry(true);
    if (!selectedRegistry) {
        return;
    }

    const token = localStorage.getItem('token');

    const isAct =
        selectedRegistry.type === 'customer_act' ||
        selectedRegistry.type === 'supplier_act';

    setSelectedInvoiceKeys([]);
    setInvoicesSource([]);

    const counterpartyId =
        counterparties.find(
            counterparty =>
                counterparty.name ===
                selectedRegistry.counterparty_name
        )?.id;

    modalForm.setFieldsValue({
        type: selectedRegistry.type,

        counterparty_id:
            counterpartyId,

        /*
         * Для акта родитель уже должен
         * быть установлен.
         */
        parent_id:
            selectedRegistry.parent_id ||
            undefined,

        title:
            selectedRegistry.title,

        number:
            selectedRegistry.number,

        date:
            selectedRegistry.date
                ? dayjs(selectedRegistry.date)
                : dayjs()
    });

    /*
     * Для обычного заказа сохраняем
     * текущую логику с первым шагом.
     */
    if (!isAct) {
        setSelectedTaskKeys([]);
        setCurrentStep(0);
        setIsModalVisible(true);

        return;
    }

    /*
     * Акт обязательно должен иметь
     * родительский заказ.
     */
    const parentOrderId =
        Number(
            selectedRegistry.parent_id
        );

    if (
        !Number.isInteger(parentOrderId) ||
        parentOrderId <= 0
    ) {
        message.error(
            'У акта не указан родительский заказ'
        );

        return;
    }

    setModalLoading(true);
    setIsModalVisible(true);

    try {
        /*
         * Загружаем полный родительский заказ.
         */
        const parentResponse =
            await axios.get(
                `/api/registries/${parentOrderId}`,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

        const parentOrder =
            parentResponse.data;

        const parentItems =
            Array.isArray(parentOrder?.items)
                ? parentOrder.items
                : [];

        /*
         * Берём только задачи, реально
         * присутствующие в родительском заказе.
         */
        const taskIds = [
            ...new Set(
                parentItems
                    .map(item =>
                        Number(
                            item.invoice?.task_id
                        )
                    )
                    .filter(Number.isInteger)
            )
        ];

        if (taskIds.length === 0) {
            message.warning(
                'В родительском заказе нет задач'
            );

            setIsModalVisible(false);

            return;
        }

        setSelectedTaskKeys(
            taskIds
        );

        /*
         * Загружаем строки для акта.
         *
         * parentId нужен, чтобы бэкенд:
         * - показал строки только родительского заказа;
         * - отметил уже включённые в этот или другие акты;
         * - не позволил выбрать лишние строки.
         */
        const invoicesResponse =
            await axios.get(
                '/api/registries/get-invoices-by-tasks',
                {
                    params: {
                        taskIds:
                            taskIds.join(','),

                        docType:
                            selectedRegistry.type,

                        parentId:
                            parentOrderId,

                        /*
                         * Если бэкенд поддерживает,
                         * можно использовать для отличия
                         * текущего редактируемого акта.
                         */
                        currentRegistryId:
                            selectedRegistry.id
                    },

                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

        const loadedInvoices =
            Array.isArray(
                invoicesResponse.data
            )
                ? invoicesResponse.data
                : [];

        setInvoicesSource(
            loadedInvoices
        );

        /*
         * Сразу открываем шаг №2.
         */
        setCurrentStep(1);
    } catch (error) {
        console.error(
            'Ошибка подготовки строк акта:',
            error
        );

        message.error(
            error.response?.data?.error ||
            'Не удалось загрузить строки родительского заказа'
        );

        setIsModalVisible(false);
    } finally {
        setModalLoading(false);
    }
};

    const filteredGroupedRegistryItems = useMemo(() => {
        const groups = Array.isArray(groupedRegistryItems)
            ? groupedRegistryItems
            : [];

        const search = String(registryViewSearch || '')
            .trim()
            .toLowerCase();

        return groups
            .map(group => {
                const filteredItems = (
                    Array.isArray(group.items)
                        ? group.items
                        : []
                ).filter(item => {
                    const invoice = item.invoice || {};
                    const task = invoice.task || group.task || {};

                    const linkedAct =
                        selectedRegistry?.type === 'supplier_order'
                            ? invoice.linked_supplier_act
                            : invoice.linked_customer_act;

                    const isClosed = Boolean(linkedAct);

                    if (
                        registryViewClosureFilter === 'closed' &&
                        !isClosed
                    ) {
                        return false;
                    }

                    if (
                        registryViewClosureFilter === 'open' &&
                        isClosed
                    ) {
                        return false;
                    }

                    if (!search) return true;

                    const taskForTt =
                        group.task || task;

                    const nomerTt = getTaskVal(
                        taskForTt,
                        'Nomer_TT'
                    );

                    return matchesIdOrTtSearch(
                        registryViewSearch,
                        {
                            ids: [
                                item.id,
                                item.invoice_id,
                                group.dbId,
                                group.extId,
                                task.id,
                                task.external_id,
                                invoice.id,
                                invoice.task_id
                            ],
                            tt: nomerTt
                        }
                    );
                });

                return {
                    ...group,
                    items: filteredItems,
                    total: filteredItems.reduce(
                        (sum, item) =>
                            sum +
                            Number(item.invoice?.amount || 0),
                        0
                    )
                };
            })
            .filter(group => {
                if (group.items.length > 0) return true;
                if (!search) return false;

                const nomerTt = getTaskVal(
                    group.task,
                    'Nomer_TT'
                );

                return matchesIdOrTtSearch(
                    registryViewSearch,
                    {
                        ids: [
                            group.dbId,
                            group.extId,
                            group.task?.id,
                            group.task?.external_id
                        ],
                        tt: nomerTt
                    }
                );
            });
    }, [
        groupedRegistryItems,
        registryViewSearch,
        registryViewClosureFilter,
        selectedRegistry?.type,
        enrichedTasks
    ]);

    const filteredRegistryRowsCount = useMemo(
        () =>
            filteredGroupedRegistryItems.reduce(
                (sum, group) =>
                    sum + (group.items?.length || 0),
                0
            ),
        [filteredGroupedRegistryItems]
    );

    const paginatedGroupedRegistryItems =
    useMemo(() => {
        const groups =
            Array.isArray(
                filteredGroupedRegistryItems
            )
                ? filteredGroupedRegistryItems
                : [];

        const start =
            (registryViewPage - 1) *
            registryViewPageSize;

        const end =
            start +
            registryViewPageSize;

        return groups.slice(
            start,
            end
        );
    }, [
        filteredGroupedRegistryItems,
        registryViewPage,
        registryViewPageSize
    ]);

    const getRegistryTypeTitle = type => {
        const titles = {
            customer_order: 'Заказ покупателя',
            customer_act: 'Акт покупателя',
            supplier_order: 'Заказ поставщика',
            supplier_act: 'Акт поставщика'
        };

        return titles[type] || 'Документ';
    };

    const formatRegistryDate = date => {
        if (!date) return 'без даты';

        const parsed = dayjs(date);

        return parsed.isValid()
            ? parsed.format('DD.MM.YYYY')
            : 'без даты';
    };

    const getRegistryItemDistributionState = (
        item,
        registryType
    ) => {
        const invoice = item?.invoice;

        if (!invoice) {
            return {
                unallocated: false,
                reason: ''
            };
        }

        /*
        * Заказ покупателя:
        * строка считается нераспределённой,
        * если по её задаче нет заказа поставщика.
        */
        if (registryType === 'customer_order') {
            const linkedSupplierOrders =
                String(
                    invoice
                        .linked_supplier_order_numbers ||
                    ''
                ).trim();

            return {
                unallocated:
                    linkedSupplierOrders.length === 0,

                reason:
                    'Расходы задачи ещё не распределены в заказ поставщика'
            };
        }

        /*
        * Заказ поставщика:
        * строка считается незакрытой,
        * если она ещё не попала в акт поставщика.
        */
        if (registryType === 'supplier_order') {
            const linkedSupplierActs =
                String(
                    invoice.linked_supplier_act ||
                    ''
                ).trim();

            return {
                unallocated:
                    linkedSupplierActs.length === 0,

                reason:
                    'Строка ещё не включена в акт поставщика'
            };
        }

        return {
            unallocated: false,
            reason: ''
        };
    };

    const refreshRegistryView = useCallback(
        async (
            registryId = selectedRegistry?.id,
            options = {}
        ) => {
            const {
                refreshList = true,
                refreshActs = true,
                refreshHistory = false,
                fromRemoteSync = false,
                silent = false
            } = options;

            if (!registryId) {
                return null;
            }

            const token =
                localStorage.getItem('token');

            const headers = {
                Authorization:
                    `Bearer ${token}`
            };

            try {
                const requests = [
                    axios.get(
                        `/api/registries/${registryId}`,
                        { headers }
                    )
                ];

                if (refreshHistory) {
                    requests.push(
                        axios.get(
                            `/api/registries/${registryId}/history`,
                            { headers }
                        ),

                        axios.get(
                            `/api/registries/${registryId}/comments`,
                            { headers }
                        )
                    );
                }

                const responses =
                    await Promise.all(requests);

                const registryResponse =
                    responses[0];

                setSelectedRegistry(
                    registryResponse.data
                );

                headerForm.setFieldsValue({
                    number:
                        registryResponse.data.number,

                    title:
                        registryResponse.data.title,

                    date:
                        registryResponse.data.date
                            ? dayjs(
                                registryResponse.data.date
                            )
                            : null,

                    period:
                        registryPeriodValue(
                            registryResponse.data
                        )
                });

                if (refreshHistory) {
                    setRegistryHistory(
                        responses[1]?.data || []
                    );

                    setCommentHistory(
                        responses[2]?.data || []
                    );
                }

                if (refreshList) {
                    await fetchRegistries();
                }

                if (refreshActs) {
                    await fetchAllActs();

                    /*
                    * Сбрасываем кеш раскрытых актов,
                    * иначе там останутся старые данные.
                    */
                    setExpandedActs({});
                }

                if (!fromRemoteSync) {
                    broadcastRegistryUpdated(registryId, {
                        updated_at:
                            registryResponse.data
                                ?.updated_at || null,
                        total_amount:
                            registryResponse.data
                                ?.total_amount ?? null
                    });
                }

                return registryResponse.data;
            } catch (error) {
                console.error(
                    'Ошибка обновления окна документа:',
                    error
                );

                if (!silent) {
                    message.error(
                        error.response?.data?.error ||
                        'Не удалось обновить документ'
                    );
                }

                return null;
            }
        },
        [selectedRegistry?.id, broadcastRegistryUpdated]
    );

    useEffect(() => {
        refreshRegistryViewRef.current = refreshRegistryView;
    }, [refreshRegistryView]);

    const removeVdrGroupFromRegistry = async group => {
        const itemIds = (group?.items || [])
            .map(item => item.id)
            .filter(Boolean);

        if (itemIds.length === 0) {
            message.warning(
                'В этой ВДР нет строк для отвязки'
            );
            return;
        }

        const groupKey =
            group?.dbId ||
            group?.extId ||
            itemIds[0];

        setRemovingVdrGroupKey(groupKey);

        try {
            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`
            };

            await Promise.all(
                itemIds.map(itemId =>
                    axios.delete(
                        `/api/registries/items/${itemId}`,
                        { headers }
                    )
                )
            );

            message.success(
                `Строки отвязаны от документа (${itemIds.length})`
            );

            await refreshRegistryView(
                selectedRegistry?.id,
                {
                    refreshList: true,
                    refreshActs: true,
                    refreshHistory: true
                }
            );
        } catch (error) {
            console.error(
                'Ошибка отвязки строк от документа:',
                error
            );

            message.error(
                error.response?.data?.error ||
                    'Ошибка при отвязке строк от документа'
            );
        } finally {
            setRemovingVdrGroupKey(null);
        }
    };

    const getRegistryStatusLabel = status => {
        return (
            REGISTRY_STATUS_OPTIONS.find(
                item => item.value === status
            )?.text ||
            status ||
            'Не указан'
        );
    };

    const registryViewFooterActions = [
    selectedRegistry?.type?.includes('order') &&
    !isHeaderEdit &&
    !isEditMode && (
        <Button
            key="basis"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() =>
                createActOnBasis(
                    selectedRegistry
                )
            }
        >
            Создать Акт на основании
        </Button>
    ),

    selectedRegistry &&
    !isHeaderEdit &&
    !isEditMode && (
        <Button
            key="workflow"
            icon={<SendOutlined />}
            onClick={() => {
                window.location.href =
                    `/doc-workflows?registry_id=${selectedRegistry.id}`;
            }}
        >
            На согласование
        </Button>
    ),

    selectedRegistry?.type === 'customer_order' &&
    !isHeaderEdit &&
    !isEditMode && (
        <Tooltip
            key="create-supplier-tip"
            title={
                Number(selectedRegistry?.total_rows) > 0 &&
                Number(selectedRegistry?.total_rows) ===
                    Number(selectedRegistry?.closed_rows)
                    ? 'Создать заказы поставщикам (и при необходимости акты) по свободным расходным строкам'
                    : 'Можно создать и до полного закрытия заказа покупателя — попадут только свободные строки ВДР'
            }
        >
            <Button
                key="create-supplier"
                icon={<PlusOutlined />}
                onClick={handleOpenSupplierBasis}
                loading={basisLoading}
                style={{
                    background: '#722ed1',
                    color: '#fff'
                }}
            >
                Заказы поставщикам на основании
            </Button>
        </Tooltip>
    ),

    !isHeaderEdit &&
    !isEditMode && (
        <Button
            key="edit-header"
            icon={<EditOutlined />}
            onClick={() =>
                setIsHeaderEdit(true)
            }
        >
            Редактировать реквизиты
        </Button>
    ),

    isHeaderEdit && (
        <Button
            key="cancel-header-edit"
            onClick={() =>
                setIsHeaderEdit(false)
            }
        >
            Отмена
        </Button>
    ),

    isHeaderEdit && (
        <Button
            key="save-header"
            type="primary"
            icon={<SaveOutlined />}
            onClick={
                handleSaveMetadata
            }
        >
            Сохранить реквизиты
        </Button>
    ),

    !isEditMode &&
    !isHeaderEdit && (
        <Button
            key="edit-rows"
            type="primary"
            icon={<EditOutlined />}
            onClick={() =>
                setIsEditMode(true)
            }
        >
            Режим правки состава
        </Button>
    ),

    isEditMode && (
        <Button
            key="finish-row-edit"
            onClick={() =>
                setIsEditMode(false)
            }
        >
            Завершить ред. состава
        </Button>
    ),

    isEditMode && (
        <Button
            key="add"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={
                openAddItemsToRegistry
            }
        >
            Добавить строки
        </Button>
    ),

    !isHeaderEdit && (
        <Button
            key="close"
            onClick={
                closeRegistryView
            }
        >
            Закрыть
        </Button>
    )
].filter(Boolean);

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Title level={2}>Документы</Title>
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => { setIsAddingItemsToExistingRegistry(false);setIsCreateOnBasis(false);setIsEditMode(false); setIsModalVisible(true); setCurrentStep(0); setSelectedTaskKeys([]); setSelectedInvoiceKeys([]); modalForm.resetFields(); }}>Создать отчет</Button>
                </Row>

                <Card style={{ marginBottom: 20 }}>
                    <Row gutter={[16, 16]}>
                        <Col span={5}>
                            <Input.Search
                                allowClear
                                enterButton="Найти"
                                placeholder="Поиск по №, названию или ID"
                                value={mainSearch.text}

                                onChange={event => {
                                    const value =
                                        event.target.value;

                                    const nextSearch = {
                                        ...mainSearch,
                                        text: value
                                    };

                                    setMainSearch(
                                        nextSearch
                                    );

                                    /*
                                    * При очистке сразу
                                    * обновляем таблицу.
                                    */
                                    if (!value) {
                                        applyMainRegistrySearch(
                                            nextSearch
                                        );
                                    }
                                }}

                                onSearch={() => {
                                    applyMainRegistrySearch();
                                }}
                            />
                        </Col>

                        <Col span={5}>
                            <RangePicker
                                style={{
                                    width: '100%'
                                }}

                                value={
                                    mainSearch.dateRange
                                }

                                onChange={value => {
                                    const nextSearch = {
                                        ...mainSearch,
                                        dateRange: value
                                    };

                                    setMainSearch(
                                        nextSearch
                                    );

                                    applyMainRegistrySearch(
                                        nextSearch
                                    );
                                }}
                            />
                        </Col>

                        <Col span={5}>
                            <Select
                                placeholder="Статус"
                                style={{
                                    width: '100%'
                                }}
                                allowClear
                                value={
                                    mainSearch.status
                                }

                                options={[
                                    {
                                        value: 'draft',
                                        label: 'Черновик'
                                    },
                                    {
                                        value: 'approval',
                                        label:
                                            'На согласовании'
                                    },
                                    {
                                        value: 'approved',
                                        label: 'Утверждён'
                                    },
                                    {
                                        value: 'rejected',
                                        label: 'Отклонён'
                                    },
                                    {
                                        value: 'paid',
                                        label: 'Оплачен'
                                    }
                                ]}

                                onChange={value => {
                                    const nextSearch = {
                                        ...mainSearch,
                                        status: value
                                    };

                                    setMainSearch(
                                        nextSearch
                                    );

                                    applyMainRegistrySearch(
                                        nextSearch
                                    );
                                }}
                            />
                        </Col>

                        <Col span={5}>
                            <Select
                                placeholder="Закрытие (акты)"
                                style={{
                                    width: '100%'
                                }}
                                allowClear
                                value={
                                    mainSearch.closedStatus
                                }

                                options={[
                                    {
                                        value: 'none',
                                        label:
                                            'Нет актов (0%)'
                                    },
                                    {
                                        value: 'partial',
                                        label:
                                            'Частично (1–99%)'
                                    },
                                    {
                                        value: 'full',
                                        label:
                                            'Полностью (100%)'
                                    }
                                ]}

                                onChange={value => {
                                    const nextSearch = {
                                        ...mainSearch,
                                        closedStatus: value
                                    };

                                    setMainSearch(
                                        nextSearch
                                    );

                                    applyMainRegistrySearch(
                                        nextSearch
                                    );
                                }}
                            />
                        </Col>

                        <Col span={4}>
                            <Button
                                block
                                icon={
                                    <ClearOutlined />
                                }

                                onClick={async () => {
                                    const emptySearch = {
                                        text: '',
                                        ka: '',
                                        dateRange: null,
                                        status: null,
                                        closedStatus: null
                                    };

                                    setMainSearch(
                                        emptySearch
                                    );

                                    await fetchRegistries({
                                        /*
                                        * Очищаем верхние
                                        * и колоночные фильтры.
                                        */
                                        type: [
                                            'customer_order',
                                            'supplier_order'
                                        ],

                                        id: '',
                                        number: '',
                                        title: '',
                                        counterparty_name:
                                            '',
                                        status: '',
                                        date: '',
                                        user_fio: '',

                                        search: '',
                                        date_from: '',
                                        date_to: '',
                                        closed_status: '',

                                        page: 1,

                                        limit:
                                            registriesPagination
                                                .pageSize
                                    });

                                    setRegistryTableFilters({
                                        id: '',
                                        number: '',
                                        title: '',
                                        counterparty_name:
                                            '',
                                        status: '',
                                        date: '',
                                        user_fio: '',

                                        type: [
                                            'customer_order',
                                            'supplier_order'
                                        ]
                                    });
                                }}
                            >
                                Сброс
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Card
                    title={
                        <Space wrap>
                            <span>Реестр заказов</span>
                            {(selectedOrderKeys.length > 0 ||
                                selectedActKeys.length > 0) && (
                                <Tag color="blue">
                                    Выбрано:{' '}
                                    {selectedOrderKeys.length +
                                        selectedActKeys.length}
                                </Tag>
                            )}
                        </Space>
                    }
                    extra={
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'payment_request',
                                        label: 'Заявка на оплату',
                                        icon: (
                                            <FileExcelOutlined />
                                        )
                                    },
                                    {
                                        key: 'work_statement',
                                        label:
                                            'Ведомость выполненных работ',
                                        icon: (
                                            <FileExcelOutlined />
                                        )
                                    },
                                    {
                                        type: 'divider'
                                    },
                                    {
                                        key: 'supplier_order',
                                        label:
                                            'Заказ поставщика (Excel + PDF)',
                                        icon: (
                                            <FileExcelOutlined />
                                        ),
                                        disabled:
                                            selectedOrderKeys.length ===
                                            0
                                    }
                                ],
                                onClick: ({ key }) => {
                                    if (
                                        key ===
                                        'supplier_order'
                                    ) {
                                        downloadSupplierOrdersExport();
                                        return;
                                    }

                                    downloadPaymentDocsExport(
                                        key
                                    );
                                }
                            }}
                            disabled={
                                paymentDocsExportLoading ||
                                supplierOrdersExportLoading ||
                                (
                                    selectedOrderKeys.length ===
                                        0 &&
                                    selectedActKeys.length ===
                                        0
                                )
                            }
                        >
                            <Button
                                type="primary"
                                loading={
                                    paymentDocsExportLoading ||
                                    supplierOrdersExportLoading
                                }
                                icon={<FileExcelOutlined />}
                            >
                                Выгрузить{' '}
                                <DownOutlined />
                            </Button>
                        </Dropdown>
                    }
                >
                    <Table
                        dataSource={registries}
                        rowKey="id"
                        loading={loading}
                        rowSelection={{
                            selectedRowKeys:
                                selectedOrderKeys,
                            onChange: keys =>
                                setSelectedOrderKeys(keys),
                            getCheckboxProps: record => ({
                                disabled:
                                    record.type !==
                                    'supplier_order',
                                title:
                                    record.type !==
                                    'supplier_order'
                                        ? 'Доступно только для заказов поставщика'
                                        : undefined
                            }),
                            preserveSelectedRowKeys: true
                        }}
                        pagination={{
                            current:
                                registriesPagination.current,

                            pageSize:
                                registriesPagination.pageSize,

                            total:
                                registriesTotal,

                            showSizeChanger: true,

                            pageSizeOptions: [
                                20,
                                50,
                                100,
                                200
                            ],

                            showTotal: total =>
                                `Всего документов: ${total}`
                        }}

                        onChange={handleRegistriesTableChange}
                        // НАСТРОЙКА РАСКРЫТИЯ СТРОК
                        expandable={{
                            expandedRowRender: (record) => {
                                const rawActs = expandedActs[record.id];

                                const acts = Array.isArray(rawActs)
                                    ? rawActs
                                    : [];

                                const isActLoading =
                                    Boolean(expandingLoading[record.id]);

                                if (isActLoading) {
                                    return (
                                        <div
                                            style={{
                                                padding: 20,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <Spin size="small" />
                                        </div>
                                    );
                                }

                                if (acts.length === 0) {
                                    return (
                                        <Alert
                                            message="Связанные акты не найдены"
                                            type="info"
                                            showIcon
                                        />
                                    );
                                }

                                return (
                                    <div
                                        style={{
                                            background: '#f0f5ff',
                                            padding: 10,
                                            borderRadius: 8
                                        }}
                                    >
                                        <Text
                                            strong
                                            style={{
                                                marginBottom: 10,
                                                display: 'block'
                                            }}
                                        >
                                            Связанные акты:
                                        </Text>

                                        <Table
                                            size="small"
                                            dataSource={acts}
                                            rowKey="id"
                                            pagination={false}
                                            columns={[
                                                {
                                                    title: '№ Акта',
                                                    dataIndex: 'number',
                                                    width: 150
                                                },
                                                {
                                                    title: 'Дата',
                                                    dataIndex: 'date',
                                                    render: value =>
                                                        value
                                                            ? dayjs(value).format(
                                                                'DD.MM.YYYY'
                                                            )
                                                            : '—'
                                                },
                                                {
                                                    title: 'Сумма',
                                                    dataIndex: 'total_amount',
                                                    align: 'right',
                                                    render: value => (
                                                        <b>
                                                            {(
                                                                Number(value) || 0
                                                            ).toLocaleString('ru-RU')}{' '}
                                                            р.
                                                        </b>
                                                    )
                                                },
                                                {
                                                    title: 'Статус',
                                                    dataIndex: 'status',
                                                    render: status => (
                                                        <Tag color="blue">
                                                            {status || '—'}
                                                        </Tag>
                                                    )
                                                },
                                                {
                                                    title: '',
                                                    key: 'actions',
                                                    render: (_, act) => (
                                                        <Space>
                                                            <Button
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                onClick={() =>
                                                                    viewRegistryDetails(act)
                                                                }
                                                            >
                                                                Открыть
                                                            </Button>

                                                            <Popconfirm
                                                                title="Удалить этот акт?"
                                                                onConfirm={() =>
                                                                    handleDeleteRegistry(
                                                                        act.id,
                                                                        record.id
                                                                    )
                                                                }
                                                                okText="Да"
                                                                cancelText="Нет"
                                                            >
                                                                <Button
                                                                    size="small"
                                                                    danger
                                                                    icon={
                                                                        <DeleteOutlined />
                                                                    }
                                                                />
                                                            </Popconfirm>
                                                        </Space>
                                                    )
                                                }
                                            ]}
                                        />
                                    </div>
                                );
                            },
                            // Загружаем акты только при нажатии на "+"
                            onExpand: (expanded, record) => {
                                if (expanded) fetchActsForOrder(record.id);
                            }
                        }}
                        columns={[
                            // Колонки заказов остаются прежними (как в вашем коде)
                            { title: 'Вн. №', dataIndex: 'id', key: 'id', width: 80 },
                            {
                                title: 'Закрытие',
                                key: 'closing',
                                width: 245,
                                align: 'center',
                                render: (_, record) => {
                                    const totalExpenseRows =
                                        Number(record.total_expense_rows) || 0;

                                    const distributedExpenseRows =
                                        Number(record.distributed_expense_rows) || 0;

                                    const totalExpenseAmount =
                                        Number(record.total_expense_amount) || 0;

                                    const distributedExpenseAmount =
                                        Number(record.distributed_expense_amount) || 0;

                                    const remainingExpenseRows = Math.max(
                                        totalExpenseRows - distributedExpenseRows,
                                        0
                                    );

                                    const remainingExpenseAmount = Math.max(
                                        totalExpenseAmount - distributedExpenseAmount,
                                        0
                                    );

                                    const supplierOrdersCount =
                                        Number(record.supplier_orders_count) || 0;

                                    const supplierOrdersPercent =
                                        record.type === 'customer_order'
                                            ? calculatePercent(
                                                distributedExpenseRows,
                                                totalExpenseRows
                                            )
                                            : 0;
                                    if (!record.type?.includes('order')) return null;

                                    const totalRows = toNumber(record.total_rows);
                                    const closedRows = toNumber(record.closed_rows);

                                    const orderAmount = toNumber(record.total_amount);
                                    const closedAmount = toNumber(record.closed_amount);

                                    const supplierOrdersAmount = toNumber(
                                        record.supplier_orders_amount
                                    );

                                    const supplierClosedAmount = toNumber(
                                        record.supplier_closed_amount
                                    );

                                    const supplierOrdersClosed = toNumber(
                                        record.supplier_orders_closed
                                    );

                                    /*
                                    * Круг №1:
                                    * закрытие текущего заказа его непосредственными актами.
                                    */
                                    const directActsPercent = calculatePercent(
                                        closedRows,
                                        totalRows
                                    );

                                    

                                    
                                   
                                   


                                  
                                    /*
                                    * Круг №3:
                                    * какая часть распределённых расходов
                                    * закрыта актами поставщика.
                                    */
                                    const supplierDistributedRows = toNumber(
                                        record.supplier_distributed_rows
                                    );

                                    const supplierActClosedRows = toNumber(
                                        record.supplier_act_closed_rows
                                    );

                                    const supplierDistributedAmount = toNumber(
                                        record.supplier_distributed_amount
                                    );

                                    const supplierActClosedAmount = toNumber(
                                        record.supplier_act_closed_amount
                                    );

                                    const supplierActsCount = toNumber(
                                        record.supplier_acts_count
                                    );

                                    const supplierActRemainingRows = Math.max(
                                        supplierDistributedRows - supplierActClosedRows,
                                        0
                                    );

                                    const supplierActRemainingAmount = Math.max(
                                        supplierDistributedAmount - supplierActClosedAmount,
                                        0
                                    );

                                    const supplierActsPercent =
                                        record.type === 'customer_order'
                                            ? calculatePercent(
                                                supplierActClosedRows,
                                                supplierDistributedRows
                                            )
                                            : 0;
                                                                        
                                    

                                    // Для отдельного заказа поставщика показываем его закрытие актами.
                                    if (record.type === 'supplier_order') {
                                        return (
                                            <ClosingCircle
                                                percent={directActsPercent}
                                                label="Актами"
                                                tooltip={
                                                    <div>
                                                        <div style={{ fontWeight: 700, marginBottom: 8 }}>
                                                            Закрытие заказа поставщика актами
                                                        </div>

                                                        <div>
                                                            Позиций закрыто: <b>{closedRows}</b> из{' '}
                                                            <b>{totalRows}</b>
                                                        </div>

                                                        <div>
                                                            Сумма заказа: <b>{formatMoney(orderAmount)}</b>
                                                        </div>

                                                        <div>
                                                            Закрыто актами:{' '}
                                                            <b>{formatMoney(closedAmount)}</b>
                                                        </div>

                                                        <div>
                                                            Осталось закрыть:{' '}
                                                            <b>
                                                                {formatMoney(
                                                                    Math.max(
                                                                        0,
                                                                        orderAmount - closedAmount
                                                                    )
                                                                )}
                                                            </b>
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        );
                                    }

                                    // Для заказа покупателя выводим три уровня закрытия.
                                    return (
                                        <Space size={6} align="start">
                                            <ClosingCircle
                                                percent={directActsPercent}
                                                label="Акты покупателя"
                                                tooltip={
                                                    <div>
                                                        <div style={{ fontWeight: 700, marginBottom: 8 }}>
                                                            Закрытие заказа актами покупателя
                                                        </div>

                                                        <div>
                                                            Позиций закрыто: <b>{closedRows}</b> из{' '}
                                                            <b>{totalRows}</b>
                                                        </div>

                                                        <div>
                                                            Сумма заказа: <b>{formatMoney(orderAmount)}</b>
                                                        </div>

                                                        <div>
                                                            Закрыто актами:{' '}
                                                            <b>{formatMoney(closedAmount)}</b>
                                                        </div>

                                                        <div>
                                                            Осталось закрыть:{' '}
                                                            <b>
                                                                {formatMoney(
                                                                    Math.max(
                                                                        0,
                                                                        orderAmount - closedAmount
                                                                    )
                                                                )}
                                                            </b>
                                                        </div>
                                                    </div>
                                                }
                                            />

                                            {record.type === 'customer_order' && (
                                                <ClosingCircle
                                                    percent={supplierOrdersPercent}
                                                    label="Заказы поставщика"
                                                    tooltip={
                                                        <div style={{ minWidth: 300 }}>
                                                            <div
                                                                style={{
                                                                    fontWeight: 700,
                                                                    marginBottom: 10
                                                                }}
                                                            >
                                                                Распределение расходов ВДР
                                                            </div>

                                                            <div
                                                                style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: '1fr auto',
                                                                    gap: '5px 18px'
                                                                }}
                                                            >
                                                                <span>Расходных строк ВДР:</span>
                                                                <b>{totalExpenseRows}</b>

                                                                <span>Передано поставщикам:</span>
                                                                <b>{distributedExpenseRows}</b>

                                                                <span>Не распределено:</span>
                                                                <b>{remainingExpenseRows}</b>

                                                                <span
                                                                    style={{
                                                                        marginTop: 6
                                                                    }}
                                                                >
                                                                    Сумма расходов:
                                                                </span>
                                                                <b
                                                                    style={{
                                                                        marginTop: 6
                                                                    }}
                                                                >
                                                                    {formatMoney(totalExpenseAmount)}
                                                                </b>

                                                                <span>Распределено:</span>
                                                                <b>
                                                                    {formatMoney(
                                                                        distributedExpenseAmount
                                                                    )}
                                                                </b>

                                                                <span>Осталось:</span>
                                                                <b>
                                                                    {formatMoney(
                                                                        remainingExpenseAmount
                                                                    )}
                                                                </b>

                                                                <span
                                                                    style={{
                                                                        marginTop: 6
                                                                    }}
                                                                >
                                                                    Заказов поставщика:
                                                                </span>
                                                                <b
                                                                    style={{
                                                                        marginTop: 6
                                                                    }}
                                                                >
                                                                    {supplierOrdersCount}
                                                                </b>
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            )}

                                            {record.type === 'customer_order' && (
                                                <ClosingCircle
                                                    percent={supplierActsPercent}
                                                    label="Акты поставщика"
                                                    tooltip={
                                                        <div style={{ minWidth: 300 }}>
                                                            <div
                                                                style={{
                                                                    fontWeight: 700,
                                                                    marginBottom: 10
                                                                }}
                                                            >
                                                                Закрытие расходов актами поставщика
                                                            </div>

                                                            <div
                                                                style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: '1fr auto',
                                                                    gap: '5px 18px'
                                                                }}
                                                            >
                                                                <span>Распределённых строк:</span>
                                                                <b>{supplierDistributedRows}</b>

                                                                <span>Закрыто актами:</span>
                                                                <b>{supplierActClosedRows}</b>

                                                                <span>Осталось закрыть:</span>
                                                                <b>{supplierActRemainingRows}</b>

                                                                <span style={{ marginTop: 6 }}>
                                                                    Распределённая сумма:
                                                                </span>
                                                                <b style={{ marginTop: 6 }}>
                                                                    {formatMoney(
                                                                        supplierDistributedAmount
                                                                    )}
                                                                </b>

                                                                <span>Закрыто актами:</span>
                                                                <b>
                                                                    {formatMoney(
                                                                        supplierActClosedAmount
                                                                    )}
                                                                </b>

                                                                <span>Осталось закрыть:</span>
                                                                <b>
                                                                    {formatMoney(
                                                                        supplierActRemainingAmount
                                                                    )}
                                                                </b>

                                                                <span style={{ marginTop: 6 }}>
                                                                    Актов поставщика:
                                                                </span>
                                                                <b style={{ marginTop: 6 }}>
                                                                    {supplierActsCount}
                                                                </b>
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            )}
                                        </Space>
                                    );
                                }
                            },
                            { title: 'Дата док.', dataIndex: 'date', render: d => dayjs(d).format('DD.MM.YYYY') },
                            {
                                title: 'Тип',
                                dataIndex: 'type',
                                filters: [
                                    { text: 'Заказ покупателя', value: 'customer_order' },
                                    { text: 'Заказ поставщика', value: 'supplier_order' },
                                    { text: 'Акт покупателя', value: 'customer_act' },
                                    { text: 'Акт поставщика', value: 'supplier_act' }
                                ],
                                filterMultiple: false,
                                render: t => {
                                    const typeMap = {
                                        customer_order: { color: 'geekblue', text: 'ЗАКАЗ ПОК.' },
                                        supplier_order: { color: 'orange', text: 'ЗАКАЗ ПОСТ.' },
                                    };
                                    const cfg = typeMap[t] || { color: 'default', text: t };
                                    return <Tag color={cfg.color}>{cfg.text}</Tag>;
                                }
                            },
                            { title: 'Номер док.', dataIndex: 'number', ...getColumnHintProps('number', filterHints.numbers) },
                            ,
                            {
                                title: 'Наименование',
                                dataIndex: 'title',
                                ...getColumnHintProps('title', filterHints.titles) // Подсказки по названиям
                            },
                            {
                                title: 'Контрагент',
                                dataIndex: 'counterparty_name',
                                ...getColumnHintProps('counterparty_name', filterHints.counterparties) // Подсказки по КА
                            },
                            {
                                title: 'Создатель',
                                dataIndex: ['user', 'fio'],
                                key: 'user_fio',
                                render: v => v || 'Система',
                                ...getColumnHintProps('user_fio', filterHints.creators) // Подсказки по ФИО
                            },
                            {
                                title: 'Сумма',
                                dataIndex: 'total_amount',
                                align: 'right',
                                render: v => `${parseFloat(v || 0).toLocaleString()} р.`
                            },
                            {
                                title: 'Статус',
                                dataIndex: 'status',
                                key: 'status',
                                width: 150,
                                filters: REGISTRY_STATUS_OPTIONS,
                                filterMultiple: false,
                                onFilter: (value, record) => record.status === value,
                                render: s => {
                                    if (!s) return <Text type="secondary">-</Text>;

                                    // Ищем объект статуса (теперь через .text)
                                    const statusObj = REGISTRY_STATUS_OPTIONS.find(
                                        opt => opt.value.toLowerCase() === s.toLowerCase()
                                    );
                                    
                                    const label = statusObj ? statusObj.text : s;

                                    const colorMap = {
                                        draft: 'orange',
                                        approval: 'blue',
                                        approved: 'green',
                                        rejected: 'red',
                                        paid: 'purple'
                                    };

                                    return (
                                        <Tag color={colorMap[s.toLowerCase()] || 'default'}>
                                            {label.toUpperCase()}
                                        </Tag>
                                    );
                                }
                            },
                            { title: 'Действия', key: 'actions', render: (_, record) => (
                                    <Space>
                                        <Button icon={<EyeOutlined />} onClick={() => viewRegistryDetails(record)} />
                                        <Popconfirm title="Удалить?" onConfirm={() => handleDeleteRegistry(record.id)} >
                                            <Button icon={<DeleteOutlined />} danger />
                                        </Popconfirm>
                                    </Space>
                                )}
                        ]}
                    />

                </Card>

                {/* ТАБЛИЦА ВСЕХ АКТОВ */}
                <Card
                    style={{ marginTop: 24 }}
                    title={
                        <Space>
                            <div
                                style={{
                                    width: 10,
                                    height: 20,
                                    background: '#13c2c2',
                                    borderRadius: 2
                                }}
                            />
                            Реестр Актов (Покупатели / Поставщики)
                            {selectedActKeys.length > 0 && (
                                <Tag color="cyan">
                                    Выбрано актов:{' '}
                                    {selectedActKeys.length}
                                </Tag>
                            )}
                        </Space>
                    }
                    extra={
                        <Space>
                            <Button
                                onClick={() => {
                                    setSelectedOrderKeys([]);
                                    setSelectedActKeys([]);
                                }}
                                disabled={
                                    selectedOrderKeys.length ===
                                        0 &&
                                    selectedActKeys.length ===
                                        0
                                }
                            >
                                Сбросить выбор
                            </Button>
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: 'payment_request',
                                            label:
                                                'Заявка на оплату',
                                            icon: (
                                                <FileExcelOutlined />
                                            )
                                        },
                                        {
                                            key: 'work_statement',
                                            label:
                                                'Ведомость выполненных работ',
                                            icon: (
                                                <FileExcelOutlined />
                                            )
                                        },
                                        {
                                            type: 'divider'
                                        },
                                        {
                                            key: 'supplier_act',
                                            label:
                                                'Акт поставщика (Excel + PDF)',
                                            icon: (
                                                <FileExcelOutlined />
                                            )
                                        }
                                    ],
                                    onClick: ({ key }) => {
                                        if (
                                            key ===
                                            'supplier_act'
                                        ) {
                                            downloadSupplierActsExport();
                                            return;
                                        }

                                        downloadPaymentDocsExport(
                                            key
                                        );
                                    }
                                }}
                                disabled={
                                    paymentDocsExportLoading ||
                                    supplierActsExportLoading ||
                                    (
                                        selectedOrderKeys.length ===
                                            0 &&
                                        selectedActKeys.length ===
                                            0
                                    )
                                }
                            >
                                <Button
                                    loading={
                                        paymentDocsExportLoading ||
                                        supplierActsExportLoading
                                    }
                                    icon={<FileExcelOutlined />}
                                >
                                    Выгрузить{' '}
                                    <DownOutlined />
                                </Button>
                            </Dropdown>
                        </Space>
                    }
                >
                    <Table
                        dataSource={allActs.filter(act => {
                            const searchVal = mainSearch.text?.toLowerCase();
                            // Основной поиск по верхней строке
                            const matchText = !searchVal ||
                                act.number?.toLowerCase().includes(searchVal) ||
                                act.title?.toLowerCase().includes(searchVal);
                            const matchStatus = !mainSearch.status || act.status === mainSearch.status;
                            return matchText && matchStatus;
                        })}
                        rowKey="id"
                        loading={allActsLoading}
                        size="small"
                        rowSelection={{
                            selectedRowKeys: selectedActKeys,
                            onChange: keys =>
                                setSelectedActKeys(keys),
                            getCheckboxProps: record => ({
                                disabled:
                                    record.type !==
                                    'supplier_act',
                                title:
                                    record.type !==
                                    'supplier_act'
                                        ? 'Доступно только для актов поставщика'
                                        : undefined
                            }),
                            preserveSelectedRowKeys: true
                        }}
                        columns={[
                            { title: 'Вн. №', dataIndex: 'id', width: 80 },
                            { title: 'Дата', dataIndex: 'date', render: d => dayjs(d).format('DD.MM.YYYY') },
                            {
                                title: 'Тип',
                                dataIndex: 'type',
                                width: 120,
                                filters: [
                                    { text: 'АКТ ПОК.', value: 'customer_act' },
                                    { text: 'АКТ ПОСТ.', value: 'supplier_act' }
                                ],
                                onFilter: (value, record) => record.type === value, // Клиентская фильтрация
                                render: t => <Tag color="cyan">{t === 'customer_act' ? 'АКТ ПОК.' : 'АКТ ПОСТ.'}</Tag>
                            },
                            {
                                title: 'Номер док.',
                                dataIndex: 'number',
                                // Используем вашу функцию поиска с подсказками
                                ...getColumnHintProps('number', allActsFilterHints.numbers),
                                onFilter: (value, record) => record.number?.toString().toLowerCase().includes(value.toLowerCase()),
                                render: v => <b>{v}</b>
                            },
                            {
                                title: 'Основание (Заказ)',
                                key: 'order_link',
                                render: (_, record) => record.parentOrder ? (
                                    <Tag color="blue">Заказ №{record.parentOrder.number}</Tag>
                                ) : <Text type="secondary" italic>без заказа</Text>
                            },
                            {
                                title: 'Контрагент',
                                dataIndex: 'counterparty_name',
                                ...getColumnHintProps('counterparty_name', allActsFilterHints.counterparties),
                                onFilter: (value, record) => record.counterparty_name?.toLowerCase().includes(value.toLowerCase()),
                            },
                            {
                                title: 'Сумма',
                                dataIndex: 'total_amount',
                                align: 'right',
                                render: v => <b>{parseFloat(v || 0).toLocaleString()} р.</b>
                            },
                            {
                                title: 'Статус',
                                dataIndex: 'status',
                                filters: REGISTRY_STATUS_OPTIONS,
                                onFilter: (value, record) => record.status === value,
                                render: s => {
                                    const statusObj = REGISTRY_STATUS_OPTIONS.find(opt => opt.value === s);
                                    return <Tag color={s === 'draft' ? 'orange' : 'green'}>{statusObj?.label || s}</Tag>;
                                }
                            },
                            {
                                title: 'Действия',
                                key: 'actions',
                                width: 100,
                                render: (_, record) => (
                                    <Space>
                                        <Button size="small" icon={<EyeOutlined />} onClick={() => viewRegistryDetails(record)} />
                                        <Popconfirm title="Удалить?" onConfirm={() => handleDeleteRegistry(record.id)}>
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </Space>
                                )
                            }
                        ]}
                    />
                </Card>
                <Modal
    title={
        attachInvoiceProposal
            ? `Добавить новые строки в ${
                attachInvoiceProposal
                    .registryTypeTitle
            }?`
            : 'Добавить строки в заказ'
    }

    open={
        Boolean(
            attachInvoiceProposal
        )
    }

    width={620}

    zIndex={6000}

    maskClosable={false}

    keyboard={
        !attachInvoiceLoading
    }

    closable={
        !attachInvoiceLoading
    }

    confirmLoading={
        attachInvoiceLoading
    }

    okText={
        attachInvoiceProposal
            ? `Добавить (${
                attachInvoiceProposal
                    .invoiceIds?.length || 0
            })`
            : 'Добавить'
    }

    cancelText="Оставить только в ВДР"

    onOk={
        confirmAttachInvoicesToRegistry
    }

    onCancel={
        cancelAttachInvoicesToRegistry
    }
>
    {attachInvoiceProposal && (
        <div>
            <Paragraph>
                Документ:{' '}
                <Text strong>
                    {attachInvoiceProposal
                        .registryNumber ||
                        `ID ${
                            attachInvoiceProposal
                                .registryId
                        }`}
                </Text>
            </Paragraph>

            {attachInvoiceProposal
                .registryTitle && (
                <Paragraph>
                    Название:{' '}
                    <Text strong>
                        {
                            attachInvoiceProposal
                                .registryTitle
                        }
                    </Text>
                </Paragraph>
            )}

            <Paragraph>
                Новых строк:{' '}
                <Text strong>
                    {attachInvoiceProposal
                        .invoiceIds?.length || 0}
                </Text>
            </Paragraph>

            <Paragraph>
                Общая сумма:{' '}
                <Text strong>
                    {Number(
                        attachInvoiceProposal
                            .totalAmount || 0
                    ).toLocaleString(
                        'ru-RU',
                        {
                            minimumFractionDigits:
                                2,
                            maximumFractionDigits:
                                2
                        }
                    )}{' '}
                    ₽
                </Text>
            </Paragraph>

            <div
                style={{
                    maxHeight: 240,
                    overflowY: 'auto',
                    padding: 10,
                    background:
                        '#fafafa',
                    borderRadius: 6
                }}
            >
                {attachInvoiceProposal
                    .invoices
                    ?.map(item => (
                        <div
                            key={
                                item.invoice_id
                            }
                            style={{
                                display:
                                    'flex',

                                justifyContent:
                                    'space-between',

                                alignItems:
                                    'center',

                                gap: 16,

                                padding:
                                    '6px 0',

                                borderBottom:
                                    '1px solid #f0f0f0'
                            }}
                        >
                            <div>
                                <div>
                                    {item.rate_name ||
                                        `Строка ID ${item.invoice_id}`}
                                </div>

                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11
                                    }}
                                >
                                    ID:{' '}
                                    {
                                        item.invoice_id
                                    }
                                </Text>
                            </div>

                            <Text strong>
                                {Number(
                                    item.amount ||
                                    0
                                ).toLocaleString(
                                    'ru-RU',
                                    {
                                        minimumFractionDigits:
                                            2,
                                        maximumFractionDigits:
                                            2
                                    }
                                )}{' '}
                                ₽
                            </Text>
                        </div>
                    ))}
            </div>
        </div>
    )}
</Modal>
                {/* МОДАЛЬНОЕ ОКНО ПРОСМОТРА */}
                <Modal
                    title={
    <div
        style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            paddingRight: 30
        }}
    >
        <Space size="middle">
            {registryViewHistory.length > 0 && (
                <Button
                    size="small"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBackRegistryView}
                >
                    Назад
                </Button>
            )}

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    lineHeight: 1.25
                }}
            >
                <Text strong>
                    {getRegistryTypeTitle(
                        selectedRegistry?.type
                    )}
                    {' №'}
                    {selectedRegistry?.number || '—'}
                </Text>

                <Text
                    type="secondary"
                    style={{ fontSize: 12 }}
                >
                    от{' '}
                    {formatRegistryDate(
                        selectedRegistry?.date
                    )}

                    {selectedRegistry?.title
                        ? ` — ${selectedRegistry.title}`
                        : ''}
                </Text>
            </div>
        </Space>

        <Button
    type="primary"

    size="small"

    icon={
        <FileExcelOutlined />
    }

    onClick={
        openExportFormatModal
    }
>
    Скачать Excel
</Button>
    </div>
}
                    open={isViewModalVisible}
width="90%"
destroyOnClose={false}
onCancel={closeRegistryView}
style={{
    top: 20,
    paddingBottom: 0
}}
styles={{
    content: {
        maxHeight:
            'calc(100vh - 40px)',

        display:
            'flex',

        flexDirection:
            'column',

        overflow:
            'hidden'
    },

    header: {
        flex:
            '0 0 auto',

        marginBottom:
            0,

        paddingBottom:
            16,

        borderBottom:
            '1px solid #f0f0f0'
    },

    body: {
        flex:
            '1 1 auto',

        minHeight:
            0,

        overflowY:
            'auto',

        paddingTop:
            16,

        paddingBottom:
            20
    },

    footer: {
        flex:
            '0 0 auto',

        marginTop:
            0,

        padding:
            '12px 24px',

        background:
            '#fff',

        borderTop:
            '1px solid #f0f0f0',

        boxShadow:
            '0 -4px 12px rgba(0, 0, 0, 0.06)'
    }
}}
footer={
    <div
        style={{
            display:
                'flex',

            alignItems:
                'center',

            justifyContent:
                'space-between',

            gap:
                16,

            width:
                '100%',

            flexWrap:
                'wrap'
        }}
    >
        <Space
            size={20}
            wrap
            split={
                <Divider
                    type="vertical"
                    style={{
                        height:
                            30
                    }}
                />
            }
        >
            <div>
                <Text
                    type="secondary"
                    style={{
                        display:
                            'block',

                        fontSize:
                            12
                    }}
                >
                    Задач
                </Text>

                <Text strong>
                    {
                        registryViewFooterStats
                            .tasksCount
                    }
                </Text>
            </div>

            <div>
                <Text
                    type="secondary"
                    style={{
                        display:
                            'block',

                        fontSize:
                            12
                    }}
                >
                    Строк ВДР
                </Text>

                <Text strong>
                    {
                        registryViewFooterStats
                            .rowsCount
                    }
                </Text>
            </div>

            <div>
                <Text
                    type="secondary"
                    style={{
                        display:
                            'block',

                        fontSize:
                            12
                    }}
                >
                    Стоимость
                </Text>

                <Text
                    strong
                    style={{
                        fontSize:
                            16
                    }}
                >
                    {Number(
                        registryViewFooterStats
                            .amount
                    ).toLocaleString(
                        'ru-RU',
                        {
                            minimumFractionDigits:
                                2,

                            maximumFractionDigits:
                                2
                        }
                    )}{' '}
                    ₽
                </Text>
            </div>
        </Space>

        <Space
            wrap
            style={{
                justifyContent:
                    'flex-end'
            }}
        >
            {
                registryViewFooterActions
            }
        </Space>
    </div>
}
                >
                    <Spin
    spinning={registryViewLoading}
    size="large"
    tip="Загрузка документа..."
>
    <div
        style={{
            minHeight:
                registryViewLoading
                    ? 360
                    : undefined,

            display:
                registryViewLoading
                    ? 'flex'
                    : 'block',

            alignItems:
                registryViewLoading
                    ? 'center'
                    : undefined,

            justifyContent:
                registryViewLoading
                    ? 'center'
                    : undefined
        }}
    >
        {!registryViewLoading &&
            selectedRegistry && (
                <div className="registry-view-content">
                            {/* РЕКВИЗИТЫ */}
                            {isHeaderEdit ? (
                                <Card
                                    size="small"
                                    title="Правка реквизитов документа"
                                    style={{
                                        marginBottom: 20,
                                        background: '#fafafa'
                                    }}
                                >
                                    <Form
                                        form={headerForm}
                                        layout="vertical"
                                    >
                                        <Row gutter={16}>
                                            <Col xs={24} md={6}>
                                                <Form.Item
                                                    name="number"
                                                    label="Номер документа"
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} md={10}>
                                                <Form.Item
                                                    name="title"
                                                    label="Наименование"
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} md={8}>
                                                <Form.Item
                                                    name="date"
                                                    label="Дата документа"
                                                >
                                                    <DatePicker
                                                        format="DD.MM.YYYY"
                                                        style={{
                                                            width: '100%'
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} md={10}>
                                                <Form.Item
                                                    name="period"
                                                    label="Период документа (по дате закрытия заявок)"
                                                >
                                                    <RangePicker
                                                        format="DD.MM.YYYY"
                                                        placeholder={['С', 'По']}
                                                        style={{
                                                            width: '100%'
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card>
                            ) : (
                                <Descriptions
                                    bordered
                                    size="small"
                                    style={{
                                        marginBottom: 20
                                    }}
                                >
                                    <Descriptions.Item
                                        label="Контрагент"
                                        span={2}
                                    >
                                        <b>
                                            {selectedRegistry
                                                .counterparty_name || '—'}
                                        </b>
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Дата документа">
                                        {selectedRegistry.date
                                            ? dayjs(
                                                selectedRegistry.date
                                            ).format('DD.MM.YYYY')
                                            : '—'}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Номер документа">
                                        {selectedRegistry.number || '—'}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Период">
                                        {formatRegistryPeriod(selectedRegistry) || (
                                            <Text type="secondary">не задан</Text>
                                        )}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Наименование">
                                        {selectedRegistry.title || '—'}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Статус">
                                        <Select
                                            value={selectedRegistry.status}
                                            onChange={updateRegistryStatus}
                                            style={{ width: 180 }}
                                            options={REGISTRY_STATUS_OPTIONS.map(item => ({
                                                value: item.value,
                                                label: item.text
                                            }))}
                                            disabled={
                                                isHeaderEdit ||
                                                isEditMode
                                            }
                                        />
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Итоговая сумма">
                                        <Text
                                            type="danger"
                                            strong
                                            style={{
                                                fontSize: 18
                                            }}
                                        >
                                            {Number(
                                                registryViewFooterStats.amount ||
                                                selectedRegistry.total_amount ||
                                                0
                                            ).toLocaleString('ru-RU', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}{' '}
                                            ₽
                                        </Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            )}

                            {/* ПРОВЕРКА ПЕРИОДА */}
                            {['customer_order', 'supplier_order'].includes(selectedRegistry.type) && (
                                !formatRegistryPeriod(selectedRegistry) ? (
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                        message="Период документа не задан"
                                        description="Укажите период в реквизитах — CRM будет подсвечивать заявки с другой датой закрытия и уведомлять автора о не включённых строках ВДР контрагента."
                                    />
                                ) : periodCheckLoading && !periodCheck ? (
                                    <Alert
                                        type="info"
                                        style={{ marginBottom: 16 }}
                                        message="Проверка периода…"
                                    />
                                ) : periodCheck?.enabled ? (
                                    <>
                                        {periodCheck.missing_count === 0 && periodCheck.outside_tasks === 0 && (
                                            <Alert
                                                type="success"
                                                showIcon
                                                style={{ marginBottom: 16 }}
                                                message={`За период ${formatRegistryPeriod(selectedRegistry)} все строки ВДР контрагента включены, даты закрытия заявок в периоде`}
                                            />
                                        )}

                                        {periodCheck.missing_count > 0 && (
                                            <Collapse
                                                style={{ marginBottom: 16, borderColor: '#faad14' }}
                                                items={[{
                                                    key: 'missing',
                                                    label: (
                                                        <Text strong style={{ color: '#d46b08' }}>
                                                            Не включено в документ строк ВДР за период: {periodCheck.missing_count}
                                                            {' '}(заявок: {periodCheck.missing_tasks}) на сумму {formatPeriodMoney(periodCheck.missing_amount)} ₽
                                                        </Text>
                                                    ),
                                                    children: (
                                                        <>
                                                            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                                                                Строки «{selectedRegistry.counterparty_name}», заявки по которым закрыты в периоде,
                                                                но которые не входят ни в один документ этого типа.
                                                            </Paragraph>
                                                            {!isSelectedRegistryAct && (
                                                                <Space style={{ marginBottom: 8 }}>
                                                                    <Button
                                                                        type="primary"
                                                                        icon={<PlusOutlined />}
                                                                        disabled={!periodMissingKeys.length}
                                                                        loading={periodAdding}
                                                                        onClick={() => addPeriodMissingToRegistry(periodMissingKeys)}
                                                                    >
                                                                        Добавить выбранные ({periodMissingKeys.length})
                                                                    </Button>
                                                                    <Popconfirm
                                                                        title={`Добавить все ${periodCheck.missing_count} строк в документ?`}
                                                                        okText="Добавить"
                                                                        cancelText="Отмена"
                                                                        onConfirm={() => addPeriodMissingToRegistry(
                                                                            periodCheck.missing.map(row => row.invoice_id)
                                                                        )}
                                                                    >
                                                                        <Button loading={periodAdding}>Добавить все</Button>
                                                                    </Popconfirm>
                                                                </Space>
                                                            )}
                                                            <Table
                                                                rowKey="invoice_id"
                                                                size="small"
                                                                dataSource={periodCheck.missing}
                                                                pagination={periodCheck.missing.length > 20 ? { pageSize: 20 } : false}
                                                                rowSelection={isSelectedRegistryAct ? undefined : {
                                                                    selectedRowKeys: periodMissingKeys,
                                                                    onChange: setPeriodMissingKeys
                                                                }}
                                                                columns={[
                                                                    {
                                                                        title: 'Заявка',
                                                                        dataIndex: 'external_id',
                                                                        width: 100,
                                                                        render: (value, row) => (
                                                                            <Tooltip title={row.task_title}>
                                                                                <Tag color="blue">#{value || row.task_id}</Tag>
                                                                            </Tooltip>
                                                                        )
                                                                    },
                                                                    {
                                                                        title: 'Закрыта',
                                                                        dataIndex: 'closed_at',
                                                                        width: 130,
                                                                        render: value => value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—'
                                                                    },
                                                                    { title: 'Статья', dataIndex: 'article' },
                                                                    { title: 'Номенклатура', dataIndex: 'rate_name' },
                                                                    {
                                                                        title: 'Кол-во',
                                                                        dataIndex: 'quantity',
                                                                        width: 90,
                                                                        render: (value, row) => `${value} ${row.unit || ''}`
                                                                    },
                                                                    {
                                                                        title: 'Сумма',
                                                                        dataIndex: 'amount',
                                                                        width: 120,
                                                                        align: 'right',
                                                                        render: value => formatPeriodMoney(value)
                                                                    }
                                                                ]}
                                                            />
                                                        </>
                                                    )
                                                }]}
                                            />
                                        )}

                                        {periodCheck.outside_tasks > 0 && (
                                            <Alert
                                                type="error"
                                                showIcon
                                                style={{ marginBottom: 16 }}
                                                message={`Заявок с датой закрытия вне периода ${formatRegistryPeriod(selectedRegistry)}: ${periodCheck.outside_tasks} (строк: ${periodCheck.outside_lines})`}
                                                description={
                                                    <Space wrap size={[4, 4]}>
                                                        {periodCheck.outside.map(row => (
                                                            <Tag key={row.task_id} color="red">
                                                                #{row.external_id || row.task_id}:{' '}
                                                                {row.closed_at
                                                                    ? dayjs(row.closed_at).format('DD.MM.YYYY')
                                                                    : 'не закрыта'}
                                                            </Tag>
                                                        ))}
                                                    </Space>
                                                }
                                            />
                                        )}
                                    </>
                                ) : null
                            )}

                            {/* ОБЩАЯ СТАТИСТИКА ЗАКРЫТИЯ */}
                            {selectedRegistry.type?.includes('order') &&
                            selectedRegistry.closure_stats && (
                                <Card
                                    title="Статистика закрытия"
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                >
                                    <Row
                                        gutter={[16, 16]}
                                        align="middle"
                                    >
                                        <Col xs={24} sm={12} md={5}>
                                            <div
                                                style={{
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <Progress
                                                    type="circle"
                                                    size={92}
                                                    percent={
                                                        Number(
                                                            selectedRegistry
                                                                .closure_stats
                                                                .closed_percent
                                                        ) || 0
                                                    }
                                                />

                                                <div
                                                    style={{
                                                        marginTop: 6
                                                    }}
                                                >
                                                    Закрытие актами
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xs={24} sm={12} md={6}>
                                            <Descriptions
                                                size="small"
                                                column={1}
                                                bordered
                                            >
                                                <Descriptions.Item label="Позиций">
                                                    {Number(
                                                        selectedRegistry
                                                            .closure_stats
                                                            .total_rows
                                                    ) || 0}
                                                </Descriptions.Item>

                                                <Descriptions.Item label="Закрыто">
                                                    {Number(
                                                        selectedRegistry
                                                            .closure_stats
                                                            .closed_rows
                                                    ) || 0}
                                                </Descriptions.Item>

                                                <Descriptions.Item label="Осталось">
                                                    {Number(
                                                        selectedRegistry
                                                            .closure_stats
                                                            .remaining_rows
                                                    ) || 0}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </Col>

                                        <Col xs={24} sm={12} md={8}>
                                            <Descriptions
                                                size="small"
                                                column={1}
                                                bordered
                                            >
                                                <Descriptions.Item label="Сумма заказа">
                                                    {Number(
                                                        selectedRegistry
                                                            .closure_stats
                                                            .total_amount || 0
                                                    ).toLocaleString(
                                                        'ru-RU'
                                                    )}{' '}
                                                    ₽
                                                </Descriptions.Item>

                                                <Descriptions.Item label="Закрыто">
                                                    {Number(
                                                        selectedRegistry
                                                            .closure_stats
                                                            .closed_amount || 0
                                                    ).toLocaleString(
                                                        'ru-RU'
                                                    )}{' '}
                                                    ₽
                                                </Descriptions.Item>

                                                <Descriptions.Item label="Осталось">
                                                    {Number(
                                                        selectedRegistry
                                                            .closure_stats
                                                            .remaining_amount || 0
                                                    ).toLocaleString(
                                                        'ru-RU'
                                                    )}{' '}
                                                    ₽
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </Col>

                                        <Col xs={24} sm={12} md={5}>
                                            <Descriptions
                                                size="small"
                                                column={1}
                                                bordered
                                            >
                                                <Descriptions.Item label="Актов">
                                                    {Number(
                                                        selectedRegistry
                                                            .closure_stats
                                                            .acts_count
                                                    ) || 0}
                                                </Descriptions.Item>

                                                <Descriptions.Item label="Состояние">
                                                    {selectedRegistry
                                                        .closure_stats
                                                        .fully_closed ? (
                                                        <Tag color="green">
                                                            Закрыт
                                                        </Tag>
                                                    ) : (
                                                        <Tag color="orange">
                                                            Частично
                                                        </Tag>
                                                    )}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </Col>
                                    </Row>
                                </Card>
                            )}

                            {/* ЕДИНЫЙ БЛОК АКТОВ И ЗАКАЗОВ ПОСТАВЩИКА */}
                            {selectedRegistry.type?.includes('order') && (
                                <Card
                                    title="Связанные документы и закрытие"
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                >
                                    <Tabs
                                        defaultActiveKey="acts"
                                        items={[
                                            {
                                                key: 'acts',

                                                label: (
                                                    <span>
                                                        Акты{' '}
                                                        <Tag>
                                                            {
                                                                selectedRegistry
                                                                    .closure_by_acts
                                                                    ?.length || 0
                                                            }
                                                        </Tag>
                                                    </span>
                                                ),

                                                children: Array.isArray(
                                                    selectedRegistry
                                                        .closure_by_acts
                                                ) &&
                                                selectedRegistry
                                                    .closure_by_acts
                                                    .length > 0 ? (
                                                    <Table
                                                        size="small"
                                                        rowKey="act_id"
                                                        pagination={false}
                                                        scroll={{ x: 950 }}
                                                        dataSource={
                                                            selectedRegistry
                                                                .closure_by_acts
                                                        }
                                                        columns={[
                                                            {
                                                                title: 'Акт',
                                                                dataIndex:
                                                                    'act_number',
                                                                width: 140,

                                                                render: (
                                                                    value,
                                                                    act
                                                                ) => (
                                                                    <Button
                                                                        type="link"
                                                                        size="small"
                                                                        onClick={() =>
                                                                            viewRegistryDetails({
                                                                                id:
                                                                                    act.act_id
                                                                            })
                                                                        }
                                                                    >
                                                                        {value ||
                                                                            `ID ${act.act_id}`}
                                                                    </Button>
                                                                )
                                                            },
                                                            {
                                                                title: 'Дата',
                                                                dataIndex:
                                                                    'act_date',
                                                                width: 110,

                                                                render: value =>
                                                                    value
                                                                        ? dayjs(
                                                                            value
                                                                        ).format(
                                                                            'DD.MM.YYYY'
                                                                        )
                                                                        : '—'
                                                            },
                                                            {
                                                                title: 'Контрагент',
                                                                dataIndex:
                                                                    'counterparty_name',
                                                                ellipsis: true
                                                            },
                                                            {
                                                                title: 'Строк',
                                                                dataIndex:
                                                                    'matched_rows',
                                                                align: 'right',
                                                                width: 90
                                                            },
                                                            {
                                                                title: 'Доля заказа',
                                                                dataIndex:
                                                                    'order_rows_percent',
                                                                width: 145,

                                                                render: value => (
                                                                    <Progress
                                                                        percent={
                                                                            Number(
                                                                                value
                                                                            ) || 0
                                                                        }
                                                                        size="small"
                                                                    />
                                                                )
                                                            },
                                                            {
                                                                title: 'Закрыто на сумму',
                                                                dataIndex:
                                                                    'matched_amount',
                                                                align: 'right',
                                                                width: 165,

                                                                render: value => (
                                                                    <b>
                                                                        {Number(
                                                                            value ||
                                                                            0
                                                                        ).toLocaleString(
                                                                            'ru-RU'
                                                                        )}{' '}
                                                                        ₽
                                                                    </b>
                                                                )
                                                            },
                                                            {
                                                                title: 'Сумма акта',
                                                                dataIndex:
                                                                    'act_total_amount',
                                                                align: 'right',
                                                                width: 150,

                                                                render: value =>
                                                                    `${Number(
                                                                        value || 0
                                                                    ).toLocaleString(
                                                                        'ru-RU'
                                                                    )} ₽`
                                                            },
                                                            {
                                                                title: 'Статус',
                                                                dataIndex:
                                                                    'act_status',
                                                                width: 120,

                                                                render: value => (
                                                                    <Tag
                                                                        color={
                                                                            value === 'approved'
                                                                                ? 'green'
                                                                                : value === 'rejected'
                                                                                    ? 'red'
                                                                                    : value === 'approval'
                                                                                        ? 'blue'
                                                                                        : 'default'
                                                                        }
                                                                    >
                                                                        {getRegistryStatusLabel(value)}
                                                                    </Tag>
                                                                )
                                                            }
                                                        ]}
                                                    />
                                                ) : (
                                                    <Alert
                                                        type="info"
                                                        showIcon
                                                        message="Связанные акты отсутствуют"
                                                    />
                                                )
                                            },

                                            ...(selectedRegistry.type ===
                                                'customer_order'
                                                ? [
                                                    {
                                                        key:
                                                            'supplier-orders',

                                                        label: (
                                                            <span>
                                                                Заказы поставщика{' '}
                                                                <Tag color="purple">
                                                                    {
                                                                        selectedRegistry
                                                                            .supplier_coverage_by_orders
                                                                            ?.length ||
                                                                        0
                                                                    }
                                                                </Tag>
                                                            </span>
                                                        ),

                                                        children: (
                                                            <>
                                                                {selectedRegistry
                                                                    .supplier_coverage_stats && (
                                                                    <Row
                                                                        gutter={[
                                                                            12,
                                                                            12
                                                                        ]}
                                                                        style={{
                                                                            marginBottom:
                                                                                16
                                                                        }}
                                                                    >
                                                                        <Col
                                                                            xs={
                                                                                24
                                                                            }
                                                                            md={
                                                                                5
                                                                            }
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    textAlign:
                                                                                        'center'
                                                                                }}
                                                                            >
                                                                                <Progress
                                                                                    type="circle"
                                                                                    size={
                                                                                        88
                                                                                    }
                                                                                    percent={
                                                                                        Number(
                                                                                            selectedRegistry
                                                                                                .supplier_coverage_stats
                                                                                                .distributed_rows_percent
                                                                                        ) ||
                                                                                        0
                                                                                    }
                                                                                />

                                                                                <div
                                                                                    style={{
                                                                                        marginTop:
                                                                                            5
                                                                                    }}
                                                                                >
                                                                                    Распределено
                                                                                    строк
                                                                                </div>
                                                                            </div>
                                                                        </Col>

                                                                        <Col
                                                                            xs={
                                                                                24
                                                                            }
                                                                            md={
                                                                                9
                                                                            }
                                                                        >
                                                                            <Descriptions
                                                                                bordered
                                                                                size="small"
                                                                                column={
                                                                                    1
                                                                                }
                                                                            >
                                                                                <Descriptions.Item label="Расходных строк">
                                                                                    {Number(
                                                                                        selectedRegistry
                                                                                            .supplier_coverage_stats
                                                                                            .total_expense_rows
                                                                                    ) ||
                                                                                        0}
                                                                                </Descriptions.Item>

                                                                                <Descriptions.Item label="Распределено">
                                                                                    {Number(
                                                                                        selectedRegistry
                                                                                            .supplier_coverage_stats
                                                                                            .distributed_expense_rows
                                                                                    ) ||
                                                                                        0}
                                                                                </Descriptions.Item>

                                                                                <Descriptions.Item label="Осталось">
                                                                                    {Number(
                                                                                        selectedRegistry
                                                                                            .supplier_coverage_stats
                                                                                            .remaining_expense_rows
                                                                                    ) ||
                                                                                        0}
                                                                                </Descriptions.Item>
                                                                            </Descriptions>
                                                                        </Col>

                                                                        <Col
                                                                            xs={
                                                                                24
                                                                            }
                                                                            md={
                                                                                10
                                                                            }
                                                                        >
                                                                            <Descriptions
                                                                                bordered
                                                                                size="small"
                                                                                column={
                                                                                    1
                                                                                }
                                                                            >
                                                                                <Descriptions.Item label="Расходная сумма">
                                                                                    {Number(
                                                                                        selectedRegistry
                                                                                            .supplier_coverage_stats
                                                                                            .total_expense_amount ||
                                                                                        0
                                                                                    ).toLocaleString(
                                                                                        'ru-RU'
                                                                                    )}{' '}
                                                                                    ₽
                                                                                </Descriptions.Item>

                                                                                <Descriptions.Item label="Распределено">
                                                                                    {Number(
                                                                                        selectedRegistry
                                                                                            .supplier_coverage_stats
                                                                                            .distributed_expense_amount ||
                                                                                        0
                                                                                    ).toLocaleString(
                                                                                        'ru-RU'
                                                                                    )}{' '}
                                                                                    ₽
                                                                                </Descriptions.Item>

                                                                                <Descriptions.Item label="Осталось">
                                                                                    {Number(
                                                                                        selectedRegistry
                                                                                            .supplier_coverage_stats
                                                                                            .remaining_expense_amount ||
                                                                                        0
                                                                                    ).toLocaleString(
                                                                                        'ru-RU'
                                                                                    )}{' '}
                                                                                    ₽
                                                                                </Descriptions.Item>
                                                                            </Descriptions>
                                                                        </Col>
                                                                    </Row>
                                                                )}

                                                                {Array.isArray(
                                                                    selectedRegistry
                                                                        .supplier_coverage_by_orders
                                                                ) &&
                                                                selectedRegistry
                                                                    .supplier_coverage_by_orders
                                                                    .length >
                                                                    0 ? (
                                                                    <Table
                                                                        size="small"
                                                                        rowKey={
                                                                            'supplier_order_id'
                                                                        }
                                                                        pagination={
                                                                            false
                                                                        }
                                                                        scroll={{
                                                                            x: 1100
                                                                        }}
                                                                        dataSource={
                                                                            selectedRegistry
                                                                                .supplier_coverage_by_orders
                                                                        }
                                                                        columns={[
                                                                            {
                                                                                title:
                                                                                    'Заказ поставщика',
                                                                                dataIndex:
                                                                                    'supplier_order_number',
                                                                                width:
                                                                                    170,

                                                                                render: (
                                                                                    value,
                                                                                    order
                                                                                ) => (
                                                                                    <Button
                                                                                        type="link"
                                                                                        size="small"
                                                                                        onClick={() =>
                                                                                            viewRegistryDetails({
                                                                                                id:
                                                                                                    order.supplier_order_id
                                                                                            })
                                                                                        }
                                                                                    >
                                                                                        {value ||
                                                                                            `ID ${order.supplier_order_id}`}
                                                                                    </Button>
                                                                                )
                                                                            },
                                                                            {
                                                                                title:
                                                                                    'Дата',
                                                                                dataIndex:
                                                                                    'supplier_order_date',
                                                                                width:
                                                                                    110,

                                                                                render:
                                                                                    value =>
                                                                                        value
                                                                                            ? dayjs(
                                                                                                value
                                                                                            ).format(
                                                                                                'DD.MM.YYYY'
                                                                                            )
                                                                                            : '—'
                                                                            },
                                                                            {
                                                                                title:
                                                                                    'Поставщик',
                                                                                dataIndex:
                                                                                    'counterparty_name',
                                                                                ellipsis:
                                                                                    true
                                                                            },
                                                                            {
                                                                                title:
                                                                                    'Распределено строк',
                                                                                dataIndex:
                                                                                    'distributed_rows',
                                                                                align:
                                                                                    'right',
                                                                                width:
                                                                                    145
                                                                            },
                                                                            {
                                                                                title:
                                                                                    'Доля расходов',
                                                                                dataIndex:
                                                                                    'share_of_expense_rows',
                                                                                width:
                                                                                    145,

                                                                                render:
                                                                                    value => (
                                                                                        <Progress
                                                                                            percent={
                                                                                                Number(
                                                                                                    value
                                                                                                ) ||
                                                                                                0
                                                                                            }
                                                                                            size="small"
                                                                                        />
                                                                                    )
                                                                            },
                                                                            {
                                                                                title:
                                                                                    'Распределено на сумму',
                                                                                dataIndex:
                                                                                    'distributed_amount',
                                                                                align:
                                                                                    'right',
                                                                                width:
                                                                                    180,

                                                                                render:
                                                                                    value => (
                                                                                        <b>
                                                                                            {Number(
                                                                                                value ||
                                                                                                0
                                                                                            ).toLocaleString(
                                                                                                'ru-RU'
                                                                                            )}{' '}
                                                                                            ₽
                                                                                        </b>
                                                                                    )
                                                                            },
                                                                            {
                                                                                title:
                                                                                    'Закрытие актами',
                                                                                dataIndex:
                                                                                    'supplier_closed_percent',
                                                                                width:
                                                                                    150,

                                                                                render:
                                                                                    value => (
                                                                                        <Progress
                                                                                            percent={
                                                                                                Number(
                                                                                                    value
                                                                                                ) ||
                                                                                                0
                                                                                            }
                                                                                            size="small"
                                                                                        />
                                                                                    )
                                                                            },
                                                                            {
                                                                                title:
                                                                                    'Статус',
                                                                                dataIndex:
                                                                                    'supplier_order_status',
                                                                                width:
                                                                                    120,

                                                                                render: value => (
                                                                                    <Tag
                                                                                        color={
                                                                                            value === 'approved'
                                                                                                ? 'green'
                                                                                                : value === 'rejected'
                                                                                                    ? 'red'
                                                                                                    : value === 'approval'
                                                                                                        ? 'blue'
                                                                                                        : 'default'
                                                                                        }
                                                                                    >
                                                                                        {getRegistryStatusLabel(value)}
                                                                                    </Tag>
                                                                                )
                                                                            }
                                                                        ]}
                                                                    />
                                                                ) : (
                                                                    <Alert
                                                                        type="info"
                                                                        showIcon
                                                                        message="Расходные строки ещё не распределены по заказам поставщика"
                                                                    />
                                                                )}
                                                            </>
                                                        )
                                                    }
                                                ]
                                                : []),
                                            
...(selectedRegistry.type === 'supplier_order'
    ? [
        {
            key: 'customer-orders',

            label: (
                <span>
                    Заказы покупателя{' '}
                    <Tag color="green">
                        {
                            selectedRegistry
                                .customer_coverage_by_orders
                                ?.length || 0
                        }
                    </Tag>
                </span>
            ),

            children: (
                <>
                    {/* КРАТКАЯ СВОДКА */}
                    {selectedRegistry
                        .customer_coverage_stats && (
                        <Row
                            gutter={[12, 12]}
                            align="middle"
                            style={{
                                marginBottom: 16
                            }}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                md={5}
                            >
                                <div
                                    style={{
                                        textAlign: 'center'
                                    }}
                                >
                                    <Progress
                                        type="circle"
                                        size={90}
                                        percent={
                                            Number(
                                                selectedRegistry
                                                    .customer_coverage_stats
                                                    .linked_rows_percent
                                            ) || 0
                                        }
                                        format={percent => (
                                            <span
                                                style={{
                                                    fontWeight: 700
                                                }}
                                            >
                                                {percent}%
                                            </span>
                                        )}
                                    />

                                    <div
                                        style={{
                                            marginTop: 6,
                                            fontSize: 12
                                        }}
                                    >
                                        Связано с заказами
                                        покупателя
                                    </div>
                                </div>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                md={9}
                            >
                                <Descriptions
                                    bordered
                                    size="small"
                                    column={1}
                                >
                                    <Descriptions.Item label="Строк заказа поставщика">
                                        {Number(
                                            selectedRegistry
                                                .customer_coverage_stats
                                                .total_expense_rows
                                        ) || 0}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Связано с заказами покупателя">
                                        {Number(
                                            selectedRegistry
                                                .customer_coverage_stats
                                                .linked_expense_rows
                                        ) || 0}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Без заказа покупателя">
                                        {Number(
                                            selectedRegistry
                                                .customer_coverage_stats
                                                .unlinked_expense_rows
                                        ) || 0}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Заказов покупателя">
                                        {Number(
                                            selectedRegistry
                                                .customer_coverage_stats
                                                .customer_orders_count
                                        ) || 0}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                md={10}
                            >
                                <Descriptions
                                    bordered
                                    size="small"
                                    column={1}
                                >
                                    <Descriptions.Item label="Сумма заказа поставщика">
                                        {Number(
                                            selectedRegistry
                                                .customer_coverage_stats
                                                .total_expense_amount ||
                                            0
                                        ).toLocaleString(
                                            'ru-RU'
                                        )}{' '}
                                        ₽
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Связано с заказами покупателя">
                                        {Number(
                                            selectedRegistry
                                                .customer_coverage_stats
                                                .linked_expense_amount ||
                                            0
                                        ).toLocaleString(
                                            'ru-RU'
                                        )}{' '}
                                        ₽
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Не связано">
                                        {Number(
                                            selectedRegistry
                                                .customer_coverage_stats
                                                .unlinked_expense_amount ||
                                            0
                                        ).toLocaleString(
                                            'ru-RU'
                                        )}{' '}
                                        ₽
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Связано по сумме">
                                        <Progress
                                            percent={
                                                Number(
                                                    selectedRegistry
                                                        .customer_coverage_stats
                                                        .linked_amount_percent
                                                ) || 0
                                            }
                                            size="small"
                                        />
                                    </Descriptions.Item>
                                </Descriptions>
                            </Col>
                        </Row>
                    )}

                    {/* ТАБЛИЦА ЗАКАЗОВ ПОКУПАТЕЛЯ */}
                    {Array.isArray(
                        selectedRegistry
                            .customer_coverage_by_orders
                    ) &&
                    selectedRegistry
                        .customer_coverage_by_orders
                        .length > 0 ? (
                        <Table
                            size="small"
                            rowKey="customer_order_id"
                            pagination={false}
                            scroll={{
                                x: 1100
                            }}
                            dataSource={
                                selectedRegistry
                                    .customer_coverage_by_orders
                            }
                            columns={[
                                {
                                    title:
                                        'Заказ покупателя',

                                    dataIndex:
                                        'customer_order_number',

                                    key:
                                        'customer_order_number',

                                    width: 170,

                                    render: (
                                        value,
                                        order
                                    ) => (
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() =>
                                                viewRegistryDetails({
                                                    id:
                                                        order
                                                            .customer_order_id
                                                })
                                            }
                                        >
                                            {value ||
                                                `ID ${order.customer_order_id}`}
                                        </Button>
                                    )
                                },
                                {
                                    title: 'Дата',

                                    dataIndex:
                                        'customer_order_date',

                                    key:
                                        'customer_order_date',

                                    width: 110,

                                    render: value =>
                                        value
                                            ? dayjs(
                                                value
                                            ).format(
                                                'DD.MM.YYYY'
                                            )
                                            : '—'
                                },
                                {
                                    title:
                                        'Покупатель',

                                    dataIndex:
                                        'counterparty_name',

                                    key:
                                        'counterparty_name',

                                    ellipsis: true
                                },
                                {
                                    title:
                                        'Связано строк',

                                    dataIndex:
                                        'matched_expense_rows',

                                    key:
                                        'matched_expense_rows',

                                    width: 120,
                                    align: 'right',

                                    render: value => (
                                        <b>
                                            {Number(
                                                value
                                            ) || 0}
                                        </b>
                                    )
                                },
                                {
                                    title:
                                        'Доля заказа поставщика',

                                    dataIndex:
                                        'supplier_order_share_percent',

                                    key:
                                        'supplier_order_share_percent',

                                    width: 180,

                                    render: value => (
                                        <Progress
                                            percent={
                                                Number(
                                                    value
                                                ) || 0
                                            }
                                            size="small"
                                        />
                                    )
                                },
                                {
                                    title:
                                        'Связанная сумма расходов',

                                    dataIndex:
                                        'matched_expense_amount',

                                    key:
                                        'matched_expense_amount',

                                    width: 185,
                                    align: 'right',

                                    render: value => (
                                        <b>
                                            {Number(
                                                value || 0
                                            ).toLocaleString(
                                                'ru-RU'
                                            )}{' '}
                                            ₽
                                        </b>
                                    )
                                },
                                {
                                    title:
                                        'Доходных строк покупателя',

                                    dataIndex:
                                        'matched_income_rows',

                                    key:
                                        'matched_income_rows',

                                    width: 180,
                                    align: 'right',

                                    render: value =>
                                        Number(value) || 0
                                },
                                {
                                    title:
                                        'Доходная сумма',

                                    dataIndex:
                                        'matched_income_amount',

                                    key:
                                        'matched_income_amount',

                                    width: 160,
                                    align: 'right',

                                    render: value =>
                                        `${Number(
                                            value || 0
                                        ).toLocaleString(
                                            'ru-RU'
                                        )} ₽`
                                },
                                {
                                    title:
                                        'Сумма заказа покупателя',

                                    dataIndex:
                                        'customer_order_total_amount',

                                    key:
                                        'customer_order_total_amount',

                                    width: 190,
                                    align: 'right',

                                    render: value =>
                                        `${Number(
                                            value || 0
                                        ).toLocaleString(
                                            'ru-RU'
                                        )} ₽`
                                },
                                {
                                    title: 'Статус',

                                    dataIndex:
                                        'customer_order_status',

                                    key:
                                        'customer_order_status',

                                    width: 130,

                                    render: value => (
                                        <Tag
                                            color={
                                                value === 'approved'
                                                    ? 'green'
                                                    : value === 'rejected'
                                                        ? 'red'
                                                        : value === 'approval'
                                                            ? 'blue'
                                                            : 'default'
                                            }
                                        >
                                            {getRegistryStatusLabel(value)}
                                        </Tag>
                                    )
                                },
                                {
    title: 'Акты покупателя',

    dataIndex: 'act_numbers',

    key: 'act_numbers',

    width: 240,

    render: (_, row) => {
        const actNumbers = String(
            row.act_numbers || ''
        )
            .split(',')
            .map(value => value.trim())
            .filter(Boolean);

        const actsCount =
            Number(row.acts_count) ||
            actNumbers.length;

        if (actsCount === 0) {
            return (
                <Tag>
                    Нет актов
                </Tag>
            );
        }

        const visibleActs =
            actNumbers.slice(0, 2);

        const hiddenCount = Math.max(
            actsCount - visibleActs.length,
            0
        );

        return (
            <Tooltip
                placement="topLeft"
                title={
                    <div>
                        <div
                            style={{
                                fontWeight: 700,
                                marginBottom: 6
                            }}
                        >
                            Акты заказа покупателя
                        </div>

                        {actNumbers.map(
                            (number, index) => (
                                <div
                                    key={`${number}-${index}`}
                                >
                                    {number}
                                </div>
                            )
                        )}
                    </div>
                }
            >
                <Space
                    size={[4, 4]}
                    wrap
                    style={{
                        cursor: 'help'
                    }}
                >
                    {visibleActs.map(
                        (number, index) => (
                            <Tag
                                key={`${number}-${index}`}
                                color="cyan"
                            >
                                {number}
                            </Tag>
                        )
                    )}

                    {hiddenCount > 0 && (
                        <Tag color="blue">
                            +{hiddenCount}
                        </Tag>
                    )}
                </Space>
            </Tooltip>
        );
    }
}
                            ]}
                        />
                    ) : (
                        <Alert
                            type="info"
                            showIcon
                            message="Связанные заказы покупателя не найдены"
                            description={
                                'Для задач текущего заказа поставщика ' +
                                'не найдены доходные строки, включённые ' +
                                'в заказы покупателя.'
                            }
                        />
                    )}
                </>
            )
        }
    ]
    : [])


                                        ]}
                                    />
                                </Card>
                            )}

                            {/* ПОИСК ПО СОСТАВУ */}
                            <Card
                                size="small"
                                style={{ marginBottom: 16 }}
                            >
                                <Row
                                    gutter={[12, 12]}
                                    align="middle"
                                >
                                    <Col flex="auto">
                                        <Input
                                            allowClear
                                            value={registryViewSearch}
                                            prefix={<SearchOutlined />}
                                            placeholder={
                                                'Поиск по № ТТ или ID через запятую: 91897, T2WO06705967'
                                            }
                                            onChange={event => {
                                                    setRegistryViewSearch(
                                                        event.target.value
                                                    );
                                                    setRegistryViewPage(1);
                                                }
                                            }
                                        />
                                    </Col>

                                    <Col>
                                        <Select
                                            value={
                                                registryViewClosureFilter
                                            }
                                            style={{ width: 190 }}
                                            onChange={value => {
                                                setRegistryViewClosureFilter(
                                                    value
                                                );

                                                setRegistryViewPage(1);
                                            }}
                                            options={[
                                                {
                                                    value: 'all',
                                                    label: 'Все строки'
                                                },
                                                {
                                                    value: 'closed',
                                                    label: 'Закрытые актами'
                                                },
                                                {
                                                    value: 'open',
                                                    label: 'Не закрытые'
                                                }
                                            ]}
                                        />
                                    </Col>

                                    <Col>
                                        <Text type="secondary">
                                            Задач:{' '}
                                            <b>
                                                {
                                                    filteredGroupedRegistryItems
                                                        .length
                                                }
                                            </b>
                                            {' · '}
                                            Строк:{' '}
                                            <b>
                                                {
                                                    filteredRegistryRowsCount
                                                }
                                            </b>
                                        </Text>
                                    </Col>
                                </Row>
                            </Card>

                            <Divider orientation="left">
                                Состав документа
                            </Divider>

                            {/* ОТФИЛЬТРОВАННЫЙ СОСТАВ */}
                            {paginatedGroupedRegistryItems.length > 0 ? (
    <>
        {paginatedGroupedRegistryItems.map(
            group => {
                                        const taskLinkedSupplierOrders =
                                            [
                                                ...new Set(
                                                    group.items
                                                        .flatMap(item =>
                                                            String(
                                                                item.invoice
                                                                    ?.linked_supplier_order_numbers ||
                                                                ''
                                                            )
                                                                .split(',')
                                                                .map(value =>
                                                                    value.trim()
                                                                )
                                                                .filter(
                                                                    Boolean
                                                                )
                                                        )
                                                )
                                            ].join(', ');

                                        const isOutsidePeriod =
                                            periodOutsideTaskIds.has(
                                                Number(group.dbId)
                                            );

                                        return (
                                            <Card
                                                key={
                                                    group.dbId ||
                                                    group.extId
                                                }
                                                size="small"
                                                style={{
                                                    marginBottom: 16,
                                                    border:
                                                        isOutsidePeriod
                                                            ? '2px solid #ff4d4f'
                                                            : '1px solid #d9d9d9',
                                                    background:
                                                        isOutsidePeriod
                                                            ? '#fff1f0'
                                                            : undefined
                                                }}
                                                title={
                                                    <Space
                                                        wrap
                                                        size={6}
                                                    >
                                                        <Tag color="blue">
                                                            #
                                                            {group.extId ||
                                                                group.dbId ||
                                                                '—'}
                                                        </Tag>
                                                        {isOutsidePeriod && (
                                                            <Tooltip title={`Период документа: ${formatRegistryPeriod(selectedRegistry)}`}>
                                                                <Tag color="red">
                                                                    {group.task?.closed_at
                                                                        ? `Закрыта ${dayjs(group.task.closed_at).format('DD.MM.YYYY')} — вне периода`
                                                                        : 'Нет даты закрытия'}
                                                                </Tag>
                                                            </Tooltip>
                                                        )}
                                                        {getTaskVal(
                                                            group.task,
                                                            'Nomer_TT'
                                                        ) !== '-' && (
                                                            <Tag color="geekblue">
                                                                ТТ:{' '}
                                                                {getTaskVal(
                                                                    group.task,
                                                                    'Nomer_TT'
                                                                )}
                                                            </Tag>
                                                        )}
                                                        <TaskDistributionInfo
                                                            group={group}
                                                        />

                                                        <Text strong>
                                                            {group.task
                                                                ?.title ||
                                                                'Без названия'}
                                                        </Text>

                                                        {selectedRegistry.type ===
                                                            'customer_order' && (
                                                            <>
                                                                <Divider
                                                                    type="vertical"
                                                                    style={{
                                                                        margin:
                                                                            '0 4px'
                                                                    }}
                                                                />

                                                                {taskLinkedSupplierOrders ? (
                                                                    <Tag
                                                                        color="orange"
                                                                        icon={
                                                                            <SyncOutlined />
                                                                        }
                                                                    >
                                                                        Заказано:
                                                                        №
                                                                        {
                                                                            taskLinkedSupplierOrders
                                                                        }
                                                                    </Tag>
                                                                ) : (
                                                                    <Tag
                                                                        style={{
                                                                            fontStyle:
                                                                                'italic'
                                                                        }}
                                                                    >
                                                                        Не передано
                                                                        поставщику
                                                                    </Tag>
                                                                )}
                                                            </>
                                                        )}
                                                    </Space>
                                                }
                                                extra={
    <Space size={4}>
        {!isSelectedRegistryAct ? (
            <Button
                type="link"
                icon={
                    <EditOutlined />
                }
                onClick={() =>
                    openFullTaskModal(
                        group.dbId,
                        group.extId,
                        selectedRegistry
                    )
                }
            >
                Редактировать ВДР
            </Button>
        ) : (
            <Tooltip title="В акте редактирование ВДР недоступно">
                <Tag color="default">
                    Только просмотр
                </Tag>
            </Tooltip>
        )}

        {!isSelectedRegistryAct && (
            <Popconfirm
                title="Отвязать строки от документа?"
                description={
                    `Строки ВДР останутся в задаче. Из документа будут убраны ${group.items?.length || 0} строк.`
                }
                okText="Отвязать"
                cancelText="Отмена"
                okButtonProps={{
                    danger: true
                }}
                onConfirm={() =>
                    removeVdrGroupFromRegistry(
                        group
                    )
                }
            >
                <Button
                    type="link"
                    danger
                    icon={
                        <DeleteOutlined />
                    }
                    loading={
                        removingVdrGroupKey ===
                            (
                                group.dbId ||
                                group.extId
                            )
                    }
                >
                    Отвязать строки от документа
                </Button>
            </Popconfirm>
        )}
    </Space>
}
                                            >
                                                <Table
                                                    rowKey="id"
                                                    size="small"
                                                    dataSource={
                                                        group.items
                                                    }
                                                    rowClassName={item => {
                                                        const state =
                                                            getRegistryItemDistributionState(
                                                                item,
                                                                selectedRegistry?.type
                                                            );

                                                        return state.unallocated
                                                            ? 'registry-unallocated-row'
                                                            : '';
                                                    }}
                                                    pagination={false}
                                                    scroll={{
                                                        x: 800
                                                    }}
                                                    columns={[
                                                        {
                                                            title: 'Акт',
                                                            width: 130,

                                                            render: (
                                                                _,
                                                                row
                                                            ) => {
                                                                const number =
                                                                    selectedRegistry.type ===
                                                                    'supplier_order'
                                                                        ? row
                                                                            .invoice
                                                                            ?.linked_supplier_act
                                                                        : row
                                                                            .invoice
                                                                            ?.linked_customer_act;

                                                                return number ? (
                                                                    <Tag
                                                                        color="cyan"
                                                                        style={{
                                                                            fontSize:
                                                                                10
                                                                        }}
                                                                    >
                                                                        {
                                                                            number
                                                                        }
                                                                    </Tag>
                                                                ) : (
                                                                    <Text type="secondary">
                                                                        —
                                                                    </Text>
                                                                );
                                                            }
                                                        },
                                                        {
                                                            title:
                                                                'Наименование / комментарий',

                                                            render: (
                                                                _,
                                                                row
                                                            ) => (
                                                                <div>
                                                                    <div>
                                                                        {row
                                                                            .invoice
                                                                            ?.rate_name ||
                                                                            '—'}
                                                                    </div>

                                                                    {row
                                                                        .invoice
                                                                        ?.article && (
                                                                        <div
                                                                            style={{
                                                                                fontSize:
                                                                                    11,
                                                                                color:
                                                                                    '#888'
                                                                            }}
                                                                        >
                                                                            {
                                                                                row
                                                                                    .invoice
                                                                                    .article
                                                                            }
                                                                        </div>
                                                                    )}

                                                                    {row
                                                                        .invoice
                                                                        ?.note && (
                                                                        <div
                                                                            style={{
                                                                                fontStyle:
                                                                                    'italic',
                                                                                color:
                                                                                    'orange',
                                                                                fontSize:
                                                                                    11
                                                                            }}
                                                                        >
                                                                            КП:{' '}
                                                                            {
                                                                                row
                                                                                    .invoice
                                                                                    .note
                                                                            }
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        },
                                                        {
                                                            title:
                                                                'Кол-во',
                                                            dataIndex: [
                                                                'invoice',
                                                                'quantity'
                                                            ],
                                                            align:
                                                                'center',
                                                            width: 80
                                                        },
                                                        {
                                                            title: 'Цена',
                                                            dataIndex: [
                                                                'invoice',
                                                                'price'
                                                            ],
                                                            align:
                                                                'right',
                                                            width: 125,

                                                            render: value =>
                                                                `${Number(
                                                                    value ||
                                                                    0
                                                                ).toLocaleString(
                                                                    'ru-RU'
                                                                )} ₽`
                                                        },
                                                        {
                                                            title: 'Сумма',
                                                            dataIndex: [
                                                                'invoice',
                                                                'amount'
                                                            ],
                                                            align:
                                                                'right',
                                                            width: 135,

                                                            render: value => (
                                                                <b>
                                                                    {Number(
                                                                        value ||
                                                                        0
                                                                    ).toLocaleString(
                                                                        'ru-RU'
                                                                    )}{' '}
                                                                    ₽
                                                                </b>
                                                            )
                                                        },
                                                        !isSelectedRegistryAct && {
    title: '',
    width: 50,

    render: (_, row) => (
        <Tooltip title="Редактировать строку ВДР">
            <Button
                type="text"
                icon={
                    <EditOutlined
                        style={{
                            color:
                                '#1890ff'
                        }}
                    />
                }
                onClick={() =>
                    openVdrEditModal(
                        row
                    )
                }
            />
        </Tooltip>
    )
},

                                                        isEditMode && {
                                                            title: '',
                                                            width: 50,

                                                            render: (
                                                                _,
                                                                row
                                                            ) => (
                                                                <Popconfirm
                                                                    title="Удалить из отчёта?"
                                                                    onConfirm={() =>
                                                                        removeItemFromRegistry(
                                                                            row.id
                                                                        )
                                                                    }
                                                                >
                                                                    <Button
                                                                        type="text"
                                                                        danger
                                                                        icon={
                                                                            <DeleteOutlined />
                                                                        }
                                                                    />
                                                                </Popconfirm>
                                                            )
                                                        }
                                                    ].filter(Boolean)}
                                                />

                                                <div
                                                    style={{
                                                        textAlign: 'right',
                                                        marginTop: 8,
                                                        paddingRight: 50
                                                    }}
                                                >
                                                    <Text type="secondary">
                                                        Итого по найденным
                                                        строкам задачи:{' '}
                                                    </Text>

                                                    <Text strong>
                                                        {Number(
                                                            group.total ||
                                                            0
                                                        ).toLocaleString(
                                                            'ru-RU'
                                                        )}{' '}
                                                        ₽
                                                    </Text>
                                                </div>
                                            </Card>
                                          );
                                    }
                                )}

        {filteredGroupedRegistryItems.length >
            registryViewPageSize && (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 20,
                    marginBottom: 12
                }}
            >
                <Pagination
                    current={registryViewPage}
                    pageSize={registryViewPageSize}
                    total={
                        filteredGroupedRegistryItems.length
                    }
                    showSizeChanger
                    pageSizeOptions={[
                        5,
                        10,
                        20,
                        50
                    ]}
                    showTotal={total =>
                        `Всего задач: ${total}`
                    }
                    onChange={(
                        page,
                        pageSize
                    ) => {
                        setRegistryViewPage(
                            page
                        );

                        setRegistryViewPageSize(
                            pageSize
                        );
                    }}
                />
            </div>
        )}
    </>
) : (
    <Alert
                                    type="warning"
                                    showIcon
                                    message="По заданным условиям строки не найдены"
                                    action={
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setRegistryViewSearch('');
                                                setRegistryViewClosureFilter(
                                                    'all'
                                                );
                                            }}
                                        >
                                            Сбросить поиск
                                        </Button>
                                    }
                                />
                            )}

                            <RegistryHistoryCard
                                history={registryHistory}
                            />
                        </div>
                    )}
                    </div>
                    </Spin>
                </Modal>

                <Modal
    title={
        <Space>
            <FileExcelOutlined />

            <span>
                Выберите формат Excel
            </span>
        </Space>
    }

    open={
        exportFormatModalOpen
    }

    onCancel={() => {
        if (
            !exportLoading
        ) {
            setExportFormatModalOpen(
                false
            );
        }
    }}

    onOk={
        confirmRegistryExport
    }

    okText="Скачать"

    cancelText="Отмена"

    confirmLoading={
        exportLoading
    }

    maskClosable={
        !exportLoading
    }

    closable={
        !exportLoading
    }

    destroyOnClose
>
    <Space
        direction="vertical"
        size="middle"
        style={{
            width:
                '100%'
        }}
    >
        <Text type="secondary">
            Выберите структуру и оформление
            выгружаемого файла.
        </Text>

        <Select
            value={
                selectedExportFormat
            }

            onChange={
                setSelectedExportFormat
            }

            style={{
                width:
                    '100%'
            }}

            options={[
    {
        value:
            'daily_report',

        label:
            'Ежедневный отчёт'
    },

    {
        value:
            'customer_order',

        label:
            'Заказ покупателя',

        disabled:
            selectedRegistry
                ?.type !==
            'customer_order'
    },

    {
        value:
            'customer_order_with_note',

        label:
            '_ с примечанием',

        disabled:
            selectedRegistry
                ?.type !==
            'customer_order'
    },

    {
        value:
            'customer_order_name_km_inline',

        label:
            'название в км в строку',

        disabled:
            selectedRegistry
                ?.type !==
            'customer_order'
    },

    {
        value:
            'customer_order_with_note_km_inline',

        label:
            '_ с примечанием + км в строку',

        disabled:
            selectedRegistry
                ?.type !==
            'customer_order'
    },

    {
        value:
            'supplier_order',

        label:
            'Заказ поставщика',

        disabled:
            selectedRegistry
                ?.type !==
            'supplier_order'
    }
]}
        />

        {selectedExportFormat ===
            'daily_report' && (
            <Alert
                type="info"
                showIcon
                message="Ежедневный отчёт"
                description={
                    'Текущий реализованный формат сводного ежедневного отчёта.'
                }
            />
        )}

        {(
            selectedExportFormat ===
                'customer_order' ||
            selectedExportFormat ===
                'customer_order_with_note' ||
            selectedExportFormat ===
                'customer_order_name_km_inline' ||
            selectedExportFormat ===
                'customer_order_with_note_km_inline'
        ) && (
            <Alert
                type="success"
                showIcon
                message={
                    selectedExportFormat ===
                    'customer_order_with_note'
                        ? '_ с примечанием'
                        : selectedExportFormat ===
                          'customer_order_name_km_inline'
                        ? 'название в км в строку'
                        : selectedExportFormat ===
                          'customer_order_with_note_km_inline'
                        ? '_ с примечанием + км в строку'
                        : 'Заказ покупателя'
                }
                description={
                    selectedExportFormat ===
                    'customer_order_with_note'
                        ? 'Файл формируется по шаблону zakaz_com.xlsx: работы с 8 строки, нумерация в столбце B, примечание в столбце AK.'
                        : selectedExportFormat ===
                          'customer_order_name_km_inline'
                        ? 'Файл формируется по шаблону zakaz_line.xlsx: км 1.1.2 пишутся в строку работ заявки.'
                        : selectedExportFormat ===
                          'customer_order_with_note_km_inline'
                        ? 'Файл формируется по шаблону zakaz_com_line.xlsx: примечание в AK, км 1.1.2 в строку работ заявки, стоимость км с учётом % и штрафа.'
                        : 'Файл будет сформирован по приложенному Excel-шаблону с сохранением цветов, границ, размеров, объединений и двух листов.'
                }
            />
        )}

        {selectedExportFormat ===
            'supplier_order' && (
            <Alert
                type="success"
                showIcon
                message="Заказ поставщика"
                description={
                    'Пока используется тот же шаблон, что и для заказа покупателя. В файл попадут расходные строки ВДР. Позже шаблон и структура могут быть заменены отдельно.'
                }
            />
        )}
    </Space>
</Modal>

                <Modal
    title="Создание заказов поставщикам"
    open={supplierBasisModal}
    onCancel={() => {
        setSupplierBasisModal(false);
        setSelectedBasisKeys([]);
    }}
    width={1050}
    footer={[
        <Button
            key="cancel"
            onClick={() => {
                setSupplierBasisModal(false);
                setSelectedBasisKeys([]);
            }}
        >
            Отмена
        </Button>,

        <Button
            key="order"
            type="primary"
            disabled={
                selectedBasisKeys.length === 0
            }
            onClick={() =>
                confirmCreateSuppliers(false)
            }
        >
            Создать только заказы (
            {selectedBasisKeys.length})
        </Button>,

        <Button
            key="both"
            type="primary"
            danger
            disabled={
                selectedBasisKeys.length === 0
            }
            onClick={() =>
                confirmCreateSuppliers(true)
            }
        >
            Заказы + акты (
            {selectedBasisKeys.length})
        </Button>
    ]}
>
    <Alert
        message={
            'Заказы будут созданы только по свободным строкам ВДР'
        }
        description={
            'Если часть строк поставщика уже включена в другой заказ, ' +
            'можно сформировать новый заказ по оставшимся строкам.'
        }
        type="info"
        showIcon
        style={{
            marginBottom: 16
        }}
    />

    <Table
        dataSource={basisSuppliers}
        rowKey={record =>
            record.key || record.name
        }
        size="small"
        pagination={false}
        scroll={{
            x: 950
        }}
        rowSelection={{
            selectedRowKeys:
                selectedBasisKeys,

            onChange: keys =>
                setSelectedBasisKeys(
                    keys
                ),

            getCheckboxProps: record => ({
                disabled:
                    Number(
                        record.availableRows
                    ) === 0,

                name:
                    record.name
            })
        }}
        expandable={{
            expandedRowRender: record => (
                <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={
                        record.items || []
                    }
                    columns={[
                        {
                            title: 'Задача',
                            width: 120,
                            render: (_, item) =>
                                item.task
                                    ?.external_id ||
                                item.task_id ||
                                '—'
                        },
                        {
                            title:
                                'Наименование',
                            dataIndex:
                                'rate_name'
                        },
                        {
                            title: 'Сумма',
                            dataIndex:
                                'amount',
                            align: 'right',
                            width: 140,
                            render: value =>
                                `${Number(
                                    value || 0
                                ).toLocaleString(
                                    'ru-RU'
                                )} ₽`
                        },
                        {
                            title: 'Заказ',
                            width: 220,
                            render: (_, item) => {
                                if (
                                    !item.is_used
                                ) {
                                    return (
                                        <Tag color="green">
                                            Свободна
                                        </Tag>
                                    );
                                }

                                return (
                                    <Tooltip
                                        title={
                                            item
                                                .existing_orders
                                                ?.join(
                                                    ', '
                                                )
                                        }
                                    >
                                        <Tag color="orange">
                                            {
                                                item
                                                    .existing_orders
                                                    ?.join(
                                                        ', '
                                                    )
                                            }
                                        </Tag>
                                    </Tooltip>
                                );
                            }
                        }
                    ]}
                    rowClassName={item =>
                        item.is_used
                            ? 'supplier-basis-used-row'
                            : ''
                    }
                />
            )
        }}
        columns={[
            {
                title: 'Поставщик',
                dataIndex: 'name',
                key: 'name',
                width: 230,

                render: (
                    text,
                    record
                ) => (
                    <Text
                        strong={
                            Number(
                                record
                                    .availableRows
                            ) > 0
                        }
                    >
                        {text}
                    </Text>
                )
            },
            {
                title: 'Всего строк',
                dataIndex:
                    'totalRows',
                key: 'totalRows',
                align: 'center',
                width: 100,

                render: value => (
                    <Tag>
                        {Number(value) || 0}
                    </Tag>
                )
            },
            {
                title: 'Уже в заказах',
                dataIndex:
                    'usedRows',
                key: 'usedRows',
                align: 'center',
                width: 120,

                render: value => (
                    <Tag color="orange">
                        {Number(value) || 0}
                    </Tag>
                )
            },
            {
                title: 'Свободно',
                dataIndex:
                    'availableRows',
                key: 'availableRows',
                align: 'center',
                width: 100,

                render: value => (
                    <Tag
                        color={
                            Number(value) > 0
                                ? 'green'
                                : 'default'
                        }
                    >
                        {Number(value) || 0}
                    </Tag>
                )
            },
            {
                title:
                    'Свободная сумма',

                dataIndex:
                    'availableAmount',

                key:
                    'availableAmount',

                align: 'right',
                width: 150,

                render: value => (
                    <b>
                        {Number(
                            value || 0
                        ).toLocaleString(
                            'ru-RU'
                        )}{' '}
                        ₽
                    </b>
                )
            },
            {
                title: 'Распределение',
                dataIndex:
                    'coveragePercent',
                key:
                    'coveragePercent',
                width: 150,

                render: value => (
                    <Progress
                        percent={
                            Number(value) || 0
                        }
                        size="small"
                    />
                )
            },
            {
                title:
                    'Существующие заказы',

                dataIndex:
                    'existingOrders',

                key:
                    'existingOrders',

                width: 210,

                render: value => {
                    const orders =
                        Array.isArray(value)
                            ? value
                            : [];

                    if (
                        orders.length === 0
                    ) {
                        return '—';
                    }

                    return (
                        <Tooltip
                            title={
                                orders.join(', ')
                            }
                        >
                            <Space
                                size={[4, 4]}
                                wrap
                            >
                                {orders
                                    .slice(0, 2)
                                    .map(order => (
                                        <Tag
                                            key={order}
                                            color="blue"
                                        >
                                            {order}
                                        </Tag>
                                    ))}

                                {orders.length >
                                    2 && (
                                    <Tag>
                                        +
                                        {
                                            orders.length -
                                            2
                                        }
                                    </Tag>
                                )}
                            </Space>
                        </Tooltip>
                    );
                }
            },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                align: 'center',
                width: 150,

                render: status => {
                    if (
                        status ===
                        'fully_created'
                    ) {
                        return (
                            <Tag color="default">
                                Всё распределено
                            </Tag>
                        );
                    }

                    if (
                        status ===
                        'partially_created'
                    ) {
                        return (
                            <Tag color="orange">
                                Частично
                            </Tag>
                        );
                    }

                    return (
                        <Tag color="green">
                            Свободен
                        </Tag>
                    );
                }
            }
        ]}
    />
</Modal>

                <Modal
                    title="Быстрое редактирование строки ВДР"
                    open={vdrEditModalVisible}
                    onCancel={() => setVdrEditModalVisible(false)}
                    onOk={saveVdrEdit}
                    width={500}
                    destroyOnClose
                >
                    {editingVdrItem && (
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ padding: '10px', background: '#f5f5f5', borderRadius: '4px', marginBottom: 15 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>Наименование работы:</Text>
                                <div style={{ fontWeight: 'bold' }}>{editingVdrItem.invoice?.rate_name}</div>
                                <div style={{ marginTop: 8 }}>
                                    <span>Цена: <b>{parseFloat(editingVdrItem.invoice?.price).toLocaleString()} р.</b></span>
                                </div>
                            </div>

                            <Form form={vdrEditForm} layout="vertical">
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item name="quantity" label="Количество" rules={[{ required: true }]}>
                                            <InputNumber style={{ width: '100%' }} step={0.01} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name="percent" label="Доля %" rules={[{ required: true }]}>
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                {...percentInputNumberWithSuffixProps}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name="penalty" label="Штраф %">
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                min={0}
                                                max={100}
                                                formatter={value => `${value}%`}
                                                parser={value => value.replace('%', '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>

                            <Alert
                                message="Сумма будет пересчитана автоматически после сохранения"
                                type="info"
                                showIcon
                            />
                        </div>
                    )}
                </Modal>

                {/* БОЛЬШОЕ ОКНО РЕДАКТИРОВАНИЯ (ПОЛНЫЙ ФУНКЦИОНАЛ) */}
                <Modal
                title={
    <Space
        style={{
            width: '95%',
            justifyContent:
                'space-between'
        }}
    >
        <Space>
            <span>
                ВДР задачи #
                {activeTaskExtId}
            </span>

            {taskModalRegistryContext?.id && (
                <Tag color="blue">
                    {taskModalRegistryContext
                        .type ===
                    'customer_order'
                        ? 'Заказ покупателя'
                        : 'Заказ поставщика'}{' '}
                    №
                    {taskModalRegistryContext
                        .number ||
                        taskModalRegistryContext
                            .id}
                </Tag>
            )}
        </Space>

        <div style={{fontSize: '14px'}}>
                                Рентабельность: {(() => {
                                const inc = taskInvoices.income.reduce((s, i) => s + (i.include ? parseFloat(i.amount || 0) : 0), 0);
                                const exp = taskInvoices.expense.reduce((s, i) => s + (i.include ? parseFloat(i.amount || 0) : 0), 0);
                                const res = inc > 0 ? Math.round(((inc - exp) / inc) * 100) : 0;
                                return <Tag color={res < 20 ? 'red' : 'green'}>{res}%</Tag>;
                            })()}
                            </div>
    </Space>
}
                   
                    open={taskModalOpen}
                    onCancel={closeFullTaskModal}
                    width="98%"
                    style={{ top: 20 }}
                    footer={[
                        <Button key="cancel"  onClick={closeFullTaskModal}>Отмена</Button>,
                        <Button
                            key="save"
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={saveTaskVdrChanges}
                            danger={hasUnsavedChanges}
                        >
                            {hasUnsavedChanges ? 'Сохранить изменения *' : 'Данные сохранены'}
                        </Button>
                    ]}
                >
                    {taskMetadataLoading ? <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div> : (
                        <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <Alert
                                message="Внимание: вы редактируете первичные данные ВДР задачи. Это повлияет на все документы, где она участвует."
                                type="warning" showIcon style={{marginBottom: 16}}
                            />

                            {/* Инфо-панель */}
                            <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                                <Col span={6}><Card size="small" title="БС"><b>{getTaskVal(taskData, 'Nomer_BS')}</b></Card></Col>
                                <Col span={6}><Card size="small" title="ТТ"><b>{getTaskVal(taskData, 'Nomer_TT')}</b></Card></Col>
                                <Col span={6}><Card size="small" title="Исполнитель">{taskData?.assigned_user_fio}</Card></Col>
                                <Col span={6}>
                                    <Card size="small" title="Статус ВДР">
                                        <Select
                                            style={{width:'100%'}}
                                            value={taskData?.status_invoices || 'new'}
                                            options={TASK_STATUS_OPTIONS}
                                            onChange={async (val) => {
                                                const token = localStorage.getItem('token');
                                                await axios.put(`/api/tasks/${activeTaskDbId}/status`, { status: val }, { headers: { 'Authorization': `Bearer ${token}` } });
                                                message.success('Статус ВДР обновлен');
                                            }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Form form={editInvoiceForm} component={false}>
                                <Collapse defaultActiveKey={['income', 'expense']}>
                                    {['income', 'expense'].map(type => (
                                        <Panel
                                            key={type}
                                            header={
                                                <div style={{display:'flex', justifyContent:'space-between', width:'100%', paddingRight: 20}}>
                                                    <b>{type === 'income' ? 'Доходы (Заказчик)' : 'Расходы (Подрядчик / Свои)'}</b>
                                                    <Button size="small" type="primary" icon={<PlusOutlined />} onClick={(e) => { e.stopPropagation(); handleAddNewTaskInvoice(type); }}>Добавить строку</Button>
                                                </div>
                                            }
                                        >
                                            <Table
                                                size="small"
                                                dataSource={taskInvoices[type]}
                                                columns={getTaskInvoiceColumns(editInvoiceForm, type)}
                                                pagination={false}
                                                rowKey="id"
                                                scroll={{ x: 1380 }}
                                            />
                                        </Panel>
                                    ))}
                                    <Panel header="Комментарии и История" key="comments">
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <TextArea
                                                    rows={4}
                                                    value={taskComments}
                                                    onChange={e => { setTaskComments(e.target.value); setHasUnsavedChanges(true); }}
                                                    placeholder="Комментарий к расчету..."
                                                />
                                            </Col>
                                            <Col span={12}>
                                                <div style={{ height: 120, overflowY: 'auto', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                                                    {commentHistory.map((c, i) => (
                                                        <div key={i} style={{fontSize: 11, borderBottom: '1px solid #ddd', padding: '4px 0'}}>
                                                            <Text type="secondary" style={{fontSize: 9}}>{dayjs(c.created_at).format('DD.MM HH:mm')}</Text> <b>{c.user_name}:</b> {c.text}
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                    </Panel>
                                </Collapse>
                            </Form>
                        </div>
                    )}
                </Modal>
            {/* МАСТЕР СОЗДАНИЯ */}
            {/* Постоянная полоса прокрутки: без неё при высоте мастера ≈ высоте окна она то появляется, то исчезает, и окно дёргается */}
            <Modal title="Мастер формирования отчета" open={isModalVisible} onCancel={() => setIsModalVisible(false)} width="98%" footer={null} destroyOnClose styles={{ wrapper: { overflowY: 'scroll' } }}>
                <Form form={modalForm} layout="vertical" preserve={true}>
                    <Steps current={currentStep} items={[{ title: 'Поиск заданий' }, { title: 'Выбор позиций ВДР' }]} style={{ marginBottom: 25 }} />
                    <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                        <div style={{ background: '#f5f5f5', padding: '15px 20px', borderRadius: 8, marginBottom: 20 }}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Text type="secondary">
                                        Внешние ID (до 4000 символов):
                                    </Text>
                                    <TextArea
                                        allowClear
                                        rows={3}
                                        maxLength={4000}
                                        showCount
                                        placeholder="91897, 93303, …"
                                        value={apiFilters.external_ids}
                                        onChange={event =>
                                            handleFilterChange(
                                                'external_ids',
                                                event.target.value
                                            )
                                        }
                                    />
                                </Col>

                                <Col span={12}>
                                    <Text type="secondary">
                                        Номера ТТ (до 4000 символов):
                                    </Text>
                                    <TextArea
                                        allowClear
                                        rows={3}
                                        maxLength={4000}
                                        showCount
                                        placeholder="T2WO06705967, T2WO06714049, …"
                                        value={apiFilters.nomer_tt}
                                        onChange={event =>
                                            handleFilterChange(
                                                'nomer_tt',
                                                event.target.value
                                            )
                                        }
                                    />
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                <Col span={4}><Text type="secondary">Объект (БС):</Text><Select mode="multiple" style={{width:'100%'}} options={options.objects} value={apiFilters.object_id} onChange={v => handleFilterChange('object_id', v)} showSearch maxTagCount="responsive" /></Col>
                                <Col span={4}><Text type="secondary">Заказчик (Регион):</Text><Select style={{width:'100%'}} options={options.regions} value={apiFilters.region} onChange={v => handleFilterChange('region', v)} showSearch /></Col>
                                <Col span={4}><Text type="secondary">Вид работ:</Text><Select mode="multiple" style={{width:'100%'}} options={options.types} value={apiFilters.news_type_name} onChange={v => handleFilterChange('news_type_name', v)} showSearch /></Col>
                                <Col span={4}><Text type="secondary">Исполнитель:</Text><Select mode="multiple" style={{width:'100%'}} options={options.users} value={apiFilters.assigned_user_fio} onChange={v => handleFilterChange('assigned_user_fio', v)} showSearch /></Col>
                            </Row>

                            <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                <Col span={4}><Text type="secondary">Заполнил ВДР:</Text><Select mode="multiple" style={{width:'100%'}} options={options.fillers} value={apiFilters.filling_user} onChange={v => handleFilterChange('filling_user', v)} showSearch /></Col>
                            </Row>
                            <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                <Col span={4}><Text type="secondary">Этапы:</Text><Select mode="multiple" style={{width:'100%'}} options={options.stages} value={apiFilters.onlyStatus} onChange={v => handleFilterChange('onlyStatus', v)} /></Col>
                                <Col span={4}><Text type="secondary">Стадия:</Text><Select mode="multiple" style={{width:'100%'}} options={[{value:1,label:'В работе'},{value:2,label:'Завершено'}]} value={apiFilters.stage} onChange={v => handleFilterChange('stage', v)} /></Col>
                                <Col span={4}><Text type="secondary">Статус ВДР:</Text><Select mode="multiple" style={{width:'100%'}} options={TASK_STATUS_OPTIONS} value={apiFilters.status_invoices} onChange={v => handleFilterChange('status_invoices', v)} /></Col>
                                <Col span={6}><Text type="secondary">Рентабельность (%):</Text><div style={{display:'flex', gap: 8}}><InputNumber placeholder="От" value={apiFilters.minProfitability} onChange={v => handleFilterChange('minProfitability', v)} /><InputNumber placeholder="До" value={apiFilters.maxProfitability} onChange={v => handleFilterChange('maxProfitability', v)} /></div></Col>
                                <Col span={6} style={{textAlign:'right', paddingTop: 22}}><Space><Button type="primary" icon={<SyncOutlined />} onClick={handleSearchTasks} loading={loading}>Найти</Button><Button danger icon={<ClearOutlined />} onClick={() => { setApiFilters({ ...INITIAL_API_FILTERS }); setTasksSource([]); }}>Сбросить</Button></Space></Col>
                            </Row>
                            <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                <Col span={4}><Text type="secondary">Дата создания:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geCreated, apiFilters.leCreated]} onChange={v => { handleFilterChange('geCreated', v?.[0]); handleFilterChange('leCreated', v?.[1]); }} /></Col>
                                <Col span={4}><Text type="secondary">Покидание сайта:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geExit, apiFilters.leExit]} onChange={v => { handleFilterChange('geExit', v?.[0]); handleFilterChange('leExit', v?.[1]); }} /></Col>
                                <Col span={4}><Text type="secondary">Обновление:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geUpdated, apiFilters.leUpdated]} onChange={v => { handleFilterChange('geUpdated', v?.[0]); handleFilterChange('leUpdated', v?.[1]); }} /></Col>
                                <Col span={4}>
        <Text type="secondary">
            Дата закрытия:
        </Text>

        <RangePicker
            showTime
            style={{
                width: '100%'
            }}
            format="DD.MM.YYYY HH:mm"
            value={[
                apiFilters.geClosed,
                apiFilters.leClosed
            ]}
            placeholder={[
                'Закрыта с',
                'Закрыта по'
            ]}
            onChange={value => {
                handleFilterChange(
                    'geClosed',
                    value?.[0] || null
                );

                handleFilterChange(
                    'leClosed',
                    value?.[1] || null
                );
            }}
        />
    </Col>
                                <Col span={3}>
                                    <Text type="secondary">Заказ пок.:</Text>
                                    <Select
                                        placeholder="Все"
                                        style={{ width: '100%' }}
                                        allowClear
                                        value={apiFilters.has_customer_order}
                                        onChange={v => handleFilterChange('has_customer_order', v)}
                                        options={[{label:'Да', value:'yes'}, {label:'Нет', value:'no'}]}
                                    />
                                </Col>
                                <Col span={3}>
                                    <Text type="secondary">Заказ пост.:</Text>
                                    <Select
                                        placeholder="Все"
                                        style={{ width: '100%' }}
                                        allowClear
                                        value={apiFilters.has_supplier_order}
                                        onChange={v => handleFilterChange('has_supplier_order', v)}
                                        options={[{label:'Да', value:'yes'}, {label:'Нет', value:'no'}]}
                                    />
                                </Col>
                                <Col span={3}>
                                    <Text type="secondary">Еж. отчет:</Text>
                                    <Select
                                        placeholder="Все"
                                        style={{ width: '100%' }}
                                        allowClear
                                        value={apiFilters.has_daily_report}
                                        onChange={v => handleFilterChange('has_daily_report', v)}
                                        options={[{label:'Да', value:'yes'}, {label:'Нет', value:'no'}]}
                                    />
                                </Col>
                            </Row>
                        </div>
                        {/* Перед таблицей переключатель */}
                        <div style={{ marginBottom: 10, textAlign: 'right', background: '#fff', padding: '5px 10px', borderRadius: '4px' }}>
                            <Space>
                                <Text strong>Скрыть полностью распределенные задачи:</Text>
                                <Switch checked={hideFullyReported} onChange={setHideFullyReported} />
                            </Space>
                        </div>
                        <Table
                            rowSelection={{
                                selectedRowKeys: selectedTaskKeys,
                                // ДОБАВЛЕНО: Обработчик для одиночных кликов
                                onChange: (selectedRowKeys) => {
                                    setSelectedTaskKeys(selectedRowKeys);
                                },
                                onSelectAll: (selected) => {
                                    const allIds = selected ? filteredTasksSource.map(i => i.id) : [];
                                    setSelectedTaskKeys(allIds);
                                },
                                selections: [
                                    Table.SELECTION_ALL,
                                    Table.SELECTION_NONE,
                                ],
                                preserveSelectedRowKeys: true
                            }}
                            dataSource={filteredTasksSource} // <--- Важно: отфильтрованный список
                            rowKey="id"
                            size="small"
                            scroll={{ x: 1500 }}
                            columns={[
                                { title: 'ID', dataIndex: 'id', width: 70 },
                                { title: 'Внешний ID', dataIndex: 'external_id', width: 110 },
                                {
                                    title: 'Название',
                                    dataIndex: 'title',
                                    render: (text, r) => {
                                        const total = parseInt(r.total_income_count || 0);
                                        const assigned = parseInt(r.assigned_income_count || 0);
                                        const hasUnassigned = total > assigned && assigned > 0;

                                        return (
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Text strong={hasUnassigned}>{text}</Text>
                                                {hasUnassigned && (
                                                    <Tag color="orange" style={{ fontSize: '10px', marginTop: 4, width: 'fit-content' }}>
                                                        Есть не вошедшие строки ({total - assigned} из {total})
                                                    </Tag>
                                                )}
                                                {total === 0 && <Text type="secondary" style={{fontSize:'10px'}}>Нет позиций ВДР (доход)</Text>}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    title: 'Созданы документы',
                                    width: 220,
                                    render: (_, r) => {
                                        // Вспомогательная функция для парсинга данных из БД (формат "id:number|id:number")
                                        const renderLinks = (rawData, label) => {
                                            if (!rawData) return <div>{label}: <Text type="secondary">-</Text></div>;

                                            const docs = rawData.split('|');
                                            return (
                                                <div style={{ marginBottom: 2 }}>
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>{label}: </Text>
                                                    {docs.map((str, idx) => {
                                                        const [id, num] = str.split(':');
                                                        return (
                                                            <span key={id}>
                                <a
                                    href={`/docs?id=${id}`} // Предполагаем, что ваш роутер поймет такой URL
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '11px', textDecoration: 'underline' }}
                                >
                                    №{num}
                                </a>
                                                                {idx < docs.length - 1 ? ', ' : ''}
                            </span>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        };

                                        return (
                                            <div style={{ lineHeight: '1.4', padding: '4px 0' }}>
                                                {renderLinks(r.linked_customer_orders, 'Заказ пок.')}
                                                {renderLinks(r.linked_supplier_orders, 'Заказ пост.')}
                                                {renderLinks(r.linked_daily_reports, 'Еж. отчет')}
                                            </div>
                                        );
                                    }
                                },
                                { title: 'Исполнитель', dataIndex: 'assigned_user_fio' },
                                {
                                    title: 'Статус ВДР',
                                    dataIndex: 'status_invoices',
                                    render: s => {
                                        const statusObj = TASK_STATUS_OPTIONS.find(opt => opt.value === s);
                                        return <Tag color="blue">{statusObj ? statusObj.label : s}</Tag>;
                                    }
                                }
                            ]}
                        />
                        <Button type="primary" block size="large" onClick={goToInvoicesStep} disabled={selectedTaskKeys.length === 0} style={{ marginTop: 20 }}>Выбрать позиции ({selectedTaskKeys.length} задач) <ArrowRightOutlined /></Button>
                    </div>

                    {currentStep === 1 && (
                        <>
                            <div style={{ marginBottom: 20, padding: 15, border: '1px solid #d9d9d9', borderRadius: 8, background: '#fff' }}>
                                <Row gutter={16}>
                                    <Col span={4}>
                                        <Form.Item name="type" label="Тип документа" rules={[{required:true}]}>
                                            <Select
                                                options={REGISTRY_TYPES}
                                                onChange={handleDocTypeChange}
                                                disabled={isCreateOnBasis || isAddingItemsToExistingRegistry}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={5}><Form.Item name="title" label="Наименование отчета"><Input /></Form.Item></Col>
                                    <Col span={4}><Form.Item name="number" label="Номер документа"><Input placeholder="Пусто = Вн. №" /></Form.Item></Col>
                                    <Col span={5}><Form.Item name="date" label="Дата" initialValue={dayjs()} rules={[{required:true}]}><DatePicker style={{width:'100%'}} format="DD.MM.YYYY" /></Form.Item></Col>
                                    <Col span={6}>
                                        <Form.Item name="counterparty_id" label="Контрагент" rules={[{required: true}]}>
                                            <Select
                                                showSearch
                                                // Используем наш отфильтрованный список (только те, кто есть в выбранных задачах)
                                                options={filteredCpOptions}
                                                disabled={isAddingItemsToExistingRegistry}
                                                optionFilterProp="label"
                                                // УБРАЛИ disabled, чтобы можно было переключаться между доступными КА
                                                placeholder="Выберите контрагента"
                                                onChange={() => {
                                                    // При смене КА сбрасываем выбранные строки, так как они могут принадлежать другому КА
                                                    setSelectedInvoiceKeys([]);
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    {!isAddingItemsToExistingRegistry && (
                                        <Col span={6}>
                                            <Form.Item
                                                name="period"
                                                label="Период документа"
                                                tooltip="По дате закрытия заявок. Автору придёт уведомление, если у контрагента за период останутся не включённые строки ВДР или в документ попадут заявки с другой датой закрытия."
                                            >
                                                <RangePicker
                                                    style={{ width: '100%' }}
                                                    format="DD.MM.YYYY"
                                                    placeholder={['С', 'По']}
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}
                                    {/* ПРИВЯЗКА К ЗАКАЗУ (показывается только для Актов) */}
                                    {(docType || activeTab)?.includes('act') && (
                                        <Col span={4}>
                                            <Form.Item name="parent_id" label="Привязать к Заказу">
                                                <Select
                                                    placeholder="Выберите заказ"
                                                    allowClear
                                                    showSearch
                                                    optionFilterProp="label"
                                                    // Пункт 3: Блокируем выбор заказа (он уже выбран)
                                                    disabled={isCreateOnBasis || isAddingItemsToExistingRegistry}
                                                    options={availableOrders
                                                        .filter(o => !selectedCpName || o.counterparty_name === selectedCpName)
                                                        .map(o => ({ label: `Заказ №${o.number}`, value: o.id }))
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                            <Card
    size="small"
    style={{
        marginBottom: 16,
        background: '#fafafa'
    }}
>
    <Row
        gutter={[12, 12]}
        align="middle"
    >
        <Col
            xs={24}
            md={12}
            lg={10}
        >
            <Input
                allowClear
                prefix={
                    <SearchOutlined />
                }
                value={
                    wizardInvoiceSearch
                }
                placeholder={
                    'Поиск по № ТТ или ID через запятую: 91897, T2WO06705967'
                }
                onChange={event => {
                    setWizardInvoiceSearch(
                        event.target.value
                    );
                    setWizardInvoicePage(1);
                }}
            />
        </Col>

        <Col
            xs={24}
            md={8}
            lg={5}
        >
            <Select
                value={
                    wizardInvoiceAvailability
                }
                style={{
                    width: '100%'
                }}
                onChange={value => {
                    setWizardInvoiceAvailability(
                        value
                    );
                    setWizardInvoicePage(1);
                }}
                options={[
                    {
                        value: 'all',
                        label:
                            'Все строки'
                    },
                    {
                        value:
                            'available',
                        label:
                            'Только свободные'
                    },
                    {
                        value: 'used',
                        label:
                            'Уже распределённые'
                    },
                    {
                        value:
                            'selected',
                        label:
                            'Только выбранные'
                    }
                ]}
            />
        </Col>

        <Col>
            <Button
                type="primary"
                onClick={
                    handleSelectAllInvoices
                }
            >
                Выбрать все
            </Button>
        </Col>

        <Col>
            <Button
                onClick={
                    handleSelectVisibleInvoices
                }
            >
                Выбрать на странице
            </Button>
        </Col>

        <Col>
            <Button
                onClick={
                    handleClearVisibleInvoices
                }
            >
                Снять на странице
            </Button>
        </Col>

        <Col>
            <Button
                onClick={
                    handleClearAllInvoices
                }
            >
                Снять все
            </Button>
        </Col>

        <Col>
            <Button
                icon={
                    <ClearOutlined />
                }
                onClick={() => {
                    setWizardInvoiceSearch(
                        ''
                    );

                    setWizardInvoiceAvailability(
                        'all'
                    );

                    setWizardInvoicePage(1);
                }}
            >
                Сбросить фильтры
            </Button>
        </Col>
    </Row>

    <Divider
        style={{
            margin: '12px 0'
        }}
    />

    <Space
        wrap
        size={12}
    >
        <Text type="secondary">
            Задач:{' '}
            <b>
                {
                    wizardFilteredStats
                        .groups
                }
            </b>
        </Text>

        <Text type="secondary">
            Найдено строк:{' '}
            <b>
                {
                    wizardFilteredStats
                        .rows
                }
            </b>
        </Text>

        <Text type="secondary">
            Свободных:{' '}
            <b>
                {
                    wizardFilteredStats
                        .availableRows
                }
            </b>
        </Text>

        <Text type="secondary">
            Выбрано среди найденных:{' '}
            <b>
                {
                    wizardFilteredStats
                        .selectedRows
                }
            </b>
        </Text>

        <Text type="secondary">
            Сумма найденных:{' '}
            <b>
                {Number(
                    wizardFilteredStats
                        .amount
                ).toLocaleString(
                    'ru-RU',
                    {
                        minimumFractionDigits:
                            2,
                        maximumFractionDigits:
                            2
                    }
                )}{' '}
                ₽
            </b>
        </Text>

        <Text type="secondary">
            Выбрано на сумму:{' '}
            <b>
                {Number(
                    wizardFilteredStats
                        .selectedAmount
                ).toLocaleString(
                    'ru-RU',
                    {
                        minimumFractionDigits:
                            2,
                        maximumFractionDigits:
                            2
                    }
                )}{' '}
                ₽
            </b>
        </Text>
    </Space>
</Card>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                <Alert message="Выберите строки из ВДР. «Выбрать все» отмечает свободные строки на всех страницах." type="info" showIcon style={{ flex: 1, marginRight: 15 }} />
                                <Button type="primary" onClick={handleSelectAllInvoices}>
                                    Выбрать все доступные ({wizardFilteredStats.availableRows})
                                </Button>
                            </div>
                            {paginatedGroupedInvoicesSource.map((group, idx) => (
                                <div key={group.task?.external_id || group.task?.id || idx} style={{ marginBottom: 20, border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                                    <div style={{ background: '#fafafa', padding: '8px 15px', borderBottom: '1px solid #f0f0f0' }}>
                                        <Space wrap>
                                            <Tag color="blue">#{group.task?.external_id || group.task?.id}</Tag>
                                            {getTaskVal(group.task, 'Nomer_TT') !== '-' && (
                                                <Tag color="geekblue">
                                                    ТТ: {getTaskVal(group.task, 'Nomer_TT')}
                                                </Tag>
                                            )}
                                            <Text strong>{group.task?.title}</Text>
                                        </Space>
                                    </div>
                                    <Table
                                        rowSelection={{
    selectedRowKeys:
        selectedInvoiceKeys.filter(
            id =>
                group.items.some(
                    item =>
                        Number(
                            item.id
                        ) ===
                        Number(id)
                )
        ),

    onChange:
        groupSelectedKeys => {
            const currentGroupIds =
                new Set(
                    group.items.map(
                        item =>
                            Number(
                                item.id
                            )
                    )
                );

            setSelectedInvoiceKeys(
                previous => {
                    const outsideGroup =
                        previous.filter(
                            id =>
                                !currentGroupIds.has(
                                    Number(id)
                                )
                        );

                    return [
                        ...outsideGroup,
                        ...groupSelectedKeys
                    ];
                }
            );
        },

    getCheckboxProps:
        invoice => ({
            disabled:
                Boolean(
                    invoice.is_used
                ) ||
                (
                    selectedParentId
                        ? Boolean(
                            invoice.order_id &&
                            Number(
                                invoice.order_id
                            ) !==
                                Number(
                                    selectedParentId
                                )
                        )
                        : Boolean(
                            invoice.order_id
                        )
                )
        })
}}
                                        dataSource={group.items}
                                        rowKey="id"
                                        size="small"
                                        pagination={false}
                                        rowClassName={(r) => r.is_used ? 'row-disabled' : ''}
                                        columns={[
                                            { title: 'Работа', dataIndex: 'rate_name' },
                                            { title: 'Кол-во', dataIndex: 'quantity', align: 'center', width: 80 },
                                            { title: 'Цена', dataIndex: 'price', align: 'right', width: 100, render: v => `${parseFloat(v || 0).toLocaleString()} р.` },
                                            { title: '%', dataIndex: 'percent', align: 'center', width: 70, render: v => `${formatPercent(v)}%` },
                                            { title: 'Штраф', dataIndex: 'penalty', align: 'center', width: 80, render: v => v > 0 ? <Tag color="error">-{v}%</Tag> : '-' },
                                            { title: 'Сумма', dataIndex: 'amount', align: 'right', width: 140, render: (v, r) => <Tooltip title={r.is_used ? "Уже в другом реестре" : ""}><b>{parseFloat(v).toLocaleString()} р.</b></Tooltip> },
                                            {
                                                title: 'Статус',
                                                width: 200,
                                                render: (_, r) => {
                                                    const isIncome = r.type === 'income';
                                                    // Определяем, в каких документах уже есть эта строка
                                                    const actNum = isIncome ? r.linked_customer_act : r.linked_supplier_act;
                                                    const orderNum = isIncome ? r.linked_customer_order : r.linked_supplier_order;

                                                    return (
                                                        <Space direction="vertical" size={0}>
                                                            {orderNum && <Tag color="blue" style={{fontSize:'10px'}}>В заказе №{orderNum}</Tag>}
                                                            {actNum && <Tag color="cyan" style={{fontSize:'10px'}}>В акте №{actNum}</Tag>}
                                                            {!orderNum && !actNum && <Tag color="success">Свободно</Tag>}
                                                            {r.is_used && <div style={{color:'red', fontSize:'10px', marginTop: 4}}>Блокировка: дубликат типа</div>}
                                                        </Space>
                                                    );
                                                }
                                            },
                                        ]}
                                    />
                                </div>
                            ))}

                            {filteredGroupedInvoicesSource.length === 0 && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="По заданным условиям строки не найдены"
                                    style={{ marginBottom: 16 }}
                                />
                            )}

                            {filteredGroupedInvoicesSource.length >
                                wizardInvoicePageSize && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginTop: 12,
                                        marginBottom: 8
                                    }}
                                >
                                    <Pagination
                                        current={wizardInvoicePage}
                                        pageSize={wizardInvoicePageSize}
                                        total={
                                            filteredGroupedInvoicesSource.length
                                        }
                                        showSizeChanger
                                        pageSizeOptions={[
                                            5,
                                            10,
                                            20,
                                            50
                                        ]}
                                        showTotal={total =>
                                            `Всего задач: ${total}`
                                        }
                                        onChange={(page, pageSize) => {
                                            setWizardInvoicePage(page);
                                            setWizardInvoicePageSize(
                                                pageSize
                                            );
                                        }}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                                <Button
                                    size="large"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => setCurrentStep(0)}
                                    style={{ width: 150 }}
                                    disabled={isCreateOnBasis}
                                >
                                    Назад
                                </Button>

                                <Button
                                    type="primary"
                                    block
                                    size="large"
                                    icon={<SaveOutlined />}
                                    onClick={isEditMode ? addItemsToRegistry : handleCreate}
                                    loading={isSaving}
                                    disabled={selectedInvoiceKeys.length === 0}
                                >
                                    {isEditMode ? 'Сохранить изменения' : 'Сформировать документ'}
                                </Button>
                            </div>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    <style>{
        `.row-disabled { background-color: #fafafa; opacity: 0.6; }
                .green-header-table .ant-table-thead > tr > th {
                    background-color: #70AD47 !important;
                    color: white !important;
                    font-size: 11px !important;
                    text-align: center !important;
                }
                .daily-report-view .ant-divider-inner-text {
                    font-weight: bold;
                    color: #555;
                }
                .detail-table {
                    margin-top: 10px;
                }
                .row-disabled { background-color: #fafafa; opacity: 0.6; }
                .registry-unallocated-row > td {
                    background: #fff7e6 !important;
                }

                .registry-unallocated-row:hover > td {
                    background: #ffe7ba !important;
                }

                .registry-unallocated-row > td:first-child {
                    border-left: 4px solid #fa8c16;
                }

            `}</style>
</Layout>
);
};

export default DocumentCreatePage;